import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import { useBackgroundTasksStore } from '@/stores/backgroundTasks.ts'
import { useAuthStore } from '@/stores/auth.ts'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  PriorityBadge,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@/components/ui'
import {
  UserPlus, Mail, Phone, Calendar, TrendingUp,
  CheckCircle2, XCircle, ChevronRight, ChevronLeft, Plus,
  Target, BarChart3, Building2, User, MapPin,
  Clock, Send, Eye, ThumbsDown, Info, Search,
  RefreshCw, Settings as SettingsIcon, Trash2, X, AlertTriangle,
  Loader2, RotateCw, ExternalLink,
  SkipForward, Linkedin, Save, Check, Briefcase,
  FileText, History, Sparkles,
  Globe, Camera, Copy, Pencil,
  ArrowRightCircle,
  MessageSquare,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { scoreToneClasses } from '@/styles/colorUtils.ts'
import { PRIORITY_BORDER_CLASSES, type Priority } from '@/styles/tokens.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { QuickCaptureModal } from './QuickCaptureModal.tsx'
import { LetterComposer } from './letterComposer/LetterComposer.tsx'

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProspectLatestLetter {
  id: string
  type: string
  status: string
  personalizedBody: string
  kundenschreiberOrderId: string | null
  pensakiOrderId: string | null
  letterProvider: string | null
  campaignId: string | null
  versandDatum: string | null
  sentAt: string | null
  createdAt: string
}

interface ProspectLatestCampaign {
  id: string
  name: string
  campaignType: string
}

interface ProspectContactCounts {
  letters: number
  calls: number
  emails: number
  meetings: number
  total: number
}

interface ProspectContactHistoryItem {
  type: string
  date: string
  title: string
  outcome: string | null
}

interface Prospect {
  id: string
  companyName: string
  industry: string | null
  employeeCount: number | null
  website: string | null
  region: string | null
  zipCode: string | null
  city: string | null
  street: string | null
  country: string | null
  contactSalutation: string | null
  contactFirstName: string | null
  contactLastName: string | null
  contactRole: string | null
  contactEmail: string | null
  contactPhone: string | null
  contactLinkedinUrl: string | null
  icpScore: number
  status: string
  source: string | null
  sourceChannel: string | null
  referenceCustomerId: string | null
  triggerEvent: string | null
  notes: string | null
  memberships: string[] | null
  companyProfile: Record<string, unknown> | null
  lastContactedAt: string | null
  nextFollowUpAt: string | null
  statusChangedAt?: string | null
  recheckAfter?: string | null
  convertedDealId?: string | null
  assignedUserId?: string | null
  createdAt: string
  // Contact-context enrichment (server-side, see backend buildProspectContactContext)
  latestLetter?: ProspectLatestLetter | null
  latestCampaign?: ProspectLatestCampaign | null
  contactCounts?: ProspectContactCounts
  contactHistory?: ProspectContactHistoryItem[]
}

interface ProspectLetter {
  id: string
  type: string
  personalizedBody: string
  status: string
  sentAt: string | null
  versandDatum: string | null
  channel: string
  jobPostingId: string | null
  kundenschreiberOrderId: string | null
  letterProvider: string | null
  referenceCustomer: { id: string; companyName: string; industry: string | null; city: string | null } | null
  sentByUserId?: string | null
  sentByUserName?: string | null
  createdAt: string
}

interface ProspectHistoryEntry {
  id: string
  userId: string | null
  userName: string | null
  action: string
  details: Record<string, any>
  createdAt: string
}

interface AcquisitionTask {
  id: string
  prospectId: string
  letterId?: string | null
  type: string
  title: string
  description: string | null
  status: string
  priority: string
  dueDate: string
  completedAt: string | null
  outcome: string | null
  m365EventId: string | null
  assignedUserId: string | null
  assignedUserName: string | null
  completedByUserId: string | null
  completedByUserName: string | null
  // Angereichert vom Backend (GET /acquisition/tasks): Kontakt- und Brief-Infos
  // werden direkt an die Task gehaengt, damit der Vertriebler sie ohne
  // Zusatz-Roundtrip sieht.
  prospect?: {
    id: string
    companyName: string
    city: string | null
    zipCode: string | null
    website: string | null
    icpScore: number
    contactSalutation: string | null
    contactFirstName: string | null
    contactLastName: string | null
    contactRole: string | null
    contactPhone: string | null
    contactEmail: string | null
    contactLinkedinUrl: string | null
    source: string | null
    sourceChannel: string | null
    triggerEvent: string | null
    notes: string | null
  } | null
  letter?: {
    id: string
    type: string
    status: string
    sentAt: string | null
    versandDatum: string | null
    sentByUserName?: string | null
  } | null
}

interface ProspectJobPosting {
  id: string
  jobTitle: string
  jobUrl: string
  jobSource: string | null
  status: string
  companyName: string
  createdAt: string
  jobPostingDate: string | null
}

type ProspectNoteKind = 'call' | 'meeting' | 'email' | 'linkedin_message' | 'linkedin_connect' | 'other'

interface ProspectNote {
  id: string
  taskId: string | null
  userId: string | null
  userName: string | null
  kind: ProspectNoteKind
  content: string
  occurredAt: string
  createdAt: string
  updatedAt: string
}

// Unified activity-feed item (HubSpot-Engagements-Pattern). Mischt Briefe,
// Stellenanzeigen, Aufgaben, Gespraechsnotizen und Aenderungshistorie zu einer
// chronologischen Timeline im Aktivitaeten-Tab der Zielkunden-Detailansicht.
type ProspectActivity =
  | { kind: 'letter'; id: string; date: string; data: ProspectLetter }
  | { kind: 'job_posting'; id: string; date: string; data: ProspectJobPosting }
  | { kind: 'task'; id: string; date: string; data: AcquisitionTask }
  | { kind: 'history'; id: string; date: string; data: ProspectHistoryEntry }
  | { kind: 'note'; id: string; date: string; data: ProspectNote }

type ProspectActivityKind = ProspectActivity['kind']

const ACTIVITY_FILTER_LABELS: Record<ProspectActivityKind, string> = {
  note: 'Notizen',
  task: 'Aufgaben',
  letter: 'Briefe',
  job_posting: 'Stellenanzeigen',
  history: 'Änderungen',
}

const NOTE_KIND_LABELS: Record<ProspectNoteKind, { label: string; icon: typeof Phone }> = {
  call: { label: 'Anruf', icon: Phone },
  meeting: { label: 'Termin', icon: Calendar },
  email: { label: 'E-Mail', icon: Send },
  linkedin_message: { label: 'LinkedIn-Nachricht', icon: Linkedin },
  linkedin_connect: { label: 'LinkedIn-Kontaktaufnahme', icon: UserPlus },
  other: { label: 'Notiz', icon: MessageSquare },
}

interface KPIs {
  totalProspects: number
  lettersSent: number
  responses: number
  responseRate: number
  meetings: number
  dealsWon: number
  costPerLeadEur: number
  totalCostEur: number
}

// ─── Helpers ────────────────────────────────────────────────────────────────

type Tab = 'daily' | 'tasks' | 'prospects' | 'campaigns' | 'settings'
type TaskFilter = 'open' | 'due' | 'completed' | 'all'


const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  new: { label: 'Neu', color: 'bg-gray-100 text-gray-700' },
  qualified: { label: 'Qualifiziert', color: 'bg-blue-100 text-blue-700' },
  draft_letter: { label: 'Brief-Entwurf offen', color: 'bg-amber-50 text-amber-700' },
  letter_sent: { label: 'Brief versandt', color: 'bg-amber-100 text-amber-700' },
  letter_failed: { label: 'Versandfehler', color: 'bg-red-100 text-red-700' },
  follow_up: { label: 'Follow-up', color: 'bg-purple-100 text-purple-700' },
  interested: { label: 'Interesse', color: 'bg-green-100 text-green-700' },
  meeting_scheduled: { label: 'Termin', color: 'bg-emerald-100 text-emerald-700' },
  converted: { label: 'Gewonnen', color: 'bg-green-200 text-green-800' },
  not_interested: { label: 'Kein Interesse', color: 'bg-red-100 text-red-700' },
  nurture: { label: 'Nurture', color: 'bg-gray-100 text-gray-600' },
}

// Letter- and campaign-label maps — shared between the Zielkunden tab (showing
// per-prospect latest-letter info) and the letter-detail modal. Kept near
// STATUS_LABELS so all badge label tables live in one place.
const LETTER_TYPE_LABELS: Record<string, string> = {
  first_contact: 'Erstkontakt',
  follow_up: 'Follow-up',
  seasonal: 'Saisonal',
}

// Per-letter Status-Badges (nur die Zustaende die in der UI direkt am Brief
// sichtbar werden). 'sending' = "In Druck": an Kundenschreiber uebergeben,
// aber noch nicht physisch versandt.
const LETTER_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Entwurf',   color: 'bg-gray-100 text-gray-600' },
  approved:  { label: 'Freigegeben', color: 'bg-amber-50 text-amber-700' },
  sending:   { label: 'In Druck',  color: 'bg-orange-100 text-orange-700' },
  sent:      { label: 'Versandt',  color: 'bg-green-100 text-green-700' },
  delivered: { label: 'Zugestellt', color: 'bg-green-100 text-green-700' },
  failed:    { label: 'Fehler',    color: 'bg-red-100 text-red-700' },
}

const CAMPAIGN_TYPE_LABELS_OVERVIEW: Record<string, { label: string; color: string }> = {
  standard: { label: 'Standard', color: 'bg-blue-50 text-blue-700' },
  referenz: { label: 'Referenz', color: 'bg-emerald-50 text-emerald-700' },
  stellenanzeige: { label: 'Stellenanzeige', color: 'bg-orange-50 text-orange-700' },
}

const CONTACT_TYPE_LABELS: Record<string, { label: string; icon: typeof Mail }> = {
  letter: { label: 'Brief', icon: Mail },
  call: { label: 'Anruf', icon: Phone },
  email: { label: 'E-Mail', icon: Send },
  meeting: { label: 'Termin', icon: Calendar },
  follow_up_letter: { label: 'Follow-up Brief', icon: Mail },
  other: { label: 'Sonstiges', icon: FileText },
}

// Combine prospect-status + latest-letter-status into a single visual badge for
// the merged Zielkunden table. Main badge = prospect status. Secondary pill is
// shown only for letter states the user should see immediately (open draft,
// "in Druck" bei Kundenschreiber, failed send). sent/delivered werden durch
// prospect.status === 'letter_sent' abgedeckt.
function resolveUnifiedStatus(p: {
  status: string
  latestLetter?: { status: string } | null
}): { main: { label: string; color: string }; secondary?: { label: string; color: string } | null } {
  const main = STATUS_LABELS[p.status] ?? { label: p.status, color: 'bg-gray-100 text-gray-600' }
  const letterStatus = p.latestLetter?.status
  let secondary: { label: string; color: string } | null = null
  if (letterStatus === 'draft' || letterStatus === 'approved') {
    secondary = { label: 'Entwurf offen', color: 'bg-amber-50 text-amber-700' }
  } else if (letterStatus === 'sending') {
    secondary = { label: 'In Druck', color: 'bg-orange-50 text-orange-700' }
  } else if (letterStatus === 'failed') {
    secondary = { label: 'Versandfehler', color: 'bg-red-50 text-red-700' }
  }
  return { main, secondary }
}

const TASK_TYPE_ICONS: Record<string, typeof Phone> = {
  call: Phone,
  follow_up_letter: Mail,
  meeting: Calendar,
  email: Send,
}

function SourceBadge({ source, sourceChannel, website }: { source: string | null; sourceChannel: string | null; website?: string | null }) {
  const [copied, setCopied] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  if (!source && sourceChannel !== 'job_posting') return <span className="text-gray-400">–</span>

  let label = source
  let icon: JSX.Element | null = null
  let variant: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'brand' | 'accent' = 'neutral'
  let url: string | null = null

  if (source === 'quick-capture-google') {
    label = 'Google Maps'
    icon = <MapPin className="w-3 h-3" />
    variant = 'danger'
  } else if (sourceChannel === 'job_posting' || (source && /^https?:\/\//i.test(source))) {
    label = 'Stellenanzeige'
    icon = <Briefcase className="w-3 h-3" />
    variant = 'warning'
    url = source && /^https?:\/\//i.test(source) ? source : null
  } else if (source === 'quick-capture-website') {
    label = 'Website'
    icon = <Globe className="w-3 h-3" />
    variant = 'info'
    url = website || null
  } else if (source === 'quick-capture-image') {
    label = 'Bild/OCR'
    icon = <Camera className="w-3 h-3" />
    variant = 'accent'
  } else if (source === 'apollo') {
    label = 'Apollo'
    icon = <Building2 className="w-3 h-3" />
    variant = 'brand'
  } else if (source === 'discovery') {
    label = 'Discovery'
    icon = <Search className="w-3 h-3" />
    variant = 'success'
  }

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!url) return
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative inline-block" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
      <Badge variant={variant} className="cursor-default">
        {icon}
        {label}
      </Badge>
      {showTooltip && url && (
        <div className="absolute z-overlay bottom-full left-0 mb-1.5 bg-gray-900 text-white text-caption rounded-lg px-3 py-2 shadow-popover whitespace-nowrap">
          <div className="flex items-center gap-2">
            <span className="max-w-[240px] truncate">{url}</span>
            <button
              onClick={handleCopy}
              className="shrink-0 text-gray-300 hover:text-white transition-colors"
              title="Link kopieren"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="absolute top-full left-4 -mt-px border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  )
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function parseMemberships(val: unknown): string[] {
  if (Array.isArray(val)) return val
  if (typeof val === 'string') { try { const p = JSON.parse(val); if (Array.isArray(p)) return p } catch { /* ignore */ } }
  return []
}

function getScoreColor(score: number) {
  return scoreToneClasses(score)
}

function extractDomain(website: string | null): string | null {
  if (!website) return null
  try {
    const url = website.startsWith('http') ? website : `https://${website}`
    return new URL(url).hostname.replace(/^www\./, '')
  } catch { return null }
}

function CompanyLogo({ website, companyName, size = 'md' }: { website: string | null; companyName: string; size?: 'sm' | 'md' }) {
  const a = useAnonymize()
  const domain = extractDomain(website)
  const dim = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10'
  const iconDim = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  // Im Demo-Modus niemals das echte Logo laden (wuerde Firma verraten).
  const [imgState, setImgState] = useState<'clearbit' | 'google' | 'fallback'>(
    a.enabled || !domain ? 'fallback' : 'clearbit',
  )

  // Reset state when domain changes (navigating between prospects)
  useEffect(() => {
    setImgState(a.enabled || !domain ? 'fallback' : 'clearbit')
  }, [domain, a.enabled])

  if (imgState === 'fallback' || !domain || a.enabled) {
    return (
      <div className={cn(dim, 'rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0')}>
        <Building2 className={cn(iconDim, 'text-brand-600')} />
      </div>
    )
  }

  const src = imgState === 'clearbit'
    ? `https://logo.clearbit.com/${domain}`
    : `https://www.google.com/s2/favicons?domain=${domain}&sz=64`

  return (
    <img
      src={src}
      alt={a.name(companyName)}
      className={cn(dim, 'rounded-lg object-contain flex-shrink-0 bg-white border border-gray-100')}
      onError={() => {
        if (imgState === 'clearbit') setImgState('google')
        else setImgState('fallback')
      }}
    />
  )
}

function PersonAvatar({ firstName, lastName, size = 'sm' }: { firstName: string | null; lastName: string | null; size?: 'sm' | 'md' }) {
  const a = useAnonymize()
  // Im Demo-Modus deterministisch andere Initialen aus dem anonymisierten Namen.
  const displayName = a.enabled
    ? a.name(`${firstName ?? ''} ${lastName ?? ''}`.trim() || '?').split(' ')
    : [firstName ?? '', lastName ?? '']
  const initials = [displayName[0]?.[0], displayName[displayName.length - 1]?.[0]]
    .filter(Boolean).join('').toUpperCase() || '?'
  const dim = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'
  return (
    <div className={cn(dim, 'rounded-full bg-gray-200 text-gray-600 flex items-center justify-center flex-shrink-0 font-medium')}>
      {initials}
    </div>
  )
}

// ─── Inline Editable Field (double-click to edit) ───────────────────────────

function EditableField({
  value,
  onSave,
  placeholder = '—',
  className,
  inputClassName,
  type = 'text',
  required = false,
  allowEmpty = true,
  title = 'Doppelklick zum Bearbeiten',
  widthCh,
}: {
  value: string | number | null | undefined
  onSave: (newValue: string) => void | Promise<void>
  placeholder?: string
  className?: string
  inputClassName?: string
  type?: 'text' | 'number' | 'email' | 'tel'
  required?: boolean
  allowEmpty?: boolean
  title?: string
  widthCh?: number
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const displayValue = value == null ? '' : String(value)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  function startEdit() {
    setErrorMsg(null)
    setDraft(displayValue)
    setEditing(true)
  }

  async function commit() {
    if (saving) return
    const next = draft.trim()
    if (next === displayValue.trim()) {
      setEditing(false)
      return
    }
    if (required && next === '') {
      setErrorMsg('Pflichtfeld')
      inputRef.current?.focus()
      return
    }
    if (!allowEmpty && next === '') {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
      setErrorMsg(null)
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
      const msg = axErr?.response?.data?.message || axErr?.response?.data?.error || axErr?.message || 'Speichern fehlgeschlagen'
      setErrorMsg(msg)
    } finally {
      setSaving(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setDraft(displayValue)
      setErrorMsg(null)
      setEditing(false)
    }
  }

  if (editing) {
    const ch = widthCh ?? Math.max(displayValue.length, 8)
    return (
      <span className="inline-flex flex-col">
        <input
          ref={inputRef}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          disabled={saving}
          onClick={(e) => e.stopPropagation()}
          style={{ width: `${ch + 2}ch` }}
          className={cn(
            'px-1 py-0 border border-brand-400 rounded bg-white outline-none focus:ring-1 focus:ring-brand-400 max-w-full',
            inputClassName ?? className,
          )}
        />
        {errorMsg && <span className="text-[10px] text-red-600 mt-0.5">{errorMsg}</span>}
      </span>
    )
  }

  return (
    <span
      onDoubleClick={startEdit}
      title={title}
      className={cn(
        'cursor-text rounded px-0.5 -mx-0.5 hover:bg-brand-50/60 hover:outline hover:outline-1 hover:outline-dashed hover:outline-brand-300',
        className,
        displayValue === '' && 'text-gray-300 italic font-normal',
      )}
    >
      {displayValue || placeholder}
    </span>
  )
}

// ─── Letter Delete Button (draft only) ───────────────────────────────────

function LetterDeleteButton({ letter, prospectId }: { letter: { id: string; status: string }; prospectId?: string }) {
  const queryClient = useQueryClient()
  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/acquisition/letters/${letter.id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-letters', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['acquisition', 'prospects'] })
    },
  })
  if (letter.status !== 'draft') return null
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        if (confirm('Entwurf löschen?')) deleteMutation.mutate()
      }}
      disabled={deleteMutation.isPending}
      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-40"
      title="Entwurf löschen"
    >
      {deleteMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
    </button>
  )
}

// ─── Letter Status Toggle (manual draft → sent override) ─────────────────

function LetterStatusToggle({ letter, prospectId }: { letter: { id: string; status: string }; prospectId?: string }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<Record<string, unknown> | null>(null)

  const invalidate = () => {
    queryClient.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey
        if (Array.isArray(k) && k[0] === 'prospect-letters') return true
        if (Array.isArray(k) && k[0] === 'acquisition' && k[1] === 'letters-overview') return true
        // The merged Zielkunden tab also shows the latest-letter status/order,
        // so its query needs to refetch after a status change.
        if (Array.isArray(k) && k[0] === 'acquisition' && k[1] === 'prospects') return true
        return false
      },
    })
    if (prospectId) queryClient.invalidateQueries({ queryKey: ['prospect-letters', prospectId] })
  }

  const statusMutation = useMutation({
    mutationFn: (status: 'sent' | 'draft') => {
      const payload = { status }
      return apiClient.patch(`/acquisition/letters/${letter.id}`, payload).then((r) => r.data).catch((e) => {
        e._payload = payload
        throw e
      })
    },
    onSuccess: () => { setError(null); setErrorDetails(null); invalidate() },
    onError: (e: any) => {
      setError(e?.response?.data?.error || e?.message || 'Statuswechsel fehlgeschlagen')
      setErrorDetails({
        message: e?.message ?? null,
        status: e?.response?.status ?? null,
        responseData: e?.response?.data ?? null,
        sentPayload: e?._payload ?? null,
      })
    },
  })

  const isDraft = letter.status === 'draft'
  const isInDruck = letter.status === 'sending'
  const isSent = letter.status === 'sent' || letter.status === 'delivered'
  const canResetToDraft = isInDruck || isSent

  return (
    <div className="px-3 py-2 border-t border-amber-100/50 bg-amber-50/40">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-semibold uppercase text-amber-700">Status</span>
        {isDraft && (
          <button
            onClick={() => {
              if (confirm('Brief als versendet markieren? (Nur nutzen, wenn der Brief bereits manuell versendet wurde.)')) {
                statusMutation.mutate('sent')
              }
            }}
            disabled={statusMutation.isPending}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            title="Brief als manuell versendet markieren"
          >
            {statusMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Als versendet markieren
          </button>
        )}
        {isInDruck && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700" title="An Kundenschreiber uebergeben, noch nicht versandt">
            In Druck
          </span>
        )}
        {canResetToDraft && (
          <button
            onClick={() => {
              const msg = isInDruck
                ? 'Status zurueck auf Entwurf setzen? (Brief ist aktuell "In Druck" bei Kundenschreiber.)'
                : 'Status zurueck auf Entwurf setzen?'
              if (confirm(msg)) {
                statusMutation.mutate('draft')
              }
            }}
            disabled={statusMutation.isPending}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            title="Status zurueck auf Entwurf setzen"
          >
            {statusMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
            Auf Entwurf zuruecksetzen
          </button>
        )}
      </div>
      {error && (
        <div className="mt-1">
          <p className="text-[11px] text-red-600">{error}</p>
          {errorDetails && (
            <details className="mt-0.5">
              <summary className="text-[10px] text-red-500 cursor-pointer hover:underline">Debug-Details</summary>
              <pre className="mt-1 p-1.5 bg-red-50 border border-red-100 rounded text-[10px] text-red-700 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                {JSON.stringify(errorDetails, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Letter Order Tracker (Kundenschreiber) ───────────────────────────────

// Convert a Date (or ISO string) to the `yyyy-MM-dd` value expected by <input type="date">.
function toDateInputValue(d: string | Date | null | undefined): string {
  if (!d) return ''
  const date = typeof d === 'string' ? new Date(d) : d
  if (Number.isNaN(date.getTime())) return ''
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

interface LetterOrderTrackerLetter {
  id: string
  kundenschreiberOrderId: string | null
  status?: string | null
  versandDatum?: string | null
  sentAt?: string | null
}

// Build a user-visible debug payload from an axios error. Per project rules,
// all debug info must be shown in the frontend UI (no server logs access).
function buildErrorDetails(e: any, sentPayload?: unknown): Record<string, unknown> {
  return {
    message: e?.message ?? null,
    status: e?.response?.status ?? null,
    responseData: e?.response?.data ?? null,
    sentPayload: sentPayload ?? null,
  }
}

function LetterOrderTracker({ letter, prospectId, compact = false }: { letter: LetterOrderTrackerLetter; prospectId?: string; compact?: boolean }) {
  const queryClient = useQueryClient()
  const [orderId, setOrderId] = useState(letter.kundenschreiberOrderId ?? '')
  const [editing, setEditing] = useState(!letter.kundenschreiberOrderId)
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<Record<string, unknown> | null>(null)
  const [remoteStatus, setRemoteStatus] = useState<string | null>(null)
  const [editingDate, setEditingDate] = useState(false)
  const existingVersandValue = letter.versandDatum || letter.sentAt || null
  const [versandDateInput, setVersandDateInput] = useState(toDateInputValue(existingVersandValue) || toDateInputValue(new Date()))

  useEffect(() => {
    setOrderId(letter.kundenschreiberOrderId ?? '')
    setEditing(!letter.kundenschreiberOrderId)
  }, [letter.kundenschreiberOrderId])

  useEffect(() => {
    // Keep the date input in sync when the letter changes (e.g. after refresh).
    const v = letter.versandDatum || letter.sentAt || null
    setVersandDateInput(toDateInputValue(v) || toDateInputValue(new Date()))
  }, [letter.versandDatum, letter.sentAt])

  const invalidate = () => {
    queryClient.invalidateQueries({
      predicate: (q) => {
        const k = q.queryKey
        if (Array.isArray(k) && k[0] === 'prospect-letters') return true
        if (Array.isArray(k) && k[0] === 'acquisition' && k[1] === 'letters-overview') return true
        // Daily queue (`['acquisition', 'daily', ...]`) muss neu geladen werden, damit
        // Prospects mit frisch gesetzter Kundenschreiber-Order-ID aus der Liste verschwinden.
        if (Array.isArray(k) && k[0] === 'acquisition' && k[1] === 'daily') return true
        // Merged Zielkunden tab: latest-letter Order-ID wird inline dort angezeigt
        // und muss nach einem Save neu geladen werden.
        if (Array.isArray(k) && k[0] === 'acquisition' && k[1] === 'prospects') return true
        return false
      },
    })
    if (prospectId) queryClient.invalidateQueries({ queryKey: ['prospect-letters', prospectId] })
  }

  const saveMutation = useMutation({
    mutationFn: (id: string) => {
      const payload = { kundenschreiberOrderId: id }
      return apiClient.patch(`/acquisition/letters/${letter.id}`, payload).then((r) => r.data).catch((e) => {
        e._payload = payload
        throw e
      })
    },
    onSuccess: () => { setError(null); setErrorDetails(null); setEditing(false); invalidate() },
    onError: (e: any) => {
      setError(e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen')
      setErrorDetails(buildErrorDetails(e, e?._payload))
    },
  })

  const refreshMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/acquisition/letters/${letter.id}/refresh-status`, {}).then((r) => r.data),
    onSuccess: (data: { remoteStatus: string }) => { setError(null); setErrorDetails(null); setRemoteStatus(data.remoteStatus); invalidate() },
    onError: (e: any) => {
      setError(e?.response?.data?.error || e?.message || 'Aktualisierung fehlgeschlagen')
      setErrorDetails(buildErrorDetails(e))
    },
  })

  const markSentMutation = useMutation({
    mutationFn: (payload: { status?: string; versandDatum: string | null }) =>
      apiClient.patch(`/acquisition/letters/${letter.id}`, payload).then((r) => r.data).catch((e) => {
        e._payload = payload
        throw e
      }),
    onSuccess: () => { setError(null); setErrorDetails(null); setEditingDate(false); invalidate() },
    onError: (e: any) => {
      setError(e?.response?.data?.error || e?.message || 'Markieren fehlgeschlagen')
      setErrorDetails(buildErrorDetails(e, e?._payload))
    },
  })

  const hasOrderId = !!letter.kundenschreiberOrderId
  const isSent = letter.status === 'sent' || letter.status === 'delivered'
  const versandDatumDisplay = letter.versandDatum
    ? new Date(letter.versandDatum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null

  const handleMarkSent = () => {
    if (!versandDateInput) return
    // Use local noon time to avoid timezone edge cases shifting the day.
    const iso = new Date(`${versandDateInput}T12:00:00`).toISOString()
    markSentMutation.mutate({ status: 'sent', versandDatum: iso })
  }

  const handleUpdateDate = () => {
    if (!versandDateInput) return
    const iso = new Date(`${versandDateInput}T12:00:00`).toISOString()
    markSentMutation.mutate({ versandDatum: iso })
  }

  return (
    <div className={cn(compact ? 'py-1' : 'px-3 py-2 border-t border-amber-100/50 bg-amber-50/40')}>
      <div className="flex items-center gap-2 flex-wrap">
        {!compact && <span className="text-[10px] font-semibold uppercase text-amber-700">Kundenschreiber-Auftrag</span>}
        {editing ? (
          <>
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Order-ID"
              className={cn('flex-1 px-2 py-1 text-xs border border-amber-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-amber-400', compact ? 'min-w-[100px]' : 'min-w-[180px]')}
            />
            <button
              onClick={() => saveMutation.mutate(orderId.trim())}
              disabled={!orderId.trim() || saveMutation.isPending}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              title="Speichern"
            >
              {saveMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {!compact && 'Speichern'}
            </button>
            {letter.kundenschreiberOrderId && (
              <button
                onClick={() => { setOrderId(letter.kundenschreiberOrderId ?? ''); setEditing(false); setError(null) }}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Abbrechen"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </>
        ) : (
          <>
            <code className="px-1.5 py-0.5 text-xs bg-white border border-amber-200 rounded text-gray-700 font-mono" title={letter.kundenschreiberOrderId ?? undefined}>
              {compact && letter.kundenschreiberOrderId && letter.kundenschreiberOrderId.length > 8
                ? letter.kundenschreiberOrderId.slice(0, 8) + '…'
                : letter.kundenschreiberOrderId}
            </code>
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className={cn('flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50', compact && 'px-1.5')}
              title="Status von Kundenschreiber abrufen"
            >
              {refreshMutation.isPending
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
              {!compact && 'Aktualisieren'}
            </button>
            <button
              onClick={() => setEditing(true)}
              className={compact ? 'p-1 text-amber-600 hover:text-amber-900' : 'px-2 py-1 text-xs text-amber-700 hover:text-amber-900 underline'}
              title="Order-ID ändern"
            >
              {compact ? <Pencil className="w-3 h-3" /> : 'Ändern'}
            </button>
          </>
        )}
      </div>

      {/* Manueller Versand: nur sichtbar wenn eine Order-ID hinterlegt ist */}
      {hasOrderId && !editing && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          {editingDate ? (
            <>
              <input
                type="date"
                value={versandDateInput}
                onChange={(e) => setVersandDateInput(e.target.value)}
                className="px-2 py-1 text-xs border border-amber-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <button
                onClick={isSent ? handleUpdateDate : handleMarkSent}
                disabled={!versandDateInput || markSentMutation.isPending}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {markSentMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {isSent ? 'Datum speichern' : 'Als versendet markieren'}
              </button>
              <button
                onClick={() => {
                  setEditingDate(false)
                  setVersandDateInput(toDateInputValue(letter.versandDatum || letter.sentAt) || toDateInputValue(new Date()))
                  setError(null)
                }}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
              >
                Abbrechen
              </button>
            </>
          ) : isSent && versandDatumDisplay ? (
            <>
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-600">
                <Calendar className="w-3 h-3 text-green-600" />
                {!compact && 'Versandt: '}<span className="font-medium text-gray-800">{versandDatumDisplay}</span>
              </span>
              <button
                onClick={() => setEditingDate(true)}
                className={compact ? 'p-1 text-amber-500 hover:text-amber-800' : 'px-1.5 py-0.5 text-[11px] text-amber-700 hover:text-amber-900 underline'}
                title="Versanddatum anpassen"
              >
                {compact ? <Pencil className="w-3 h-3" /> : 'Datum ändern'}
              </button>
            </>
          ) : isSent ? (
            <button
              onClick={() => setEditingDate(true)}
              className={cn('inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100', compact ? 'p-1' : 'px-2 py-1 text-xs font-medium')}
              title="Versanddatum hinterlegen"
            >
              <Calendar className="w-3 h-3" />
              {!compact && 'Datum hinterlegen'}
            </button>
          ) : (
            <button
              onClick={() => setEditingDate(true)}
              className={cn('inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 text-green-700 hover:bg-green-100', compact ? 'p-1' : 'px-2 py-1 text-xs font-medium')}
              title="Brief manuell als versendet markieren"
            >
              <Check className="w-3 h-3" />
              {!compact && 'Als versendet markieren'}
            </button>
          )}
        </div>
      )}

      {remoteStatus && (
        <p className="mt-1 text-[11px] text-gray-500">Zuletzt von Kundenschreiber: <span className="font-medium text-gray-700">{remoteStatus}</span></p>
      )}
      {error && (
        <div className="mt-1">
          <p className="text-[11px] text-red-600">{error}</p>
          {errorDetails && (
            <details className="mt-0.5">
              <summary className="text-[10px] text-red-500 cursor-pointer hover:underline">Debug-Details</summary>
              <pre className="mt-1 p-1.5 bg-red-50 border border-red-100 rounded text-[10px] text-red-700 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                {JSON.stringify(errorDetails, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Job Posting Date Editor ──────────────────────────────────────────────

function JobPostingDateEditor({
  date,
  isFallback,
  onSave,
  isSaving,
}: {
  date: string
  isFallback: boolean
  onSave: (date: string | null) => void
  isSaving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const toInputValue = (d: string) => {
    const dt = new Date(d)
    if (isNaN(dt.getTime())) return ''
    const y = dt.getFullYear()
    const m = String(dt.getMonth() + 1).padStart(2, '0')
    const day = String(dt.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const [value, setValue] = useState(toInputValue(date))

  useEffect(() => { setValue(toInputValue(date)) }, [date])

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title={isFallback ? 'Kein Veröffentlichungsdatum — klicken zum Setzen' : 'Datum bearbeiten'}
        className={cn(
          'flex items-center gap-1 hover:text-indigo-600 transition-colors',
          isFallback ? 'text-gray-300 italic' : 'text-gray-400',
        )}
      >
        <span>{new Date(date).toLocaleDateString('de-DE')}</span>
        <Pencil className="w-3 h-3 opacity-60" />
      </button>
    )
  }

  const commit = () => {
    const iso = value ? new Date(value + 'T00:00:00').toISOString() : null
    onSave(iso)
    setEditing(false)
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="date"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') { setValue(toInputValue(date)); setEditing(false) }
        }}
        disabled={isSaving}
        className="px-1.5 py-0.5 text-xs border border-indigo-200 rounded bg-white focus:outline-none focus:border-indigo-400"
      />
      <button
        type="button"
        onClick={commit}
        disabled={isSaving}
        title="Speichern"
        className="text-green-600 hover:text-green-700 disabled:opacity-50"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => { setValue(toInputValue(date)); setEditing(false) }}
        disabled={isSaving}
        title="Abbrechen"
        className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// ─── Prospect Activity Timeline ──────────────────────────────────────────
// HubSpot-Engagements-Pattern: Briefe, Stellenanzeigen, Aufgaben und
// Aenderungshistorie als chronologischer Aktivitaets-Feed mit Filter-Chips.

function taskTypeToNoteKind(t: string): ProspectNoteKind {
  if (t === 'call' || t === 'meeting' || t === 'email') return t
  return 'other'
}

function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

interface LetterTimelineAction {
  label: string
  title?: string
  disabled?: boolean
  onClick: () => void
}

interface LinkedInTimelineAction {
  title?: string
  disabled?: boolean
  onClick: () => void
}

interface EmailTimelineAction {
  title?: string
  disabled?: boolean
  onClick: () => void
}

function ProspectActivityTimeline({ prospectId, letterAction, linkedInAction, emailAction }: {
  prospectId: string
  letterAction?: LetterTimelineAction
  linkedInAction?: LinkedInTimelineAction
  emailAction?: EmailTimelineAction
}) {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<Set<ProspectActivityKind>>(new Set())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [jobPostingUrl, setJobPostingUrl] = useState('')
  const [showQuickAddJob, setShowQuickAddJob] = useState(false)

  // Note Quick-Add form state
  const [showQuickAddNote, setShowQuickAddNote] = useState(false)
  const [noteDraftContent, setNoteDraftContent] = useState('')
  const [noteDraftKind, setNoteDraftKind] = useState<ProspectNoteKind>('call')
  const [noteDraftOccurredAt, setNoteDraftOccurredAt] = useState<string>(() => toDatetimeLocal(new Date()))

  // Task Quick-Add form state — erlaubt das manuelle Anlegen einer Aufgabe
  // (z.B. „Donnerstag 14:00 anrufen"), wenn der Vertriebler einen konkreten
  // Wiedervorlage-Termin vom Kunden bekommt.
  const [showQuickAddTask, setShowQuickAddTask] = useState(false)
  const [taskDraftTitle, setTaskDraftTitle] = useState('')
  const [taskDraftDescription, setTaskDraftDescription] = useState('')
  const [taskDraftType, setTaskDraftType] = useState<'call' | 'meeting' | 'email' | 'follow_up_letter' | 'other'>('call')
  const [taskDraftPriority, setTaskDraftPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [taskDraftDueDate, setTaskDraftDueDate] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(9, 0, 0, 0)
    return toDatetimeLocal(d)
  })
  // Bestaetigung: zuletzt gespeicherte Notiz (verbleibt 8s sichtbar). Macht
  // erfolgreichen POST sichtbar, auch wenn die Liste durch einen Fehler im
  // GET-Endpoint leer aussieht — sonst kann der Nutzer nicht unterscheiden
  // zwischen "nicht gespeichert" und "gespeichert, aber nicht geladen".
  const [lastSavedNote, setLastSavedNote] = useState<{ id: string; preview: string; at: number } | null>(null)
  useEffect(() => {
    if (!lastSavedNote) return
    const timer = window.setTimeout(() => setLastSavedNote(null), 8000)
    return () => window.clearTimeout(timer)
  }, [lastSavedNote])

  // Inline-Edit (note id being edited + draft text)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editingNoteContent, setEditingNoteContent] = useState('')

  // Delete confirmation: id of note pending deletion
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null)

  // Task-Outcome: id of task being completed + outcome text
  const [taskOutcomeId, setTaskOutcomeId] = useState<string | null>(null)
  const [taskOutcomeText, setTaskOutcomeText] = useState('')

  // Inline-Edit fuer bestehende Aufgaben — Vertriebler kann Titel, Beschreibung,
  // Typ, Prioritaet und Faelligkeit nachtraeglich aendern.
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [editTaskTitle, setEditTaskTitle] = useState('')
  const [editTaskDescription, setEditTaskDescription] = useState('')
  const [editTaskType, setEditTaskType] = useState<'call' | 'meeting' | 'email' | 'follow_up_letter' | 'other'>('call')
  const [editTaskPriority, setEditTaskPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [editTaskDueDate, setEditTaskDueDate] = useState<string>('')

  // Bestaetigung der zuletzt erstellten Aufgabe inkl. Kalender-Sync-Status —
  // sonst sieht der Nutzer nicht, ob/warum kein M365-Termin angelegt wurde.
  const [lastSavedTask, setLastSavedTask] = useState<{
    id: string | null
    title: string
    calendarSync: {
      attempted: boolean
      synced: boolean
      reason?: string
      error?: string
    } | null
    at: number
  } | null>(null)
  useEffect(() => {
    if (!lastSavedTask) return
    const timer = window.setTimeout(() => setLastSavedTask(null), 12000)
    return () => window.clearTimeout(timer)
  }, [lastSavedTask])

  const lettersQuery = useQuery<ProspectLetter[]>({
    queryKey: ['prospect-letters', prospectId],
    queryFn: () => apiClient.get(`/acquisition/prospects/${prospectId}/letters`).then((r) => r.data),
    staleTime: 30_000,
  })

  const jobPostingsQuery = useQuery<ProspectJobPosting[]>({
    queryKey: ['prospect-job-postings', prospectId],
    queryFn: () => apiClient.get(`/acquisition/prospects/${prospectId}/job-postings`).then((r) => r.data),
    staleTime: 30_000,
  })

  const tasksQuery = useQuery<AcquisitionTask[]>({
    queryKey: ['prospect-tasks', prospectId],
    queryFn: () => apiClient.get(`/acquisition/tasks?prospectId=${prospectId}`).then((r) => r.data),
    staleTime: 15_000,
  })

  const historyQuery = useQuery<ProspectHistoryEntry[]>({
    queryKey: ['prospect-history', prospectId],
    queryFn: () => apiClient.get(`/acquisition/prospects/${prospectId}/history`).then((r) => r.data),
    staleTime: 30_000,
  })

  const notesQuery = useQuery<ProspectNote[]>({
    queryKey: ['prospect-notes', prospectId],
    queryFn: () => apiClient.get(`/acquisition/prospects/${prospectId}/notes`).then((r) => r.data),
    staleTime: 15_000,
  })

  const addJobPosting = useMutation({
    mutationFn: (url: string) => apiClient.post(`/acquisition/prospects/${prospectId}/job-postings`, { url }).then((r) => r.data),
    onSuccess: () => {
      setJobPostingUrl('')
      queryClient.invalidateQueries({ queryKey: ['prospect-job-postings', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['prospect-history', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
    },
  })

  const updateJobPostingDate = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string | null }) =>
      apiClient.patch(`/job-postings/${id}`, { jobPostingDate: date }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-job-postings', prospectId] })
    },
  })

  const updateTask = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/acquisition/tasks/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-tasks', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] })
    },
  })

  const createNote = useMutation({
    mutationFn: (data: { content: string; kind: ProspectNoteKind; occurredAt: string; taskId?: string }) =>
      apiClient.post(`/acquisition/prospects/${prospectId}/notes`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-notes', prospectId] })
    },
  })

  const createTask = useMutation({
    mutationFn: (data: {
      type: 'call' | 'meeting' | 'email' | 'follow_up_letter' | 'other'
      title: string
      description: string | null
      dueDate: string
      priority: 'low' | 'medium' | 'high'
    }) => apiClient.post('/acquisition/tasks', { ...data, prospectId }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-tasks', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['prospect-history', prospectId] })
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
    },
  })

  const updateNote = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { content?: string; kind?: ProspectNoteKind; occurredAt?: string } }) =>
      apiClient.patch(`/acquisition/notes/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-notes', prospectId] })
    },
  })

  const deleteNote = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/acquisition/notes/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospect-notes', prospectId] })
    },
  })

  const resetNoteDraft = () => {
    setNoteDraftContent('')
    setNoteDraftKind('call')
    setNoteDraftOccurredAt(toDatetimeLocal(new Date()))
    createNote.reset()
  }

  const resetTaskDraft = () => {
    setTaskDraftTitle('')
    setTaskDraftDescription('')
    setTaskDraftType('call')
    setTaskDraftPriority('medium')
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(9, 0, 0, 0)
    setTaskDraftDueDate(toDatetimeLocal(d))
    createTask.reset()
  }

  const submitQuickTask = () => {
    const title = taskDraftTitle.trim()
    if (!title || !taskDraftDueDate) return
    createTask.mutate(
      {
        type: taskDraftType,
        title,
        description: taskDraftDescription.trim() || null,
        dueDate: new Date(taskDraftDueDate).toISOString(),
        priority: taskDraftPriority,
      },
      {
        onSuccess: (data: unknown) => {
          const resp = data as {
            id?: string
            calendarSync?: {
              attempted: boolean
              synced: boolean
              reason?: string
              error?: string
            } | null
          } | null
          setLastSavedTask({
            id: resp?.id ?? null,
            title,
            calendarSync: resp?.calendarSync ?? null,
            at: Date.now(),
          })
          setShowQuickAddTask(false)
          resetTaskDraft()
        },
      },
    )
  }

  const submitQuickNote = () => {
    const content = noteDraftContent.trim()
    if (!content) return
    createNote.mutate(
      {
        content,
        kind: noteDraftKind,
        occurredAt: new Date(noteDraftOccurredAt).toISOString(),
      },
      {
        onSuccess: (data: unknown) => {
          const id = (data as { id?: string } | null | undefined)?.id ?? '(ohne ID)'
          setLastSavedNote({ id, preview: content.slice(0, 80), at: Date.now() })
          setShowQuickAddNote(false)
          resetNoteDraft()
        },
      },
    )
  }

  const tasksById = useMemo(() => {
    const m = new Map<string, AcquisitionTask>()
    for (const t of tasksQuery.data ?? []) m.set(t.id, t)
    return m
  }, [tasksQuery.data])

  const activities = useMemo<ProspectActivity[]>(() => {
    const items: ProspectActivity[] = []
    for (const l of lettersQuery.data ?? []) {
      items.push({ kind: 'letter', id: l.id, date: l.sentAt ?? l.createdAt, data: l })
    }
    for (const j of jobPostingsQuery.data ?? []) {
      items.push({ kind: 'job_posting', id: j.id, date: j.jobPostingDate ?? j.createdAt, data: j })
    }
    for (const t of tasksQuery.data ?? []) {
      items.push({ kind: 'task', id: t.id, date: t.dueDate, data: t })
    }
    for (const h of historyQuery.data ?? []) {
      items.push({ kind: 'history', id: h.id, date: h.createdAt, data: h })
    }
    for (const n of notesQuery.data ?? []) {
      items.push({ kind: 'note', id: n.id, date: n.occurredAt, data: n })
    }
    return items.sort((x, y) => new Date(y.date).getTime() - new Date(x.date).getTime())
  }, [lettersQuery.data, jobPostingsQuery.data, tasksQuery.data, historyQuery.data, notesQuery.data])

  const visible = filters.size === 0 ? activities : activities.filter((it) => filters.has(it.kind))
  const isLoading = lettersQuery.isLoading || jobPostingsQuery.isLoading || tasksQuery.isLoading || historyQuery.isLoading || notesQuery.isLoading

  const toggleFilter = (kind: ProspectActivityKind) =>
    setFilters((prev) => {
      const next = new Set(prev)
      if (next.has(kind)) next.delete(kind)
      else next.add(kind)
      return next
    })

  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const counts: Record<ProspectActivityKind, number> = {
    note: notesQuery.data?.length ?? 0,
    letter: lettersQuery.data?.length ?? 0,
    job_posting: jobPostingsQuery.data?.length ?? 0,
    task: tasksQuery.data?.length ?? 0,
    history: historyQuery.data?.length ?? 0,
  }

  const filterIcon: Record<ProspectActivityKind, typeof Clock> = {
    note: MessageSquare,
    task: Clock,
    letter: Mail,
    job_posting: Briefcase,
    history: History,
  }

  return (
    <div className="space-y-3">
      {/* ─── Filter & Quick-Add ─── */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => setFilters(new Set())}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
            filters.size === 0
              ? 'bg-brand-50 text-brand-700 border-brand-200'
              : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50',
          )}
        >
          Alle
          <span className="text-[10px] text-gray-400">{activities.length}</span>
        </button>
        {(Object.keys(ACTIVITY_FILTER_LABELS) as ProspectActivityKind[]).map((kind) => {
          const active = filters.has(kind)
          const Icon = filterIcon[kind]
          return (
            <button
              key={kind}
              type="button"
              onClick={() => toggleFilter(kind)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
                active
                  ? 'bg-brand-50 text-brand-700 border-brand-200'
                  : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50',
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {ACTIVITY_FILTER_LABELS[kind]}
              <span className="text-[10px] text-gray-400">{counts[kind]}</span>
            </button>
          )
        })}
        <div className="flex-1" />
        {emailAction && (
          <button
            type="button"
            onClick={emailAction.onClick}
            disabled={emailAction.disabled}
            title={emailAction.title ?? 'E-Mail über dein M365-Postfach senden'}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
              'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
            )}
          >
            <Send className="w-3.5 h-3.5" /> E-Mail
          </button>
        )}
        {letterAction && (
          <button
            type="button"
            onClick={letterAction.onClick}
            disabled={letterAction.disabled}
            title={letterAction.title}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
              'bg-white text-brand-700 border-brand-200 hover:bg-brand-50',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
            )}
          >
            <Mail className="w-3.5 h-3.5" /> {letterAction.label}
          </button>
        )}
        {linkedInAction && (
          <button
            type="button"
            onClick={linkedInAction.onClick}
            disabled={linkedInAction.disabled}
            title={linkedInAction.title ?? 'LinkedIn-Nachricht generieren (mit Bezug auf Brief und letzten Anruf)'}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
              'bg-white text-[#0A66C2] border-[#0A66C2]/30 hover:bg-[#0A66C2]/5',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
            )}
          >
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setShowQuickAddNote((s) => {
              if (s) resetNoteDraft()
              return !s
            })
          }}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
            showQuickAddNote ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          )}
        >
          <Plus className="w-3.5 h-3.5" /> Notiz
        </button>
        <button
          type="button"
          onClick={() => {
            setShowQuickAddTask((s) => {
              if (s) resetTaskDraft()
              return !s
            })
          }}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
            showQuickAddTask ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          )}
        >
          <Plus className="w-3.5 h-3.5" /> Aufgabe
        </button>
        <button
          type="button"
          onClick={() => setShowQuickAddJob((s) => !s)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors',
            showQuickAddJob ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          )}
        >
          <Plus className="w-3.5 h-3.5" /> Stellenanzeige
        </button>
      </div>

      {showQuickAddNote && (
        <div className="bg-emerald-50/40 border border-emerald-100 rounded-lg px-3 py-2.5 space-y-2">
          {/* Kein <form> hier — der gesamte Aktivitaeten-Tab steckt bereits in
              einem aeusseren <form id="prospect-detail-form">. Verschachtelte
              Forms sind ungueltiges HTML; der Browser kollabiert das innere
              Form, wodurch `type="submit"` den AEUSSEREN Submit (Prospect-
              Update) ausloest und die Notiz nicht gespeichert wird. */}
          <div className="space-y-2">
            <Textarea
              value={noteDraftContent}
              onChange={(e) => { setNoteDraftContent(e.target.value); createNote.reset() }}
              placeholder="Was wurde besprochen?"
              rows={3}
              autoFocus
              className="text-sm"
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={noteDraftKind}
                onChange={(e) => setNoteDraftKind(e.target.value as ProspectNoteKind)}
                className="px-2 py-1.5 text-xs border border-emerald-200 rounded-lg bg-white focus:outline-none focus:border-emerald-400"
              >
                {(Object.keys(NOTE_KIND_LABELS) as ProspectNoteKind[]).map((k) => (
                  <option key={k} value={k}>{NOTE_KIND_LABELS[k].label}</option>
                ))}
              </select>
              <input
                type="datetime-local"
                value={noteDraftOccurredAt}
                onChange={(e) => setNoteDraftOccurredAt(e.target.value)}
                className="px-2 py-1.5 text-xs border border-emerald-200 rounded-lg bg-white focus:outline-none focus:border-emerald-400"
              />
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => { setShowQuickAddNote(false); resetNoteDraft() }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => submitQuickNote()}
                disabled={!noteDraftContent.trim() || createNote.isPending}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                {createNote.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Speichern
              </button>
            </div>
          </div>
          {createNote.isError && (() => {
            const err = createNote.error as
              | { message?: string; response?: { status?: number; data?: unknown } }
              | undefined
            const status = err?.response?.status
            const body = err?.response?.data
            return (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-1">
                <p className="flex items-center gap-1 font-medium">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Notiz konnte nicht gespeichert werden
                  {status != null && <span className="text-red-500">(HTTP {status})</span>}
                </p>
                {err?.message && <p className="text-red-600">{err.message}</p>}
                {body !== undefined && body !== null && (
                  <pre className="text-[10px] text-red-600 whitespace-pre-wrap break-words font-mono">
                    {typeof body === 'string' ? body : JSON.stringify(body, null, 2)}
                  </pre>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {showQuickAddTask && (
        <div className="bg-amber-50/40 border border-amber-100 rounded-lg px-3 py-2.5 space-y-2">
          {/* Kein <form> — siehe Kommentar beim Notiz-Quickadd: der Aktivitaeten-
              Tab steckt schon im aeusseren Prospect-Detail-Form. */}
          <div className="space-y-2">
            <Input
              value={taskDraftTitle}
              onChange={(e) => { setTaskDraftTitle(e.target.value); createTask.reset() }}
              placeholder={'Aufgabe (z.B. „Donnerstag um 14:00 anrufen")'}
              autoFocus
              className="text-sm"
            />
            <Textarea
              value={taskDraftDescription}
              onChange={(e) => { setTaskDraftDescription(e.target.value); createTask.reset() }}
              placeholder="Beschreibung (optional)"
              rows={2}
              className="text-sm"
            />
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={taskDraftType}
                onChange={(e) => setTaskDraftType(e.target.value as typeof taskDraftType)}
                className="px-2 py-1.5 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
              >
                <option value="call">Anruf</option>
                <option value="meeting">Termin</option>
                <option value="email">E-Mail</option>
                <option value="follow_up_letter">Follow-up Brief</option>
                <option value="other">Sonstiges</option>
              </select>
              <select
                value={taskDraftPriority}
                onChange={(e) => setTaskDraftPriority(e.target.value as typeof taskDraftPriority)}
                className="px-2 py-1.5 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
                title="Priorität"
              >
                <option value="low">Niedrig</option>
                <option value="medium">Mittel</option>
                <option value="high">Hoch</option>
              </select>
              <input
                type="datetime-local"
                value={taskDraftDueDate}
                onChange={(e) => setTaskDraftDueDate(e.target.value)}
                className="px-2 py-1.5 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
              />
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => { setShowQuickAddTask(false); resetTaskDraft() }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => submitQuickTask()}
                disabled={!taskDraftTitle.trim() || !taskDraftDueDate || createTask.isPending}
                className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                {createTask.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Speichern
              </button>
            </div>
          </div>
          {createTask.isError && (() => {
            const err = createTask.error as
              | { message?: string; response?: { status?: number; data?: unknown } }
              | undefined
            const status = err?.response?.status
            const body = err?.response?.data
            return (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-1">
                <p className="flex items-center gap-1 font-medium">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Aufgabe konnte nicht gespeichert werden
                  {status != null && <span className="text-red-500">(HTTP {status})</span>}
                </p>
                {err?.message && <p className="text-red-600">{err.message}</p>}
                {body !== undefined && body !== null && (
                  <pre className="text-[10px] text-red-600 whitespace-pre-wrap break-words font-mono">
                    {typeof body === 'string' ? body : JSON.stringify(body, null, 2)}
                  </pre>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {showQuickAddJob && (
        <div className="bg-indigo-50/40 border border-indigo-100 rounded-lg px-3 py-2.5 space-y-2">
          {/* Auch hier kein <form> — siehe Kommentar oben beim Notiz-Quickadd. */}
          <div className="flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
            <input
              type="text"
              value={jobPostingUrl}
              onChange={(e) => { setJobPostingUrl(e.target.value); addJobPosting.reset() }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && jobPostingUrl.trim()) {
                  e.preventDefault()
                  addJobPosting.mutate(jobPostingUrl.trim())
                }
              }}
              placeholder="Stellenanzeigen-URL einfügen..."
              className="flex-1 px-3 py-1.5 text-xs border border-indigo-200 rounded-lg bg-white focus:outline-none focus:border-indigo-400"
            />
            <button
              type="button"
              onClick={() => { if (jobPostingUrl.trim()) addJobPosting.mutate(jobPostingUrl.trim()) }}
              disabled={!jobPostingUrl.trim() || addJobPosting.isPending}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              {addJobPosting.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Hinzufügen
            </button>
          </div>
          {addJobPosting.isSuccess && (
            <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Wird analysiert...</p>
          )}
          {addJobPosting.isError && (
            <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Fehler beim Hinzufügen</p>
          )}
        </div>
      )}

      {/* Bestaetigung: zuletzt gespeicherte Notiz (Server hat 201 zurueckgegeben). */}
      {lastSavedNote && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 flex items-start gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="font-medium">Notiz gespeichert</span>
            <span className="text-emerald-600"> · ID {lastSavedNote.id}</span>
            {lastSavedNote.preview && (
              <span className="text-emerald-600"> · „{lastSavedNote.preview}"</span>
            )}
          </div>
        </div>
      )}

      {/* Bestaetigung: zuletzt gespeicherte Aufgabe inkl. Kalender-Sync-Status.
          Macht sichtbar, ob/warum kein M365-Termin entstanden ist — sonst
          glaubt der Vertriebler der Termin sei im Kalender, ist er aber nicht. */}
      {lastSavedTask && (() => {
        const cs = lastSavedTask.calendarSync
        const synced = cs?.synced === true
        const tone = synced
          ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
          : cs?.error
            ? 'text-red-700 bg-red-50 border-red-200'
            : 'text-amber-800 bg-amber-50 border-amber-200'
        return (
          <div className={cn('text-xs border rounded px-2 py-1.5 flex items-start gap-1.5', tone)}>
            {synced ? (
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            ) : cs?.error ? (
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            ) : (
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div>
                <span className="font-medium">Aufgabe gespeichert</span>
                <span className="opacity-70"> · „{lastSavedTask.title}"</span>
              </div>
              {synced && (
                <div className="opacity-80">
                  Termin im Outlook-Kalender angelegt.
                </div>
              )}
              {!synced && cs?.error && (
                <div className="opacity-90">Kalender-Sync fehlgeschlagen: {cs.error}</div>
              )}
              {!synced && !cs?.error && cs?.reason && (
                <div className="opacity-80">Kein Kalender-Eintrag: {cs.reason}</div>
              )}
              {!synced && !cs && (
                <div className="opacity-80">Kein Kalender-Status zurückgegeben (alte Server-Version?).</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setLastSavedTask(null)}
              className="opacity-50 hover:opacity-100 shrink-0"
              aria-label="Hinweis schließen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )
      })()}

      {/* Query-Fehler sichtbar machen — sonst sieht der Nutzer einen leeren
          Feed, obwohl der Server einen Fehler liefert. */}
      {(() => {
        const queryErrors: Array<{ label: string; err: unknown }> = []
        if (notesQuery.error) queryErrors.push({ label: 'Notizen', err: notesQuery.error })
        if (lettersQuery.error) queryErrors.push({ label: 'Briefe', err: lettersQuery.error })
        if (jobPostingsQuery.error) queryErrors.push({ label: 'Stellenanzeigen', err: jobPostingsQuery.error })
        if (tasksQuery.error) queryErrors.push({ label: 'Aufgaben', err: tasksQuery.error })
        if (historyQuery.error) queryErrors.push({ label: 'Änderungen', err: historyQuery.error })
        if (queryErrors.length === 0) return null
        return (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-1">
            <p className="flex items-center gap-1 font-medium">
              <AlertTriangle className="w-3.5 h-3.5" />
              Aktivitäten konnten nicht vollständig geladen werden
            </p>
            {queryErrors.map(({ label, err }) => {
              const e = err as { message?: string; response?: { status?: number; data?: unknown } }
              const status = e?.response?.status
              const body = e?.response?.data
              // Server liefert via errorHandler.ts { error: "..." } — inline anzeigen
              // statt nur axios' generisches "Request failed with status code 500".
              const serverMessage =
                body && typeof body === 'object' && 'error' in body
                  ? String((body as { error: unknown }).error)
                  : null
              return (
                <details key={label} className="text-[11px] text-red-600" open>
                  <summary className="cursor-pointer">
                    <span className="font-medium">{label}</span>
                    {status != null && <span> · HTTP {status}</span>}
                    {serverMessage ? (
                      <span> · {serverMessage}</span>
                    ) : (
                      e?.message && <span> · {e.message}</span>
                    )}
                  </summary>
                  {body !== undefined && body !== null && (
                    <pre className="mt-1 text-[10px] whitespace-pre-wrap break-words font-mono bg-red-100/60 rounded px-1.5 py-1">
                      {typeof body === 'string' ? body : JSON.stringify(body, null, 2)}
                    </pre>
                  )}
                </details>
              )
            })}
          </div>
        )
      })()}

      {/* ─── Feed ─── */}
      {isLoading && activities.length === 0 ? (
        <LoadingState label="Lade Aktivitäten..." size="sm" />
      ) : visible.length === 0 ? (
        <EmptyState
          size="sm"
          title={filters.size === 0 ? 'Noch keine Aktivitäten für diesen Zielkunden' : 'Keine Aktivitäten in dieser Auswahl'}
        />
      ) : (
        <div className="space-y-1.5">
          {visible.map((item) => {
            const key = `${item.kind}-${item.id}`
            const isExpanded = expanded.has(key)

            if (item.kind === 'task') {
              const task = item.data
              const TaskIcon = TASK_TYPE_ICONS[task.type] ?? Info
              const isDone = task.status === 'completed' || task.status === 'skipped'
              // Task-Outcome: bei Gespraechs-/Kontakt-Aufgaben oeffnet der Haken
              // stattdessen ein Outcome-Panel — so landet das Gespraechsergebnis
              // direkt als verknuepfte Notiz in der Timeline.
              const supportsOutcome =
                !isDone && (task.type === 'call' || task.type === 'meeting' || task.type === 'email' || task.type === 'follow_up_letter')
              const outcomeOpen = taskOutcomeId === task.id
              const editOpen = editingTaskId === task.id

              const startEditTask = () => {
                setEditingTaskId(task.id)
                setEditTaskTitle(task.title)
                setEditTaskDescription(task.description ?? '')
                setEditTaskType(((['call', 'meeting', 'email', 'follow_up_letter', 'other'] as const).includes(task.type as 'call' | 'meeting' | 'email' | 'follow_up_letter' | 'other')
                  ? task.type
                  : 'other') as 'call' | 'meeting' | 'email' | 'follow_up_letter' | 'other')
                setEditTaskPriority(((['low', 'medium', 'high'] as const).includes(task.priority as 'low' | 'medium' | 'high')
                  ? task.priority
                  : 'medium') as 'low' | 'medium' | 'high')
                setEditTaskDueDate(toDatetimeLocal(new Date(task.dueDate)))
                updateTask.reset()
              }

              const saveEditTask = () => {
                const title = editTaskTitle.trim()
                if (!title || !editTaskDueDate) return
                updateTask.mutate(
                  {
                    id: task.id,
                    data: {
                      title,
                      description: editTaskDescription.trim() || null,
                      type: editTaskType,
                      priority: editTaskPriority,
                      dueDate: new Date(editTaskDueDate).toISOString(),
                    },
                  },
                  {
                    onSuccess: () => {
                      setEditingTaskId(null)
                    },
                  },
                )
              }

              const handleCheckboxClick = () => {
                if (isDone) {
                  updateTask.mutate({ id: task.id, data: { status: 'pending' } })
                  return
                }
                if (supportsOutcome) {
                  setTaskOutcomeId(task.id)
                  setTaskOutcomeText('')
                  return
                }
                updateTask.mutate({ id: task.id, data: { status: 'completed' } })
              }

              const completeWithNote = () => {
                const text = taskOutcomeText.trim()
                if (!text) return
                createNote.mutate(
                  {
                    content: text,
                    kind: taskTypeToNoteKind(task.type),
                    occurredAt: new Date().toISOString(),
                    taskId: task.id,
                  },
                  {
                    onSuccess: () => {
                      updateTask.mutate({ id: task.id, data: { status: 'completed' } })
                      setTaskOutcomeId(null)
                      setTaskOutcomeText('')
                    },
                  },
                )
              }

              const completeWithoutNote = () => {
                updateTask.mutate({ id: task.id, data: { status: 'completed' } })
                setTaskOutcomeId(null)
                setTaskOutcomeText('')
              }

              return (
                <div key={key} className={cn(
                  'border rounded-lg text-sm transition',
                  isDone
                    ? 'border-gray-100 bg-gray-50 opacity-60'
                    : outcomeOpen
                      ? 'border-emerald-300 bg-emerald-50/40 ring-2 ring-emerald-200'
                      : 'border-gray-200 bg-white hover:border-brand-200',
                )}>
                  <div className="flex items-start gap-3 p-3">
                    <button
                      type="button"
                      onClick={handleCheckboxClick}
                      disabled={updateTask.isPending}
                      className={cn(
                        'mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition',
                        isDone
                          ? 'bg-green-500 border-green-500 text-white'
                          : outcomeOpen
                            ? 'bg-emerald-100 border-emerald-400 text-emerald-700 animate-pulse'
                            : 'bg-white border-gray-300 hover:border-green-400',
                      )}
                      title={
                        isDone ? 'Als offen markieren'
                        : outcomeOpen ? 'Aufgabe wartet auf Abschluss — bitte unten "Nur erledigen" oder "Notiz speichern" klicken'
                        : 'Als erledigt markieren'
                      }
                    >
                      {isDone ? <Check className="w-3 h-3" /> : outcomeOpen ? <Loader2 className="w-3 h-3" /> : null}
                    </button>
                    <TaskIcon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className={cn('font-medium text-xs', isDone ? 'text-gray-500 line-through' : 'text-gray-900')}>
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400 flex-wrap">
                        <span>Fällig: {new Date(task.dueDate).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        <span>•</span>
                        <PriorityBadge priority={(task.priority as Priority) ?? 'medium'} size="sm" />
                        {task.assignedUserName && (
                          <>
                            <span>•</span>
                            <span title="Zuständig">Zuständig: {a.name(task.assignedUserName)}</span>
                          </>
                        )}
                      </div>
                      {task.status === 'completed' && (task.completedAt || task.completedByUserName) && (
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-emerald-700 flex-wrap">
                          <Check className="w-3 h-3" />
                          <span>
                            Erledigt
                            {task.completedByUserName ? ` von ${a.name(task.completedByUserName)}` : ''}
                            {task.completedAt
                              ? ` am ${new Date(task.completedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                              : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    {!isDone && (
                      <>
                        <button
                          type="button"
                          onClick={() => editOpen ? setEditingTaskId(null) : startEditTask()}
                          disabled={updateTask.isPending}
                          className={cn(
                            'text-xs shrink-0',
                            editOpen ? 'text-amber-600' : 'text-gray-400 hover:text-gray-600',
                          )}
                          title="Bearbeiten"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => updateTask.mutate({ id: task.id, data: { status: 'skipped' } })}
                          disabled={updateTask.isPending}
                          className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
                          title="Überspringen"
                        >
                          <SkipForward className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  {editOpen && (
                    <div className="border-t border-gray-200 bg-amber-50/30 px-3 py-2.5 space-y-2">
                      <Input
                        value={editTaskTitle}
                        onChange={(e) => { setEditTaskTitle(e.target.value); updateTask.reset() }}
                        placeholder="Aufgabentitel"
                        className="text-sm"
                      />
                      <Textarea
                        value={editTaskDescription}
                        onChange={(e) => { setEditTaskDescription(e.target.value); updateTask.reset() }}
                        placeholder="Beschreibung (optional)"
                        rows={2}
                        className="text-sm"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={editTaskType}
                          onChange={(e) => setEditTaskType(e.target.value as typeof editTaskType)}
                          className="px-2 py-1.5 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
                        >
                          <option value="call">Anruf</option>
                          <option value="meeting">Termin</option>
                          <option value="email">E-Mail</option>
                          <option value="follow_up_letter">Follow-up Brief</option>
                          <option value="other">Sonstiges</option>
                        </select>
                        <select
                          value={editTaskPriority}
                          onChange={(e) => setEditTaskPriority(e.target.value as typeof editTaskPriority)}
                          className="px-2 py-1.5 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
                          title="Priorität"
                        >
                          <option value="low">Niedrig</option>
                          <option value="medium">Mittel</option>
                          <option value="high">Hoch</option>
                        </select>
                        <input
                          type="datetime-local"
                          value={editTaskDueDate}
                          onChange={(e) => setEditTaskDueDate(e.target.value)}
                          className="px-2 py-1.5 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:border-amber-400"
                        />
                        <div className="flex-1" />
                        <button
                          type="button"
                          onClick={() => setEditingTaskId(null)}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                        >
                          Abbrechen
                        </button>
                        <button
                          type="button"
                          onClick={saveEditTask}
                          disabled={!editTaskTitle.trim() || !editTaskDueDate || updateTask.isPending}
                          className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
                        >
                          {updateTask.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Speichern
                        </button>
                      </div>
                      {updateTask.isError && (() => {
                        const err = updateTask.error as
                          | { message?: string; response?: { status?: number; data?: unknown } }
                          | undefined
                        const status = err?.response?.status
                        const body = err?.response?.data
                        return (
                          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-1">
                            <p className="flex items-center gap-1 font-medium">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Aufgabe konnte nicht gespeichert werden
                              {status != null && <span className="text-red-500">(HTTP {status})</span>}
                            </p>
                            {err?.message && <p className="text-red-600">{err.message}</p>}
                            {body !== undefined && body !== null && (
                              <pre className="text-[10px] text-red-600 whitespace-pre-wrap break-words font-mono">
                                {typeof body === 'string' ? body : JSON.stringify(body, null, 2)}
                              </pre>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  )}
                  {outcomeOpen && (
                    <div className="border-t border-gray-200 bg-emerald-50/30 px-3 py-2.5 space-y-2">
                      <p className="text-[11px] font-medium text-emerald-800">
                        Was kam beim {NOTE_KIND_LABELS[taskTypeToNoteKind(task.type)].label.toLowerCase()} raus? (optional)
                        <span className="ml-2 font-normal text-emerald-700/70">
                          — Strg+Enter speichert, Esc bricht ab.
                        </span>
                      </p>
                      <Textarea
                        value={taskOutcomeText}
                        onChange={(e) => { setTaskOutcomeText(e.target.value); createNote.reset() }}
                        onKeyDown={(e) => {
                          // Strg/Cmd+Enter = speichern (mit Notiz wenn da, sonst „Nur erledigen").
                          // Plain Enter laesst Textarea-Newlines zu.
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            if (taskOutcomeText.trim()) completeWithNote()
                            else completeWithoutNote()
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            setTaskOutcomeId(null)
                            setTaskOutcomeText('')
                          }
                        }}
                        placeholder="z.B. Chef war im Urlaub, Rückruf am 22.4. vereinbart"
                        rows={2}
                        autoFocus
                        className="text-sm"
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => { setTaskOutcomeId(null); setTaskOutcomeText('') }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                        >
                          Abbrechen
                        </button>
                        <div className="flex-1" />
                        <button
                          type="button"
                          onClick={completeWithoutNote}
                          disabled={updateTask.isPending}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Nur erledigen
                        </button>
                        <button
                          type="button"
                          onClick={completeWithNote}
                          disabled={!taskOutcomeText.trim() || createNote.isPending || updateTask.isPending}
                          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-colors"
                        >
                          {createNote.isPending || updateTask.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Notiz speichern &amp; erledigen
                        </button>
                      </div>
                      {createNote.isError && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Notiz konnte nicht gespeichert werden
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )
            }

            if (item.kind === 'letter') {
              const l = item.data
              return (
                <div key={key} className="bg-white rounded-lg border border-amber-100">
                  <button
                    type="button"
                    onClick={() => toggleExpand(key)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-amber-50/50 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <span className="font-medium text-gray-800 truncate">
                        {l.channel === 'job_posting' ? 'Stellenanzeige-Brief' : 'Akquise-Brief'}
                      </span>
                      {(() => {
                        const info = LETTER_STATUS_LABELS[l.status] ?? { label: l.status, color: 'bg-amber-100 text-amber-700' }
                        return (
                          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0', info.color)}>
                            {info.label}
                          </span>
                        )
                      })()}
                      {l.referenceCustomer && (
                        <span className="text-gray-400 truncate">Ref: {a.name(l.referenceCustomer.companyName)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {l.sentByUserName && (l.status === 'sent' || l.status === 'delivered' || l.status === 'sending') && (
                        <span className="text-gray-500 truncate" title="Versendet von">
                          {a.name(l.sentByUserName)}
                        </span>
                      )}
                      <span className="text-gray-400">
                        {l.sentAt
                          ? new Date(l.sentAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : new Date(l.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {l.status === 'draft' && <LetterDeleteButton letter={l} prospectId={prospectId} />}
                    </div>
                  </button>
                  {isExpanded && (
                    <>
                      <div className="px-3 py-2 border-t border-amber-100/50 text-xs text-gray-600 whitespace-pre-wrap font-serif max-h-48 overflow-auto">
                        {l.personalizedBody}
                      </div>
                      <LetterStatusToggle letter={l} prospectId={prospectId} />
                      <LetterOrderTracker letter={l} prospectId={prospectId} />
                    </>
                  )}
                </div>
              )
            }

            if (item.kind === 'job_posting') {
              const jp = item.data
              return (
                <div key={key} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-indigo-100">
                  <div className="flex items-center gap-3 min-w-0">
                    <Briefcase className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <span className="font-medium text-gray-800 truncate">{jp.jobTitle}</span>
                    {jp.jobSource && <span className="text-gray-400 shrink-0">{jp.jobSource}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <JobPostingDateEditor
                      date={jp.jobPostingDate ?? jp.createdAt}
                      isFallback={!jp.jobPostingDate}
                      onSave={(date) => updateJobPostingDate.mutate({ id: jp.id, date })}
                      isSaving={updateJobPostingDate.isPending}
                    />
                    <a href={jp.jobUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:text-indigo-700">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )
            }

            if (item.kind === 'note') {
              const n = item.data
              const kindInfo = NOTE_KIND_LABELS[n.kind]
              const KindIcon = kindInfo.icon
              const linkedTask = n.taskId ? tasksById.get(n.taskId) : null
              const isEditing = editingNoteId === n.id
              return (
                <div key={key} className="bg-white rounded-lg border border-emerald-100 hover:border-emerald-200 transition-colors">
                  <div className="flex items-start gap-2 px-3 py-2">
                    <KindIcon className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 flex-wrap">
                        <span className="font-medium text-emerald-700">{kindInfo.label}</span>
                        {n.userName && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>{a.name(n.userName)}</span>
                          </>
                        )}
                        <span className="text-gray-300">·</span>
                        <span>{new Date(n.occurredAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {isEditing ? (
                        <div className="mt-2 space-y-2">
                          <Textarea
                            value={editingNoteContent}
                            onChange={(e) => { setEditingNoteContent(e.target.value); updateNote.reset() }}
                            rows={3}
                            autoFocus
                            className="text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => { setEditingNoteId(null); setEditingNoteContent('') }}
                              className="px-3 py-1 text-xs font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                            >
                              Abbrechen
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const text = editingNoteContent.trim()
                                if (!text) return
                                updateNote.mutate(
                                  { id: n.id, data: { content: text } },
                                  {
                                    onSuccess: () => {
                                      setEditingNoteId(null)
                                      setEditingNoteContent('')
                                    },
                                  },
                                )
                              }}
                              disabled={!editingNoteContent.trim() || updateNote.isPending}
                              className="flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg disabled:opacity-50"
                            >
                              {updateNote.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                              Speichern
                            </button>
                            {updateNote.isError && (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" /> Fehler
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">{n.content}</p>
                      )}
                      {linkedTask && !isEditing && (
                        <p className="mt-1 text-[11px] text-gray-400 flex items-center gap-1">
                          <ArrowRightCircle className="w-3 h-3 shrink-0" />
                          aus Aufgabe: <span className="text-gray-500">{linkedTask.title}</span>
                        </p>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => { setEditingNoteId(n.id); setEditingNoteContent(n.content); updateNote.reset() }}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setNoteToDelete(n.id)}
                          disabled={deleteNote.isPending}
                          className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50"
                          title="Löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  {noteToDelete === n.id && (
                    <div className="border-t border-red-100 bg-red-50/60 px-3 py-2 flex items-center gap-2 text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                      <span className="text-red-700 flex-1">Notiz wirklich löschen?</span>
                      <button
                        type="button"
                        onClick={() => setNoteToDelete(null)}
                        className="px-2 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      >
                        Abbrechen
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteNote.mutate(n.id, { onSuccess: () => setNoteToDelete(null) })}
                        disabled={deleteNote.isPending}
                        className="px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-1"
                      >
                        {deleteNote.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              )
            }

            // item.kind === 'history'
            const h = item.data
            return (
              <div key={key} className="flex items-start gap-2 text-xs px-3 py-1.5">
                <History className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" />
                <span className="text-gray-400 whitespace-nowrap shrink-0">{new Date(h.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap shrink-0',
                  h.action === 'create' ? 'bg-green-100 text-green-700' :
                  h.action === 'send' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-600')}>
                  {h.action}
                </span>
                {h.userName && (
                  <span className="text-gray-500 whitespace-nowrap shrink-0">{a.name(h.userName)}</span>
                )}
                <span className="text-gray-600 min-w-0">
                  {(h.details as any)?.source === 'job_posting_matching' ? 'Über Stellenanzeige verknüpft' :
                   (h.details as any)?.source === 'job_posting_edit' ? `Felder aktualisiert: ${(h.details as any)?.fieldsUpdated?.join(', ')}` :
                   (h.details as any)?.source === 'job_posting_status_change' ? `Status: ${(h.details as any)?.newStatus}` :
                   (h.details as any)?.channel === 'job_posting' ? 'Brief versendet (Stellenanzeige)' :
                   (h.details as any)?.field ? `${(h.details as any).field}: ${(h.details as any).oldValue} → ${(h.details as any).newValue}` :
                   JSON.stringify(h.details)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Component ──────────────────────────────────────────────────────────────

export function NeukundengewinnungPage() {
  const a = useAnonymize()
  const { isBeta } = useModuleStatus('neukundengewinnung')
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>('daily')
  const [showAddProspect, setShowAddProspect] = useState(false)
  const [showQuickCapture, setShowQuickCapture] = useState(false)
  const [detailProspect, setDetailProspect] = useState<Prospect | null>(null)
  const queryClient = useQueryClient()

  const [prospectsPage, setProspectsPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [sourceChannelFilter, setSourceChannelFilter] = useState<string>('')
  const [campaignFilter, setCampaignFilter] = useState<string>('')
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<string>('')
  const [dailyCampaignFilter, setDailyCampaignFilter] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // Prospect row expansion (contact history) and letter preview — state lives
  // on the main component so the modal overlay renders at top-level.
  const [expandedProspectId, setExpandedProspectId] = useState<string | null>(null)
  const [previewLetterProspect, setPreviewLetterProspect] = useState<Prospect | null>(null)
  const PROSPECTS_PER_PAGE = 50

  // Data queries
  const dailyProspects = useQuery<Prospect[]>({
    queryKey: ['acquisition', 'daily', dailyCampaignFilter],
    queryFn: () => apiClient.get(`/acquisition/prospects/daily?count=10${dailyCampaignFilter ? `&campaignId=${dailyCampaignFilter}` : ''}`).then((r) => r.data),
    retry: false,
  })

  // Load campaigns for filter dropdowns
  const campaignsList = useQuery<Array<{ id: string; name: string; status: string }>>({
    queryKey: ['acquisition', 'campaigns'],
    queryFn: () => apiClient.get('/acquisition/campaigns').then((r) => r.data),
    retry: false,
  })

  const allProspects = useQuery<{ prospects: Prospect[]; total: number }>({
    queryKey: ['acquisition', 'prospects', prospectsPage, statusFilter, sourceChannelFilter, debouncedSearch, campaignFilter, campaignTypeFilter],
    queryFn: () => apiClient.get(`/acquisition/prospects?limit=${PROSPECTS_PER_PAGE}&offset=${prospectsPage * PROSPECTS_PER_PAGE}${statusFilter ? `&status=${statusFilter}` : ''}${sourceChannelFilter ? `&sourceChannel=${sourceChannelFilter}` : ''}${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''}${campaignFilter ? `&campaignId=${campaignFilter}` : ''}${campaignTypeFilter ? `&campaignType=${campaignTypeFilter}` : ''}`).then((r) => r.data),
    enabled: tab === 'prospects',
    retry: false,
  })

  // "Heutige Aufgaben": alle pending/in_progress Tasks, deren Faelligkeit heute
  // oder in der Vergangenheit liegt. `dueBy=today` filtert serverseitig, damit
  // ueberfaellige Tasks nicht durch das Limit aus der Liste fallen.
  const tasks = useQuery<AcquisitionTask[]>({
    queryKey: ['acquisition', 'tasks'],
    queryFn: () => apiClient.get('/acquisition/tasks?dueBy=today').then((r) => r.data),
    retry: false,
  })

  // Aufgaben-Tab: separate Query mit eigenem Filter, damit die Tagesansicht
  // (`tasks`) unabhaengig bleibt. `status=all` liefert auch erledigte/skipped.
  // `due` = pending/in_progress + dueBy=today (also faellige inkl. ueberfaellig).
  const [tasksTabFilter, setTasksTabFilter] = useState<TaskFilter>('open')
  const tasksTabQuery = useMemo(() => {
    // `assignee=all` umgeht den Default-User-Scope des Endpoints. Die
    // Tagesansicht zeigt bewusst nur die eigenen Tasks (Daily Workload), der
    // Aufgaben-Tab dagegen soll die teamweite Akquise-Uebersicht sein —
    // inklusive Tasks ohne Zuweisung oder anderer Bearbeiter.
    if (tasksTabFilter === 'due') return 'status=open&dueBy=today&assignee=all'
    if (tasksTabFilter === 'completed') return 'status=completed&assignee=all'
    if (tasksTabFilter === 'all') return 'status=all&assignee=all'
    return 'status=open&assignee=all'
  }, [tasksTabFilter])
  const allTasks = useQuery<AcquisitionTask[]>({
    queryKey: ['acquisition', 'tasks', 'tab', tasksTabFilter],
    queryFn: () => apiClient.get(`/acquisition/tasks?${tasksTabQuery}`).then((r) => r.data),
    enabled: tab === 'tasks',
    retry: false,
  })

  // Beim Wechsel auf den Aufgaben-Tab: Outlook-Kalender mit den verknuepften
  // Akquise-Aufgaben abgleichen, damit verschobene Termine das `dueDate`
  // nachziehen und in Outlook geloeschte Termine als erledigt erkannt werden.
  // Server-seitig laeuft derselbe Sync zusaetzlich periodisch via Cron.
  const tasksTabSyncRef = useRef(false)
  useEffect(() => {
    if (tab !== 'tasks' || tasksTabSyncRef.current) return
    tasksTabSyncRef.current = true
    apiClient.post('/acquisition/tasks/sync-from-calendar', {})
      .then(({ data }) => {
        if ((data?.moved ?? 0) > 0 || (data?.deleted ?? 0) > 0) {
          queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] })
        }
      })
      .catch(() => { /* still keine Fehler — der Cron syncs ohnehin alle 15 Min */ })
  }, [tab, queryClient])

  const kpis = useQuery<KPIs>({
    queryKey: ['acquisition', 'kpis'],
    queryFn: () => apiClient.get('/acquisition/kpis').then((r) => r.data),
    retry: false,
  })

  // Pipeline-Tick: einmalig beim Mount. Das Backend rate-limitet auf 1×/h,
  // d. h. mehrere Reloads sind harmlos (204 No-Op). Fehler zeigen wir im
  // UI an (CLAUDE.md-Regel: keine stillen Fehler).
  const [tickError, setTickError] = useState<string | null>(null)
  const [tickSummary, setTickSummary] = useState<{
    affectedProspects: number
    affectedTasks: number
    skipped: boolean
  } | null>(null)
  const [tickRunning, setTickRunning] = useState(false)
  const runPipelineTick = useCallback((force: boolean) => {
    setTickRunning(true)
    setTickError(null)
    return apiClient.post(`/acquisition/pipeline/tick${force ? '?force=1' : ''}`, {})
      .then(({ data }) => {
        if (data?.skipped) {
          setTickSummary({ affectedProspects: 0, affectedTasks: 0, skipped: true })
          return
        }
        setTickSummary({
          affectedProspects: data?.affectedProspects ?? 0,
          affectedTasks: data?.affectedTasks ?? 0,
          skipped: false,
        })
        // Bei tatsaechlichen Aenderungen Daten neu laden
        if ((data?.affectedProspects ?? 0) + (data?.affectedTasks ?? 0) > 0) {
          queryClient.invalidateQueries({ queryKey: ['acquisition'] })
        }
      })
      .catch((err: { response?: { data?: { error?: string } }; message?: string }) => {
        setTickError(err?.response?.data?.error || err?.message || 'Pipeline-Tick fehlgeschlagen')
      })
      .finally(() => setTickRunning(false))
  }, [queryClient])

  const tickRanRef = useRef(false)
  useEffect(() => {
    if (tickRanRef.current) return
    tickRanRef.current = true
    runPipelineTick(false).then(() => {
      // Neue Aufgaben ohne Kalender-Eintrag eintragen. Das Zurueckschreiben
      // verschobener Outlook-Termine laeuft inzwischen ueber den Cron-Job
      // `acquisition.calendar.sync-from-outlook` (alle 5 min) — nicht mehr
      // beim Seitenaufruf, damit der User auch ohne aktive Browser-Session
      // einen aktuellen Stand hat.
      apiClient.post('/acquisition/tasks/sync-outstanding', {})
        .then(() => queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] }))
        .catch(() => {})
    })
  }, [runPipelineTick, queryClient])

  // Deep-link: ?prospect=UUID oeffnet die Detailansicht direkt
  const deepLinkProspectId = searchParams.get('prospect')
  useEffect(() => {
    if (!deepLinkProspectId) return
    apiClient.get<Prospect>(`/acquisition/prospects/${deepLinkProspectId}`)
      .then(({ data }) => {
        setDetailProspect(data)
        setSearchParams({}, { replace: true })
      })
      .catch(() => {/* ignore if not found */})
  }, [deepLinkProspectId, setSearchParams])

  const upsertTask = useBackgroundTasksStore((s) => s.upsertTask)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchFilter)
      setProspectsPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchFilter])

  const discoveryPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startDiscoveryPolling = () => {
    if (discoveryPollRef.current) clearInterval(discoveryPollRef.current)
    discoveryPollRef.current = setInterval(() => {
      apiClient.get('/acquisition/discovery/status').then(({ data }) => {
        if (data.log?.length) {
          const last = data.log[data.log.length - 1]
          upsertTask({
            id: 'discovery',
            label: 'Firmensuche',
            detail: last && !data.done ? `${last.step}: ${last.detail}` : 'Firmensuche läuft...',
            status: data.done ? (data.message?.includes('Fehler') ? 'error' : 'done') : 'running',
            log: data.log,
            updatedAt: Date.now(),
          })
        }
        if (data.done) {
          upsertTask({
            id: 'discovery',
            label: 'Firmensuche',
            detail: data.message || 'Firmensuche abgeschlossen',
            status: data.message?.includes('Fehler') ? 'error' : 'done',
            log: data.log,
            updatedAt: Date.now(),
          })
          queryClient.invalidateQueries({ queryKey: ['acquisition'] })
          if (discoveryPollRef.current) { clearInterval(discoveryPollRef.current); discoveryPollRef.current = null }
        }
      }).catch(() => {})
    }, 2000)
    setTimeout(() => { if (discoveryPollRef.current) { clearInterval(discoveryPollRef.current); discoveryPollRef.current = null } }, 600000)
  }

  // Check for running discovery on mount — resume polling from page-level
  useEffect(() => {
    const persistedTask = useBackgroundTasksStore.getState().tasks['discovery']
    const hadPersistedRunning = persistedTask?.status === 'running'

    apiClient.get('/acquisition/discovery/status').then(({ data }) => {
      if (data?.done) {
        // Discovery finished
        if (hadPersistedRunning || (data.updatedAt && Date.now() - data.updatedAt < 30000)) {
          upsertTask({
            id: 'discovery', label: 'Firmensuche',
            detail: data.message || 'Firmensuche abgeschlossen',
            status: data.message?.includes('Fehler') ? 'error' : 'done',
            log: data.log, updatedAt: Date.now(),
          })
          queryClient.invalidateQueries({ queryKey: ['acquisition'] })
        }
        return
      }
      if (data?.log?.length) {
        // Discovery is running with log data
        const last = data.log[data.log.length - 1]
        upsertTask({
          id: 'discovery', label: 'Firmensuche',
          detail: last ? `${last.step}: ${last.detail}` : 'Firmensuche läuft...',
          status: 'running', log: data.log, updatedAt: Date.now(),
        })
        startDiscoveryPolling()
        return
      }
      // Backend returned empty — if we had a persisted running task, poll anyway
      // (Redis might be down but task still running in Node process)
      if (hadPersistedRunning) {
        startDiscoveryPolling()
      }
    }).catch(() => {
      // Network error — if persisted task was running, try polling
      if (hadPersistedRunning) startDiscoveryPolling()
    })
    return () => { if (discoveryPollRef.current) { clearInterval(discoveryPollRef.current); discoveryPollRef.current = null } }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const triggerDiscovery = useMutation({
    mutationFn: (opts?: { forceRefresh?: boolean }) => apiClient.post('/acquisition/discovery/trigger', { forceRefresh: opts?.forceRefresh ?? false }).then((r) => r.data),
    onSuccess: () => {
      upsertTask({
        id: 'discovery', label: 'Firmensuche',
        detail: 'Firmensuche wird gestartet – lade Geo-Zonen und Branchen...',
        status: 'running', log: [], updatedAt: Date.now(),
      })
      startDiscoveryPolling()
    },
    onError: (err: { response?: { data?: { error?: string; message?: string } } }) => {
      upsertTask({
        id: 'discovery', label: 'Firmensuche',
        detail: err.response?.data?.error || err.response?.data?.message || 'Fehler bei der Firmensuche.',
        status: 'error', updatedAt: Date.now(),
      })
    },
  })

  const updateTask = useMutation({
    mutationFn: ({ id, ...data }: { id: string; status?: string; outcome?: string; outcomeNote?: string }) =>
      apiClient.patch(`/acquisition/tasks/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
    },
  })

  const updateProspect = useMutation({
    mutationFn: ({ id, ...data }: { id: string; status?: string }) =>
      apiClient.patch(`/acquisition/prospects/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
    },
  })

  const rejectProspect = useMutation({
    mutationFn: ({ id, ...data }: { id: string; reason: string; reasonCategory: string; isPermanent?: boolean; recheckAfterDays?: number }) =>
      apiClient.post(`/acquisition/prospects/${id}/reject`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
      setRejectingProspect(null)
    },
  })

  const [rejectingProspect, setRejectingProspect] = useState<Prospect | null>(null)

  // "Heutige Aufgaben": nur Tasks, deren Faelligkeit bereits erreicht ist.
  // Zukuenftig faellige Tasks bleiben pending in der DB, werden aber erst am
  // Stichtag in der Liste sichtbar (End-of-Day-Grenze, damit Tasks mit
  // dueDate=heute 23:59 den ganzen Tag ueber gelten).
  const endOfToday = new Date()
  endOfToday.setHours(23, 59, 59, 999)
  const todayTasks = (tasks.data ?? []).filter((t) => {
    if (t.status !== 'pending' && t.status !== 'in_progress') return false
    return new Date(t.dueDate).getTime() <= endOfToday.getTime()
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <UserPlus className="w-7 h-7 text-brand-500" />
            Neukundengewinnung
            {isBeta && <BetaBadge size="md" />}
          </span>
        }
        description="Automatische Akquise mit handgeschriebenen Briefen"
        actions={
          <a
            href="/vertrieb"
            className="inline-flex items-center gap-1.5 text-small text-brand-600 hover:text-brand-700 font-medium whitespace-nowrap"
            title="Pipeline-Ansicht mit allen Phasen (Recherche bis Gewonnen)"
          >
            <TrendingUp className="w-4 h-4" />
            Pipeline im Vertriebsboard öffnen
            <ArrowRightCircle className="w-4 h-4" />
          </a>
        }
      />

      {/* KPIs */}
      {kpis.data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Mail}
            label="Briefe versandt"
            value={kpis.data.lettersSent.toString()}
          />
          <KpiCard
            icon={TrendingUp}
            label="Response-Rate"
            value={`${kpis.data.responseRate}%`}
            color={kpis.data.responseRate > 10 ? 'text-green-600' : undefined}
          />
          <KpiCard
            icon={Calendar}
            label="Termine"
            value={kpis.data.meetings.toString()}
          />
          <KpiCard
            icon={Target}
            label="Deals gewonnen"
            value={kpis.data.dealsWon.toString()}
            color="text-green-600"
          />
        </div>
      )}

      {/* Pipeline-Tick Banner (Fehler / aktive Aenderungen sichtbar machen) */}
      {tickError && (
        <Card className="border-red-200 bg-red-50">
          <Card.Body className="px-4 py-2.5 flex items-start gap-2 text-caption text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Pipeline-Tick fehlgeschlagen</p>
              <p className="opacity-80">{tickError}</p>
            </div>
            <IconButton
              aria-label="Schließen"
              variant="ghost"
              size="xs"
              icon={<X className="w-3.5 h-3.5" />}
              onClick={() => setTickError(null)}
              className="text-red-400 hover:text-red-600"
            />
          </Card.Body>
        </Card>
      )}
      {tickSummary && !tickSummary.skipped && (tickSummary.affectedProspects + tickSummary.affectedTasks) > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <Card.Body className="px-4 py-2.5 flex items-start gap-2 text-caption text-blue-700">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Pipeline aktualisiert</p>
              <p className="opacity-80">
                {tickSummary.affectedProspects} Zielkunden auf neue Phase gesetzt ·
                {' '}{tickSummary.affectedTasks} neue Aufgaben angelegt.
              </p>
            </div>
            <IconButton
              aria-label="Schließen"
              variant="ghost"
              size="xs"
              icon={<X className="w-3.5 h-3.5" />}
              onClick={() => setTickSummary(null)}
              className="text-blue-400 hover:text-blue-600"
            />
          </Card.Body>
        </Card>
      )}
      {/* Force-Tick Button: gibt dem User Kontrolle, falls Aufgaben fehlen
          (z. B. nach Deploy, bei dem handleLetterSent fuer alte Briefe nicht
          mehr lief). Umgeht das 1h-Rate-Limit und erzwingt einen Backfill. */}
      {tickSummary?.skipped && (
        <div className="flex items-center justify-end gap-2 text-caption text-gray-400">
          <span>Pipeline-Tick zuletzt vor &lt; 1h ausgeführt.</span>
          <Button
            variant="secondary"
            size="xs"
            onClick={() => runPipelineTick(true)}
            loading={tickRunning}
            leadingIcon={!tickRunning ? <RefreshCw className="w-3 h-3" /> : undefined}
          >
            Jetzt nachholen
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="daily"><Calendar className="w-4 h-4" /> Tagesansicht</TabsTrigger>
          <TabsTrigger value="tasks"><CheckCircle2 className="w-4 h-4" /> Aufgaben</TabsTrigger>
          <TabsTrigger value="prospects"><Building2 className="w-4 h-4" /> Alle Zielkunden</TabsTrigger>
          <TabsTrigger value="campaigns"><BarChart3 className="w-4 h-4" /> Kampagnen</TabsTrigger>
          <TabsTrigger value="settings"><SettingsIcon className="w-4 h-4" /> Einstellungen</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ─── DAILY VIEW ─── */}
      {tab === 'daily' && (
        <div className="space-y-4">
          {/* Campaign filter for daily view */}
          <FilterBar>
            <Target className="w-4 h-4 text-gray-400" />
            <label className="text-small font-medium text-gray-600 whitespace-nowrap">Kampagne:</label>
            <select
              value={dailyCampaignFilter}
              onChange={(e) => setDailyCampaignFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-small bg-white text-gray-700 min-w-[200px]"
            >
              <option value="">Alle Zielkunden</option>
              {(campaignsList.data || []).filter((c) => c.status === 'active').map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {dailyCampaignFilter && (
              <IconButton
                aria-label="Filter zurücksetzen"
                variant="ghost"
                size="xs"
                icon={<X className="w-4 h-4" />}
                onClick={() => setDailyCampaignFilter('')}
              />
            )}
          </FilterBar>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Letter Review Card (Tinder-style) */}
          <div className="lg:col-span-2">
            {dailyProspects.isLoading && (
              <Card>
                <Card.Body>
                  <LoadingState label="Lade Tagesvorschlaege..." size="lg" />
                </Card.Body>
              </Card>
            )}

            {dailyProspects.isError && (
              <Card>
                <Card.Body>
                  <ErrorState
                    title="Fehler beim Laden der Tagesvorschlaege"
                    description={(dailyProspects.error as Error)?.message || 'Unbekannter Fehler'}
                    onRetry={() => dailyProspects.refetch()}
                    size="lg"
                  />
                </Card.Body>
              </Card>
            )}

            {dailyProspects.data && dailyProspects.data.length === 0 && (
              <Card>
                <Card.Body>
                  <EmptyState
                    icon={<Search />}
                    title="Noch keine Zielkunden gefunden"
                    description="Konfiguriere zuerst Geo-Zonen unter Einstellungen > Module > Neukundengewinnung, dann starte die Firmensuche."
                    size="lg"
                    action={
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => triggerDiscovery.mutate(undefined)}
                        loading={triggerDiscovery.isPending}
                        leadingIcon={!triggerDiscovery.isPending ? <Search className="w-4 h-4" /> : undefined}
                      >
                        {triggerDiscovery.isPending ? 'Suche laeuft...' : 'Jetzt Firmensuche starten'}
                      </Button>
                    }
                  />
                </Card.Body>
              </Card>
            )}

            {dailyProspects.data && dailyProspects.data.length > 0 && (
              <LetterReviewCard
                prospects={dailyProspects.data}
                onStatusChange={(id, status) => updateProspect.mutate({ id, status })}
                onReject={(prospect) => setRejectingProspect(prospect)}
                onEditProspect={(prospect) => setDetailProspect(prospect)}
              />
            )}
          </div>

          {/* Tasks Column */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Phone className="w-5 h-5 text-brand-500" />
                Heutige Aufgaben
                <span className="text-sm font-normal text-gray-500">
                  ({todayTasks.length})
                </span>
              </h2>
              <div className="flex items-center gap-2">
                {todayTasks.length > 0 && (
                  <SyncAllCalendarButton onSynced={() => queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] })} />
                )}
                <ResetAndSyncButton onSynced={() => queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] })} />
                <CalendarDebugButton />
              </div>
            </div>

            {/* Debug: zeigt warum ggf. Aufgaben fehlen — Server-Response vs.
                Client-Filter. Hilft zu erkennen, ob ueberfaellige Tasks
                serverseitig (Limit/Filter) oder clientseitig rausfallen. */}
            <details className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1">
              <summary className="cursor-pointer select-none">
                Debug: Aufgaben-Pipeline ({tasks.data?.length ?? 0} vom Server, {todayTasks.length} angezeigt)
              </summary>
              <div className="mt-2 space-y-1 font-mono">
                <div>URL: <code>GET /acquisition/tasks?dueBy=today</code></div>
                <div>Status: {tasks.isLoading ? 'lädt…' : tasks.isError ? `Fehler: ${String((tasks.error as Error)?.message ?? tasks.error)}` : 'OK'}</div>
                <div>Heute (endOfToday): {endOfToday.toISOString()}</div>
                <div>Server-Antwort: {tasks.data?.length ?? 0} Tasks · Client-gefiltert (status + dueDate ≤ heute): {todayTasks.length}</div>
                {(tasks.data ?? []).length > 0 && (
                  <div className="mt-1">
                    <div className="font-semibold">Roh-Tasks vom Server:</div>
                    <ul className="ml-3 list-disc">
                      {(tasks.data ?? []).map((t) => {
                        const due = new Date(t.dueDate)
                        const passesStatus = t.status === 'pending' || t.status === 'in_progress'
                        const passesDate = due.getTime() <= endOfToday.getTime()
                        const visible = passesStatus && passesDate
                        return (
                          <li key={t.id} className={visible ? '' : 'text-red-500'}>
                            {t.prospect?.companyName ?? '(kein Prospect)'} · status={t.status} · due={due.toLocaleDateString('de-DE')} · letterId={t.letterId ?? '–'}
                            {!visible && ' ← ausgeblendet'}
                            {!passesStatus && ' (Status)'}
                            {!passesDate && ' (Fälligkeit in Zukunft)'}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
                <DebugTaskAudit />
              </div>
            </details>

            {todayTasks.length === 0 && (
              <Card>
                <Card.Body>
                  <EmptyState
                    icon={<CheckCircle2 className="text-green-300" />}
                    title="Keine offenen Aufgaben"
                    size="sm"
                  />
                </Card.Body>
              </Card>
            )}

            {todayTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onComplete={(outcomeNote, outcomeCode) =>
                  updateTask.mutate({
                    id: task.id,
                    status: 'completed',
                    outcome: outcomeCode ?? 'other',
                    outcomeNote,
                  })
                }
                onSkip={() => updateTask.mutate({ id: task.id, status: 'skipped' })}
                onSyncCalendar={() => queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] })}
                onOpenProspect={(prospectId) => {
                  apiClient.get(`/acquisition/prospects/${prospectId}`)
                    .then(({ data }) => setDetailProspect(data))
                    .catch(() => {
                      if (task.prospect) {
                        setDetailProspect({
                          ...task.prospect,
                          industry: null, employeeCount: null, region: null, street: null, country: null,
                          referenceCustomerId: null, memberships: null,
                          companyProfile: null, lastContactedAt: null, nextFollowUpAt: null,
                          createdAt: new Date().toISOString(),
                        } as Prospect)
                      }
                    })
                }}
              />
            ))}

            {/* Kundenschreiber Orders */}
            <KundenschreiberOrdersList />
          </div>
        </div>
        </div>
      )}

      {/* ─── TASKS TAB ─── */}
      {tab === 'tasks' && (
        <div className="space-y-4">
          <FilterBar>
            <CheckCircle2 className="w-4 h-4 text-gray-400" />
            <label className="text-small font-medium text-gray-600 whitespace-nowrap">Filter:</label>
            <FilterChip
              active={tasksTabFilter === 'open'}
              onClick={() => setTasksTabFilter('open')}
            >
              Alle offenen
            </FilterChip>
            <FilterChip
              active={tasksTabFilter === 'due'}
              onClick={() => setTasksTabFilter('due')}
            >
              Alle fälligen
            </FilterChip>
            <FilterChip
              active={tasksTabFilter === 'completed'}
              onClick={() => setTasksTabFilter('completed')}
            >
              Alle abgeschlossenen
            </FilterChip>
            <FilterChip
              active={tasksTabFilter === 'all'}
              onClick={() => setTasksTabFilter('all')}
            >
              Alle
            </FilterChip>
            <span className="ml-auto text-small text-gray-500">
              {allTasks.data?.length ?? 0} Aufgaben
            </span>
          </FilterBar>

          {allTasks.isLoading && (
            <Card><Card.Body><LoadingState label="Lade Aufgaben..." /></Card.Body></Card>
          )}

          {allTasks.isError && (
            <Card>
              <Card.Body>
                <ErrorState
                  title="Fehler beim Laden der Aufgaben"
                  description={(allTasks.error as Error)?.message || 'Unbekannter Fehler'}
                  onRetry={() => allTasks.refetch()}
                />
              </Card.Body>
            </Card>
          )}

          {allTasks.data && allTasks.data.length === 0 && (
            <Card>
              <Card.Body>
                <EmptyState
                  icon={<CheckCircle2 className="text-green-300" />}
                  title="Keine Aufgaben in dieser Auswahl"
                  size="sm"
                />
              </Card.Body>
            </Card>
          )}

          {allTasks.data && allTasks.data.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {allTasks.data.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onComplete={(outcomeNote, outcomeCode) =>
                    updateTask.mutate({
                      id: task.id,
                      status: 'completed',
                      outcome: outcomeCode ?? 'other',
                      outcomeNote,
                    })
                  }
                  onSkip={() => updateTask.mutate({ id: task.id, status: 'skipped' })}
                  onReopen={() => updateTask.mutate({ id: task.id, status: 'pending' })}
                  onSyncCalendar={() => queryClient.invalidateQueries({ queryKey: ['acquisition', 'tasks'] })}
                  onOpenProspect={(prospectId) => {
                    apiClient.get(`/acquisition/prospects/${prospectId}`)
                      .then(({ data }) => setDetailProspect(data))
                      .catch(() => {
                        if (task.prospect) {
                          setDetailProspect({
                            ...task.prospect,
                            industry: null, employeeCount: null, region: null, street: null, country: null,
                            contactLinkedinUrl: null, referenceCustomerId: null, memberships: null,
                            companyProfile: null, lastContactedAt: null, nextFollowUpAt: null,
                            createdAt: new Date().toISOString(),
                          } as Prospect)
                        }
                      })
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── PROSPECTS LIST ─── */}
      {tab === 'prospects' && (
        <Card className="rounded-lg">
          {allProspects.isLoading && (
            <LoadingState label="Lade Zielkunden..." />
          )}
          {allProspects.isError && (
            <ErrorState
              title="Fehler beim Laden der Zielkunden"
              description={(allProspects.error as Error)?.message || 'Unbekannter Fehler'}
              onRetry={() => allProspects.refetch()}
            />
          )}
          {allProspects.data && allProspects.data.prospects.length === 0 && !statusFilter && !debouncedSearch && !campaignFilter && !campaignTypeFilter && (
            <EmptyState
              icon={<Building2 />}
              title="Noch keine Zielkunden"
              description="Die automatische Suche startet sobald Geo-Zonen konfiguriert sind."
            />
          )}
          {allProspects.data && (allProspects.data.prospects.length > 0 || statusFilter || debouncedSearch || campaignFilter || campaignTypeFilter) && (
            <>
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm text-gray-600">
                    {allProspects.data.total} Zielkunden gesamt{(searchFilter || campaignFilter || campaignTypeFilter) && ' (gefiltert)'}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => triggerDiscovery.mutate(undefined)}
                      loading={triggerDiscovery.isPending}
                      leadingIcon={!triggerDiscovery.isPending ? <Search className="w-3 h-3" /> : undefined}
                    >
                      {triggerDiscovery.isPending ? 'Suche läuft...' : 'Firmensuche'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => triggerDiscovery.mutate({ forceRefresh: true })}
                      disabled={triggerDiscovery.isPending}
                      title="Ignoriert den 7-Tage-Cooldown und den Cache — sucht komplett neu bei Google Places (verursacht API-Kosten)"
                      leadingIcon={<RefreshCw className="w-3 h-3" />}
                    >
                      Neue Suche erzwingen
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowQuickCapture(true)}
                      leadingIcon={<Plus className="w-3 h-3" />}
                    >
                      Firma
                    </Button>
                    <button
                      onClick={() => {
                        upsertTask({ id: 'dedup', label: 'Duplikate entfernen', detail: 'Duplikate werden gesucht...', status: 'running', updatedAt: Date.now() })
                        apiClient.post('/acquisition/prospects/deduplicate').then(({ data }) => {
                          upsertTask({ id: 'dedup', label: 'Duplikate entfernen', detail: `${data.removed} Duplikate entfernt`, status: 'done', updatedAt: Date.now() })
                          queryClient.invalidateQueries({ queryKey: ['acquisition'] })
                        }).catch(() => upsertTask({ id: 'dedup', label: 'Duplikate entfernen', detail: 'Fehler beim Entfernen', status: 'error', updatedAt: Date.now() }))
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-50"
                    >Duplikate entfernen</button>
                    <button
                      onClick={() => {
                        upsertTask({ id: 'addresses', label: 'Adressen ergänzen', detail: 'Adressen werden gesucht...', status: 'running', updatedAt: Date.now() })
                        apiClient.post('/acquisition/prospects/enrich-all-addresses').then(({ data }) => {
                          upsertTask({ id: 'addresses', label: 'Adressen ergänzen', detail: data.message, status: 'done', updatedAt: Date.now() })
                          queryClient.invalidateQueries({ queryKey: ['acquisition'] })
                        }).catch((err: { response?: { data?: { error?: string } } }) => {
                          upsertTask({ id: 'addresses', label: 'Adressen ergänzen', detail: err.response?.data?.error || 'Fehler', status: 'error', updatedAt: Date.now() })
                        })
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 text-amber-600 text-xs font-medium rounded-md hover:bg-amber-50"
                    >Adressen ergänzen</button>
                    <button
                      onClick={(e) => {
                        const force = e.shiftKey
                        const detailMsg = force ? 'Alle Kontakte werden neu gesucht (Cache geleert)...' : 'Kontakte werden gesucht (Impressum + Apollo)...'
                        upsertTask({ id: 'contacts', label: 'Kontakte finden', detail: detailMsg, status: 'running', updatedAt: Date.now() })
                        apiClient.post('/acquisition/prospects/enrich-contacts', { force }).then(() => {
                          const poll = setInterval(() => {
                            apiClient.get('/acquisition/prospects/enrich-status').then(({ data }) => {
                              if (data.message) {
                                const isDone = data.message.startsWith('Fertig:')
                                upsertTask({ id: 'contacts', label: 'Kontakte finden', detail: data.message, status: isDone ? 'done' : 'running', updatedAt: Date.now() })
                                queryClient.invalidateQueries({ queryKey: ['acquisition'] })
                                if (isDone) clearInterval(poll)
                              }
                            }).catch(() => {})
                          }, 3000)
                          setTimeout(() => clearInterval(poll), 600000)
                        }).catch((err: { response?: { data?: { error?: string } } }) => {
                          upsertTask({ id: 'contacts', label: 'Kontakte finden', detail: err.response?.data?.error || 'Fehler', status: 'error', updatedAt: Date.now() })
                        })
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-600 text-xs font-medium rounded-md hover:bg-blue-50"
                      title="Shift+Klick = alle neu suchen"
                    ><User className="w-3 h-3" /> Kontakte finden</button>
                    <button
                      onClick={() => {
                        upsertTask({ id: 'profiling', label: 'Website-Profiling', detail: 'Firmenprofile werden erstellt (KI-Analyse)...', status: 'running', updatedAt: Date.now() })
                        const force = (window.event as MouseEvent)?.shiftKey
                        apiClient.post('/acquisition/prospects/profile-bulk', { force }).then(() => {
                          const pollProfile = setInterval(async () => {
                            try {
                              const { data } = await apiClient.get('/acquisition/prospects/profile-status')
                              if (data.message) {
                                const isDone = data.message.startsWith('Profiling fertig')
                                upsertTask({ id: 'profiling', label: 'Website-Profiling', detail: data.message, status: isDone ? 'done' : 'running', updatedAt: Date.now() })
                                if (isDone) { clearInterval(pollProfile); queryClient.invalidateQueries({ queryKey: ['acquisition'] }) }
                              }
                            } catch { clearInterval(pollProfile) }
                          }, 3000)
                        }).catch((err: { response?: { data?: { error?: string } } }) => {
                          upsertTask({ id: 'profiling', label: 'Website-Profiling', detail: err.response?.data?.error || 'Fehler', status: 'error', updatedAt: Date.now() })
                        })
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-purple-300 text-purple-700 text-xs font-medium rounded-md hover:bg-purple-50 transition-colors"
                      title="Shift+Klick = alle Profile neu erstellen"
                    ><Sparkles className="w-3 h-3" /> Profile erstellen</button>
                    <button
                      onClick={() => {
                        upsertTask({ id: 'scores', label: 'Scores berechnen', detail: 'Scores werden berechnet...', status: 'running', updatedAt: Date.now() })
                        apiClient.post('/acquisition/scoring-config/recalculate').then(({ data }) => {
                          upsertTask({ id: 'scores', label: 'Scores berechnen', detail: `${data.updated} neu bewertet`, status: 'done', updatedAt: Date.now() })
                          queryClient.invalidateQueries({ queryKey: ['acquisition'] })
                        }).catch((err: { response?: { data?: { error?: string } } }) => {
                          upsertTask({ id: 'scores', label: 'Scores berechnen', detail: err.response?.data?.error || 'Fehler', status: 'error', updatedAt: Date.now() })
                        })
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Scores
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      placeholder="Suche nach Firma, Branche, Ort, PLZ, Ansprechpartner..."
                      value={searchFilter}
                      leadingIcon={<Search />}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      trailingIcon={searchFilter ? (
                        <button
                          type="button"
                          onClick={() => setSearchFilter('')}
                          className="text-gray-400 hover:text-gray-600"
                          title="Suche zurücksetzen"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      ) : undefined}
                    />
                  </div>
                  <select
                    value={campaignFilter}
                    onChange={(e) => { setCampaignFilter(e.target.value); setProspectsPage(0) }}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-700"
                  >
                    <option value="">Alle Kampagnen</option>
                    {(campaignsList.data || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    value={campaignTypeFilter}
                    onChange={(e) => { setCampaignTypeFilter(e.target.value); setProspectsPage(0) }}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-700"
                  >
                    <option value="">Alle Kampagnen-Typen</option>
                    <option value="standard">Standard</option>
                    <option value="referenz">Referenz</option>
                    <option value="stellenanzeige">Stellenanzeige</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setProspectsPage(0) }}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-700"
                  >
                    <option value="">Alle Status</option>
                    {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <select
                    value={sourceChannelFilter}
                    onChange={(e) => { setSourceChannelFilter(e.target.value); setProspectsPage(0) }}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-700"
                  >
                    <option value="">Alle Quellen</option>
                    <option value="job_posting">Stellenanzeige</option>
                    <option value="quick-capture-google">Google Maps</option>
                    <option value="quick-capture-website">Website</option>
                    <option value="quick-capture-image">Bild/OCR</option>
                    <option value="discovery">Discovery</option>
                  </select>
                </div>
              </div>
              {debouncedSearch && (
                <p className="px-4 py-2 text-sm text-gray-600 bg-gray-50 border-b border-gray-200">
                  <span className="font-semibold">{allProspects.data.total}</span> {allProspects.data.total === 1 ? 'Treffer' : 'Treffer'} gefunden
                </p>
              )}
              <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Score</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Firma</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Ansprechpartner</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">PLZ / Ort</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Branche</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Kampagne</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Letzter Kontakt</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Auftrag</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500">Brief</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Quelle</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {allProspects.data.prospects.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-sm text-gray-500">
                        {debouncedSearch
                          ? <>Keine Treffer für &quot;{debouncedSearch}&quot;</>
                          : statusFilter
                            ? <>Keine Zielkunden mit Status &quot;{STATUS_LABELS[statusFilter]?.label ?? statusFilter}&quot;</>
                            : 'Keine Zielkunden vorhanden'}
                      </td>
                    </tr>
                  )}
                  {allProspects.data.prospects.map((p) => {
                    const unified = resolveUnifiedStatus(p)
                    const contactCount = p.contactCounts?.total ?? 0
                    const isExpanded = expandedProspectId === p.id
                    const latestCampaign = p.latestCampaign
                    const campaignTypeInfo = latestCampaign?.campaignType
                      ? (CAMPAIGN_TYPE_LABELS_OVERVIEW[latestCampaign.campaignType] || { label: latestCampaign.campaignType, color: 'bg-gray-50 text-gray-600' })
                      : null
                    const latestContactDate = p.contactHistory && p.contactHistory.length > 0 ? p.contactHistory[0]!.date : null
                    return (
                      <tr
                        key={p.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onDoubleClick={() => setDetailProspect(p)}
                        title="Doppelklick für Detailansicht"
                      >
                        {/* Score */}
                        <td className="px-4 py-3 align-top">
                          <span className={cn('text-xs font-bold px-2 py-1 rounded', getScoreColor(p.icpScore))}>
                            {p.icpScore}
                          </span>
                        </td>
                        {/* Firma + optional Expand-Badge + optional Kontakt-Historie */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-center gap-2">
                            <CompanyLogo website={p.website} companyName={p.companyName} size="sm" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-medium text-gray-900 truncate max-w-[220px]">{a.name(p.companyName)}</p>
                                {contactCount > 1 && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setExpandedProspectId(isExpanded ? null : p.id) }}
                                    className={cn(
                                      'inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full transition-colors',
                                      isExpanded ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                                    )}
                                    title={`${contactCount} Kontaktaufnahmen – klicken zum Aufklappen`}
                                  >
                                    <span>+{contactCount - 1}</span>
                                    <ChevronRight className={cn('w-3 h-3 transition-transform', isExpanded && 'rotate-90')} />
                                  </button>
                                )}
                              </div>
                              {latestCampaign && (
                                <span className="text-xs text-gray-400">{latestCampaign.name}</span>
                              )}
                            </div>
                          </div>

                          {isExpanded && p.contactHistory && p.contactHistory.length > 0 && (
                            <div className="mt-2 ml-10 border-l-2 border-brand-200 pl-3 space-y-1.5">
                              {p.contactHistory.map((contact, idx) => {
                                const ctInfo = CONTACT_TYPE_LABELS[contact.type] || CONTACT_TYPE_LABELS.other!
                                const CtIcon = ctInfo.icon
                                return (
                                  <div key={idx} className="flex items-start gap-2 text-xs">
                                    <CtIcon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">{ctInfo.label}</span>
                                        <span className="text-gray-400">{formatDate(contact.date)}</span>
                                        {contact.outcome && (
                                          <span className="text-gray-400">– {contact.outcome}</span>
                                        )}
                                      </div>
                                      {contact.title && contact.type !== 'letter' && (
                                        <p className="text-gray-500 truncate">{contact.title}</p>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </td>
                        {/* Ansprechpartner */}
                        <td className="px-4 py-3 align-top">
                          {p.contactFirstName || p.contactLastName ? (
                            <div className="flex items-center gap-2">
                              <PersonAvatar firstName={p.contactFirstName} lastName={p.contactLastName} />
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-sm text-gray-700">
                                    {a.name(`${p.contactFirstName ?? ''} ${p.contactLastName ?? ''}`.trim())}
                                  </p>
                                  {p.contactLinkedinUrl && (
                                    <a href={p.contactLinkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:text-[#004182]" title="LinkedIn-Profil" onClick={(e) => e.stopPropagation()}>
                                      <Linkedin className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                </div>
                                {p.contactRole && (
                                  <p className="text-xs text-gray-500">{p.contactRole}</p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Nicht identifiziert</span>
                          )}
                        </td>
                        {/* PLZ / Ort */}
                        <td className="px-4 py-3 align-top text-sm text-gray-700 whitespace-nowrap">
                          {[p.zipCode, p.city].filter(Boolean).join(' ') || <span className="text-xs text-red-400">fehlt</span>}
                        </td>
                        {/* Branche */}
                        <td className="px-4 py-3 align-top text-sm text-gray-700">{p.industry ?? '–'}</td>
                        {/* Kampagne */}
                        <td className="px-4 py-3 align-top">
                          {campaignTypeInfo ? (
                            <Badge
                              variant={
                                latestCampaign?.campaignType === 'referenz' ? 'success'
                                : latestCampaign?.campaignType === 'stellenanzeige' ? 'warning'
                                : 'info'
                              }
                            >
                              {campaignTypeInfo.label}
                            </Badge>
                          ) : (
                            <span className="text-caption text-gray-300">–</span>
                          )}
                        </td>
                        {/* Letzter Kontakt */}
                        <td className="px-4 py-3 align-top text-xs text-gray-600 whitespace-nowrap">
                          {latestContactDate ? formatDate(latestContactDate) : <span className="text-gray-300">–</span>}
                        </td>
                        {/* Status (unified) */}
                        <td className="px-4 py-3 align-top text-center">
                          <div className="inline-flex flex-col items-center gap-1">
                            <StatusBadge status={p.status} label={unified.main.label} />
                            {unified.secondary && (
                              <Badge
                                variant={
                                  unified.secondary.label === 'Versandfehler' ? 'danger'
                                  : unified.secondary.label === 'In Druck' ? 'warning'
                                  : 'warning'
                                }
                                size="sm"
                              >
                                {unified.secondary.label}
                              </Badge>
                            )}
                          </div>
                        </td>
                        {/* Auftrag (Kundenschreiber) */}
                        <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                          {p.latestLetter ? (
                            <LetterOrderTracker
                              letter={{
                                id: p.latestLetter.id,
                                kundenschreiberOrderId: p.latestLetter.kundenschreiberOrderId,
                                status: p.latestLetter.status,
                                versandDatum: p.latestLetter.versandDatum,
                                sentAt: p.latestLetter.sentAt,
                              }}
                              prospectId={p.id}
                              compact
                            />
                          ) : (
                            <span className="text-xs text-gray-300">–</span>
                          )}
                        </td>
                        {/* Brief-Preview */}
                        <td className="px-4 py-3 align-top text-center">
                          {p.latestLetter ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setPreviewLetterProspect(previewLetterProspect?.id === p.id ? null : p) }}
                              className="p-1 rounded hover:bg-gray-100 transition-colors"
                              title="Brieftext anzeigen"
                            >
                              <Eye className={cn('w-4 h-4 transition-colors', previewLetterProspect?.id === p.id ? 'text-brand-600' : 'text-gray-400 hover:text-brand-600')} />
                            </button>
                          ) : (
                            <Eye className="w-4 h-4 text-gray-200 mx-auto" />
                          )}
                        </td>
                        {/* Quelle */}
                        <td className="px-4 py-3 align-top">
                          <SourceBadge source={p.source} sourceChannel={p.sourceChannel} website={p.website} />
                        </td>
                        {/* Aktionen */}
                        <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                          <ProspectRowActions prospect={p} onUpdate={() => queryClient.invalidateQueries({ queryKey: ['acquisition'] })} onEdit={(pp) => setDetailProspect(pp)} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              </div>
              {/* Pagination */}
              {allProspects.data.total > PROSPECTS_PER_PAGE && (
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                  <p className="text-caption text-gray-500">
                    Seite {prospectsPage + 1} von {Math.ceil(allProspects.data.total / PROSPECTS_PER_PAGE)} ({allProspects.data.total} gesamt)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setProspectsPage((p) => Math.max(0, p - 1))}
                      disabled={prospectsPage === 0}
                    >
                      Zurueck
                    </Button>
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setProspectsPage((p) => p + 1)}
                      disabled={(prospectsPage + 1) * PROSPECTS_PER_PAGE >= allProspects.data.total}
                    >
                      Weiter
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* ─── CAMPAIGNS ─── */}
      {tab === 'campaigns' && (
        <CampaignsTab />
      )}

      {/* ─── SETTINGS TAB ─── */}
      {tab === 'settings' && (
        <InlineSettingsTab />
      )}

      {/* ─── QUICK CAPTURE MODAL ─── */}
      {showQuickCapture && (
        <QuickCaptureModal
          onClose={() => setShowQuickCapture(false)}
          onSave={() => {
            setShowQuickCapture(false)
            queryClient.invalidateQueries({ queryKey: ['acquisition'] })
          }}
          onManualEntry={() => {
            setShowQuickCapture(false)
            setShowAddProspect(true)
          }}
        />
      )}

      {/* ─── ADD PROSPECT MODAL (manual) ─── */}
      {showAddProspect && (
        <AddProspectModal
          onClose={() => setShowAddProspect(false)}
          onSave={() => {
            setShowAddProspect(false)
            queryClient.invalidateQueries({ queryKey: ['acquisition'] })
          }}
        />
      )}

      {/* ─── REJECT PROSPECT MODAL ─── */}
      {rejectingProspect && (
        <RejectProspectModal
          prospect={rejectingProspect}
          onClose={() => setRejectingProspect(null)}
          onReject={(data) => rejectProspect.mutate({ id: rejectingProspect.id, ...data })}
          isPending={rejectProspect.isPending}
        />
      )}
      {/* ─── PROSPECT DETAIL MODAL (Doppelklick auf Zeile / Stift-Button) ─── */}
      {detailProspect && (
        <ProspectDetailModal
          prospect={
            // Keep the modal in sync with the latest data in the table
            (allProspects.data?.prospects.find((p) => p.id === detailProspect.id)) ?? detailProspect
          }
          onClose={() => setDetailProspect(null)}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['acquisition'] })}
        />
      )}

      {/* ─── LETTER PREVIEW OVERLAY (Eye-Button in der Zielkunden-Tabelle) ─── */}
      {previewLetterProspect?.latestLetter && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setPreviewLetterProspect(null) }}
          size="md"
          title={
            <span className="flex items-center gap-3">
              <span className="text-small font-medium text-gray-700">
                {LETTER_TYPE_LABELS[previewLetterProspect.latestLetter.type] || previewLetterProspect.latestLetter.type}
              </span>
              <span className="text-caption text-gray-400">
                {previewLetterProspect.latestLetter.versandDatum
                  ? formatDate(previewLetterProspect.latestLetter.versandDatum)
                  : previewLetterProspect.latestLetter.sentAt
                    ? formatDate(previewLetterProspect.latestLetter.sentAt)
                    : formatDate(previewLetterProspect.latestLetter.createdAt)}
              </span>
            </span>
          }
        >
          <div className="-mx-5 -my-5">
            <div className="px-5 py-2 bg-gray-50 border-b border-gray-100 text-caption text-gray-500">
              An: <span className="font-medium text-gray-700">{a.name(previewLetterProspect.companyName)}</span>
              {(previewLetterProspect.contactFirstName || previewLetterProspect.contactLastName) && (
                <>
                  {' – '}
                  {a.name([previewLetterProspect.contactFirstName, previewLetterProspect.contactLastName].filter(Boolean).join(' '))}
                </>
              )}
            </div>
            <div className="px-5 py-4 text-small text-gray-700 whitespace-pre-wrap leading-relaxed">
              {a.enabled
                ? '(Brieftext im Demo-Modus ausgeblendet – enthält echten Kundendialog)'
                : (previewLetterProspect.latestLetter.personalizedBody || '(Kein Text)')}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, color }: {
  icon: typeof Mail
  label: string
  value: string
  color?: string
}) {
  return (
    <Card>
      <Card.Body>
        <p className="text-caption text-gray-500 flex items-center gap-1">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </p>
        <p className={cn('text-display font-bold mt-1 leading-none tracking-tight', color ?? 'text-gray-900')}>{value}</p>
      </Card.Body>
    </Card>
  )
}

function LetterReviewCard({ prospects, onStatusChange, onReject, referenceCustomerId, onEditProspect }: {
  prospects: Prospect[]
  onStatusChange: (id: string, status: string) => void
  onReject: (prospect: Prospect) => void
  referenceCustomerId?: string
  onEditProspect?: (prospect: Prospect) => void
}) {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [decided, setDecided] = useState<Record<string, 'sent' | 'rejected' | 'skipped'>>({})
  const [showScoring, setShowScoring] = useState(false)
  const [showProfile, setShowProfile] = useState(false)

  // Inline edit: PATCH /acquisition/prospects/:id
  const updateField = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/acquisition/prospects/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
    },
  })

  // Wenn die Queue schrumpft (z.B. nach dem Speichern einer Kundenschreiber-Order-ID),
  // kann currentIndex ausserhalb der neuen Laenge liegen. Clampen auf den letzten Index.
  useEffect(() => {
    if (prospects.length === 0) return
    if (currentIndex > prospects.length - 1) {
      setCurrentIndex(prospects.length - 1)
    }
  }, [prospects.length, currentIndex])

  const prospect = prospects[currentIndex]

  if (!prospect) return null

  const total = prospects.length
  const decidedCount = Object.keys(decided).length
  const hasContact = prospect.contactFirstName || prospect.contactLastName
  const contactName = hasContact
    ? a.name(`${prospect.contactSalutation ?? ''} ${prospect.contactFirstName ?? ''} ${prospect.contactLastName ?? ''}`.trim())
    : 'Ansprechpartner unbekannt'

  const numericProspectFields: Array<keyof Prospect> = ['employeeCount']
  async function saveProspectField(field: keyof Prospect, raw: string) {
    let finalValue: string | number | null
    if (numericProspectFields.includes(field)) {
      const num = Number(raw)
      finalValue = raw === '' || Number.isNaN(num) ? null : num
    } else {
      finalValue = raw === '' ? null : raw
    }
    await updateField.mutateAsync({ id: prospect.id, data: { [field]: finalValue } })
  }

  const scoringDetails = (prospect as unknown as Record<string, unknown>).scoringDetails as {
    dimensions?: Record<string, { score: number; weight: number; reason: string }>
  } | null

  const isDecided = decided[prospect.id]

  function goNext() {
    if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1)
  }
  function goPrev() {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1)
  }

  function handleSent() {
    onStatusChange(prospect.id, 'letter_sent')
    setDecided((prev) => ({ ...prev, [prospect.id]: 'sent' }))
    if (currentIndex < total - 1) setTimeout(() => setCurrentIndex(currentIndex + 1), 300)
  }

  function handleReject() {
    onReject(prospect)
    setDecided((prev) => ({ ...prev, [prospect.id]: 'rejected' }))
  }

  function handleSkip() {
    setDecided((prev) => ({ ...prev, [prospect.id]: 'skipped' }))
    if (currentIndex < total - 1) setTimeout(() => setCurrentIndex(currentIndex + 1), 150)
  }

  return (
    <div className="space-y-4">
      {/* Header with navigation */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-brand-500" />
          Heutige Briefe
        </h2>
        <div className="flex items-center gap-3">
          {/* Progress dots */}
          <div className="flex items-center gap-1">
            {prospects.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setCurrentIndex(i)}
                className={cn(
                  'w-2.5 h-2.5 rounded-full transition-all',
                  i === currentIndex ? 'w-6 bg-brand-500' : '',
                  i !== currentIndex && decided[p.id] === 'sent' ? 'bg-green-400' : '',
                  i !== currentIndex && decided[p.id] === 'rejected' ? 'bg-red-400' : '',
                  i !== currentIndex && decided[p.id] === 'skipped' ? 'bg-gray-300' : '',
                  i !== currentIndex && !decided[p.id] ? 'bg-gray-200' : '',
                )}
              />
            ))}
          </div>
          <span className="text-sm font-medium text-gray-500">
            {currentIndex + 1}/{total}
          </span>
          <span className="text-xs text-gray-400">
            ({decidedCount} bearbeitet)
          </span>
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Company header */}
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <CompanyLogo website={prospect.website} companyName={prospect.companyName} />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">
                      <EditableField
                        value={prospect.companyName}
                        onSave={(v) => saveProspectField('companyName', v)}
                        required
                        allowEmpty={false}
                        title="Doppelklick zum Bearbeiten · Firmenname"
                        inputClassName="text-lg font-bold text-gray-900 px-1 py-0 border border-brand-400 rounded bg-white outline-none focus:ring-1 focus:ring-brand-400"
                      />
                    </h3>
                    <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full', getScoreColor(prospect.icpScore))}>
                      {prospect.icpScore} Pt
                    </span>
                    {prospect.sourceChannel === 'job_posting' && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                        <Briefcase className="w-3 h-3" />
                        Stellenanzeige
                      </span>
                    )}
                    {isDecided && (
                      <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full',
                        isDecided === 'sent' ? 'bg-green-100 text-green-700' :
                        isDecided === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      )}>
                        {isDecided === 'sent' ? 'Abgeschickt' : isDecided === 'rejected' ? 'Abgelehnt' : 'Uebersprungen'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <PersonAvatar firstName={prospect.contactFirstName} lastName={prospect.contactLastName} size="sm" />
                      {hasContact ? (
                        <>
                          <EditableField
                            value={prospect.contactSalutation ?? ''}
                            onSave={(v) => saveProspectField('contactSalutation', v)}
                            placeholder="Anrede"
                            title="Doppelklick zum Bearbeiten · Anrede (Herr/Frau)"
                            widthCh={6}
                          />
                          <EditableField
                            value={prospect.contactFirstName ?? ''}
                            onSave={(v) => saveProspectField('contactFirstName', v)}
                            placeholder="Vorname"
                            title="Doppelklick zum Bearbeiten · Vorname"
                          />
                          <EditableField
                            value={prospect.contactLastName ?? ''}
                            onSave={(v) => saveProspectField('contactLastName', v)}
                            placeholder="Nachname"
                            title="Doppelklick zum Bearbeiten · Nachname"
                          />
                        </>
                      ) : (
                        <span className="text-gray-400">{contactName}</span>
                      )}
                      <span className="text-gray-400">
                        (<EditableField
                          value={prospect.contactRole ?? ''}
                          onSave={(v) => saveProspectField('contactRole', v)}
                          placeholder="Position"
                          title="Doppelklick zum Bearbeiten · Position"
                        />)
                      </span>
                      {prospect.contactLinkedinUrl && (
                        <a href={prospect.contactLinkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:text-[#004182]" title="LinkedIn-Profil">
                          <Linkedin className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      <EditableField
                        value={prospect.zipCode ?? ''}
                        onSave={(v) => saveProspectField('zipCode', v)}
                        placeholder="PLZ"
                        title="Doppelklick zum Bearbeiten · PLZ"
                        widthCh={6}
                      />
                      <EditableField
                        value={prospect.city ?? ''}
                        onSave={(v) => saveProspectField('city', v)}
                        placeholder="Stadt"
                        title="Doppelklick zum Bearbeiten · Stadt"
                      />
                    </span>
                    {prospect.industry && (
                      <span className="text-gray-400">{prospect.industry}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Edit button (opens full detail modal) */}
            {onEditProspect && (
              <button
                type="button"
                onClick={() => onEditProspect(prospect)}
                title="Kundendetails & vollständige Bearbeitung öffnen"
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-lg bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors flex-shrink-0"
              >
                <Pencil className="w-3.5 h-3.5" />
                Bearbeiten
              </button>
            )}
          </div>

          {/* Compact details row */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>
              <EditableField
                value={prospect.employeeCount ?? ''}
                onSave={(v) => saveProspectField('employeeCount', v)}
                placeholder="–"
                title="Doppelklick zum Bearbeiten · Mitarbeiteranzahl"
                type="number"
                widthCh={5}
              /> Mitarbeiter
            </span>
            <span className="text-amber-600 font-medium">
              Trigger: <EditableField
                value={prospect.triggerEvent ?? ''}
                onSave={(v) => saveProspectField('triggerEvent', v)}
                placeholder="–"
                title="Doppelklick zum Bearbeiten · Trigger-Event"
                className="text-amber-600 font-medium"
              />
            </span>
            <EditableField
              value={prospect.street ?? ''}
              onSave={(v) => saveProspectField('street', v)}
              placeholder="Straße"
              title="Doppelklick zum Bearbeiten · Straße"
            />
            <EditableField
              value={prospect.contactEmail ?? ''}
              onSave={(v) => saveProspectField('contactEmail', v)}
              placeholder="E-Mail"
              title="Doppelklick zum Bearbeiten · E-Mail"
              type="email"
            />
            <EditableField
              value={prospect.contactPhone ?? ''}
              onSave={(v) => saveProspectField('contactPhone', v)}
              placeholder="Telefon"
              title="Doppelklick zum Bearbeiten · Telefon"
              type="tel"
            />
            {parseMemberships(prospect.memberships).map((m, i) => <span key={i} className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{m}</span>)}
          </div>

          {/* Scoring */}
          {scoringDetails?.dimensions && (
            <div className="mt-2">
              <button
                onClick={() => setShowScoring(!showScoring)}
                className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1"
              >
                <Info className="w-3 h-3" />
                {showScoring ? 'Score ausblenden' : 'Score-Details'}
              </button>
              {showScoring && (
                <div className="mt-2 bg-white rounded-lg p-3 space-y-1.5 border border-gray-100">
                  {Object.entries(scoringDetails.dimensions).map(([key, dim]) => {
                    const labels: Record<string, string> = {
                      industryFit: 'Branche', companySize: 'Groesse', regionMatch: 'Region',
                      decisionMaker: 'Entscheider', triggerEvents: 'Trigger', contactHistory: 'Historie',
                    }
                    const barColor = dim.score >= 70 ? 'bg-green-400' : dim.score >= 40 ? 'bg-amber-400' : 'bg-red-400'
                    return (
                      <div key={key} className="text-xs flex items-center gap-2">
                        <span className="text-gray-500 w-20">{labels[key] ?? key}</span>
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className={cn('h-full rounded-full', barColor)} style={{ width: `${dim.score}%` }} />
                        </div>
                        <span className="text-gray-400 w-12 text-right">{dim.score}%</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Company Profile (from AI website analysis) */}
          {prospect.companyProfile && (() => {
            const profile = prospect.companyProfile as { description?: string; industry?: string; keywords?: string[]; communicationStyle?: string; languageTone?: string; useDuOrSie?: string; employeeEstimate?: number; companyType?: string; leadership?: Array<{ name: string; role: string }>; confidence?: string }
            return (
              <div className="mt-2">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  {showProfile ? 'Firmenprofil ausblenden' : 'Firmenprofil (KI)'}
                </button>
                {showProfile && (
                  <div className="mt-2 bg-purple-50/50 rounded-lg p-3 space-y-2 border border-purple-100 text-xs">
                    {profile.description && <p className="text-gray-700">{profile.description}</p>}
                    <div className="flex flex-wrap gap-2">
                      {profile.industry && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">{profile.industry}</span>}
                      {profile.companyType && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{profile.companyType}</span>}
                      {profile.communicationStyle && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{profile.communicationStyle}</span>}
                      {profile.useDuOrSie && profile.useDuOrSie !== 'unklar' && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">Anrede: {profile.useDuOrSie}</span>}
                      {profile.employeeEstimate && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded">~{profile.employeeEstimate} MA</span>}
                    </div>
                    {profile.keywords && profile.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {profile.keywords.map((k, i) => <span key={i} className="text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">{k}</span>)}
                      </div>
                    )}
                    {profile.leadership && profile.leadership.length > 0 && (
                      <div>
                        <span className="text-gray-500 font-medium">Fuehrungsteam:</span>
                        {profile.leadership.map((l, i) => (
                          <span key={i} className="ml-2 text-gray-700">{l.name} <span className="text-gray-400">({l.role})</span></span>
                        ))}
                      </div>
                    )}
                    {profile.languageTone && <p className="text-gray-400 italic">Ton: {profile.languageTone}</p>}
                  </div>
                )}
              </div>
            )
          })()}
        </div>

        <LetterComposer
          prospect={prospect}
          referenceCustomerId={referenceCustomerId}
          onSent={handleSent}
          disabled={!!isDecided}
          variant="card"
          extraActions={(
            <>
              <button
                onClick={goPrev}
                disabled={currentIndex === 0}
                className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-white hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Vorheriger"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={goNext}
                disabled={currentIndex === total - 1}
                className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-white hover:text-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Nächster"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {!isDecided && (
                <>
                  <button
                    onClick={handleReject}
                    className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
                    title="Ablehnen"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Ablehnen
                  </button>
                  <button
                    onClick={handleSkip}
                    className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-500 text-sm font-medium rounded-lg hover:bg-white transition-colors"
                    title="Ueberspringen"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                </>
              )}
              {prospect.website && (
                <button
                  onClick={() => window.open(prospect.website!.startsWith('http') ? prospect.website! : `https://${prospect.website}`, '_blank')}
                  className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-500 text-sm rounded-lg hover:bg-white hover:text-gray-700 transition-colors"
                  title="Website aufrufen"
                >
                  <ExternalLink className="w-4 h-4" />
                  Website
                </button>
              )}
            </>
          )}
        />

        {/* Linked data: Activities (Briefe, Stellenanzeigen, Aufgaben, Aenderungen) */}
        <div className="px-6 py-3 border-t border-gray-100">
          <ProspectActivityTimeline prospectId={prospect.id} />
        </div>
      </div>

      {prospect.notes && (
        <p className="text-xs text-gray-400 italic px-1">{prospect.notes}</p>
      )}
    </div>
  )
}


function ResetAndSyncButton({ onSynced }: { onSynced: () => void }) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ updated?: number; created?: number; skipped: number; errors: string[] } | null>(null)

  const run = async () => {
    setLoading(true)
    setResult(null)
    try {
      const { data } = await apiClient.post('/acquisition/tasks/update-calendar-sync', {})
      setResult(data)
      if ((data.updated ?? 0) + (data.created ?? 0) > 0) onSynced()
    } catch {
      setResult({ skipped: 0, errors: ['Fehler beim Sync'] })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={run}
        disabled={loading}
        className="flex items-center gap-1 px-2 py-0.5 text-[11px] text-orange-500 hover:text-orange-700 border border-orange-200 rounded"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Kalender aktualisieren
      </button>
      {result && (
        <div className="text-[11px] text-gray-500">
          {result.updated ?? 0} aktualisiert, {result.created ?? 0} neu, {result.skipped} übersprungen
          {result.errors.length > 0 && <div className="text-red-500">{result.errors[0]}</div>}
        </div>
      )}
    </div>
  )
}

// Erweiterte Diagnose: zeigt offene Tasks teamweit, eigene Tasks total und
// kuerzlich auto-erledigte Tasks (Outlook-Erledigung). Hilft zu unterscheiden
// zwischen "keine Tasks vorhanden" und "Tasks wurden vom Cron auto-erledigt".
function DebugTaskAudit() {
  const myAll = useQuery<AcquisitionTask[]>({
    queryKey: ['acquisition', 'tasks', 'debug', 'mine-all'],
    queryFn: () => apiClient.get('/acquisition/tasks?status=all').then((r) => r.data),
    retry: false,
  })
  const teamOpen = useQuery<AcquisitionTask[]>({
    queryKey: ['acquisition', 'tasks', 'debug', 'team-open'],
    queryFn: () => apiClient.get('/acquisition/tasks?status=open&assignee=all').then((r) => r.data),
    retry: false,
  })
  const teamCompleted = useQuery<AcquisitionTask[]>({
    queryKey: ['acquisition', 'tasks', 'debug', 'team-completed'],
    queryFn: () => apiClient.get('/acquisition/tasks?status=completed&assignee=all').then((r) => r.data),
    retry: false,
  })

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const endOfToday = new Date()
  endOfToday.setHours(23, 59, 59, 999)
  const todayCompletedByOutlook = (teamCompleted.data ?? []).filter((t) => {
    const completedAt = (t as unknown as { completedAt?: string | null }).completedAt
    if (!completedAt) return false
    if (new Date(completedAt).getTime() < today.getTime()) return false
    const outcome = (t.outcome ?? '') as string
    return outcome.toLowerCase().includes('outlook-termin geloescht') ||
           outcome.toLowerCase().includes('outlook-termin gelöscht')
  })

  return (
    <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
      <div className="font-semibold text-gray-700">Erweiterter Audit:</div>
      <div>Eigene Tasks (alle Status): {myAll.isLoading ? '…' : myAll.data?.length ?? 0}</div>
      {(myAll.data ?? []).length > 0 && (
        <ul className="ml-3 list-disc">
          {(myAll.data ?? [])
            .slice()
            .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
            .map((t) => {
              const due = new Date(t.dueDate)
              const isPastOrToday = due.getTime() <= endOfToday.getTime()
              return (
                <li key={t.id}>
                  {t.prospect?.companyName ?? '(kein Prospect)'} · status={t.status} · due={due.toLocaleString('de-DE')} · m365EventId={t.m365EventId ? '✓' : '–'}
                  {isPastOrToday && t.status !== 'completed' && t.status !== 'skipped' && ' ← müsste heute angezeigt werden!'}
                </li>
              )
            })}
        </ul>
      )}
      <div>Teamweit offen (status=open, assignee=all): {teamOpen.isLoading ? '…' : teamOpen.data?.length ?? 0}</div>
      {(teamOpen.data ?? []).length > 0 && (
        <ul className="ml-3 list-disc">
          {(teamOpen.data ?? [])
            .slice()
            .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
            .slice(0, 20)
            .map((t) => {
              const due = new Date(t.dueDate)
              const assignedToMe = (t as unknown as { assignedUserId?: string }).assignedUserId
              return (
                <li key={t.id}>
                  {t.prospect?.companyName ?? '(kein Prospect)'} · due={due.toLocaleString('de-DE')} · assignedUserId={assignedToMe ?? '–'} · m365EventId={t.m365EventId ? '✓' : '–'}
                </li>
              )
            })}
        </ul>
      )}
      <div>Teamweit erledigt: {teamCompleted.isLoading ? '…' : teamCompleted.data?.length ?? 0}</div>
      <div className={todayCompletedByOutlook.length > 0 ? 'text-red-600 font-semibold' : ''}>
        Heute durch Outlook-Cron auto-erledigt: {todayCompletedByOutlook.length}
        {todayCompletedByOutlook.length > 0 && ' ← MOEGLICHER BUG'}
      </div>
      {todayCompletedByOutlook.length > 0 && (
        <ul className="ml-3 list-disc">
          {todayCompletedByOutlook.slice(0, 20).map((t) => (
            <li key={t.id} className="text-red-600">
              {t.prospect?.companyName ?? '(kein Prospect)'} · {t.title} · outcome=&quot;{t.outcome}&quot;
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function CalendarDebugButton() {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<Record<string, unknown> | null>(null)

  const run = async () => {
    setLoading(true)
    setResult(null)
    try {
      const { data } = await apiClient.get('/calendar/debug')
      setResult(data)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setResult({ error: e?.response?.data?.message || e?.message || 'Fehler' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={run}
        disabled={loading}
        className="flex items-center gap-1 px-2 py-0.5 text-[11px] text-gray-400 hover:text-gray-600 border border-gray-200 rounded"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Info className="w-3 h-3" />}
        M365 Debug
      </button>
      {result && (
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[11px] font-mono text-gray-700 max-w-sm text-left whitespace-pre-wrap break-all">
          {Object.entries(result).map(([k, v]) => (
            <div key={k}><b>{k}:</b> {typeof v === 'string' ? v : JSON.stringify(v, null, 2)}</div>
          ))}
        </div>
      )}
    </div>
  )
}

function SyncAllCalendarButton({ onSynced }: { onSynced: () => void }) {
  const [syncing, setSyncing] = useState(false)
  const [result, setResult] = useState<{
    synced: number
    failed: number
    results: Array<{ taskId: string; success: boolean; error?: string }>
  } | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSync = async () => {
    setSyncing(true)
    setResult(null)
    setError(null)
    setShowErrors(false)
    try {
      const { data } = await apiClient.post('/acquisition/tasks/sync-today', {})
      setResult({ synced: data.synced, failed: data.failed, results: data.results ?? [] })
      if (data.synced > 0) onSynced()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setError(e?.response?.data?.message || e?.message || 'Kalender-Sync fehlgeschlagen')
    } finally {
      setSyncing(false)
    }
  }

  const failedResults = result?.results.filter((r) => !r.success) ?? []

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        {result && (
          <span className="text-xs text-gray-500">
            {result.synced} eingetragen
            {result.failed > 0 && (
              <button
                onClick={() => setShowErrors((v) => !v)}
                className="ml-1 text-red-500 underline"
              >
                {result.failed} Fehler
              </button>
            )}
          </span>
        )}
        {error && <span className="text-xs text-red-500">{error}</span>}
        <button
          onClick={handleSync}
          disabled={syncing}
          title="Alle heutigen Aufgaben in den O365-Kalender eintragen"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-50 transition-colors"
        >
          {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calendar className="w-3 h-3" />}
          Alle im Kalender
        </button>
      </div>
      {showErrors && failedResults.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-[11px] text-red-700 max-w-xs">
          {failedResults.map((r) => (
            <p key={r.taskId}>{r.error || 'Unbekannter Fehler'}</p>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── TaskCard ─────────────────────────────────────────────────────────────

function buildFallbackEmailMailto(args: {
  email: string
  salutation: string | null
  lastName: string | null
  companyName: string
  userName: string | null
}): string {
  const greetingName = [args.salutation, args.lastName].filter(Boolean).join(' ').trim()
  const greetingLine = greetingName
    ? `Sehr geehrte/r ${greetingName},`
    : 'Sehr geehrte Damen und Herren,'
  const subject = `Kontaktversuch — ${args.companyName}`
  const body = [
    greetingLine,
    '',
    'ich habe gerade versucht, Sie telefonisch zu erreichen, leider ohne Erfolg.',
    '',
    'Hätten Sie kurz Zeit für einen Rückruf? Gerne nenne ich Ihnen auch ein Wunschthema vorab.',
    '',
    'Mit freundlichen Grüßen',
    args.userName ?? '',
  ].join('\r\n')
  return `mailto:${encodeURIComponent(args.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

function TaskCard({ task, onComplete, onSkip, onReopen, onOpenProspect, onSyncCalendar }: {
  task: AcquisitionTask
  onComplete: (outcome: string, outcomeCode?: 'reached_interested' | 'reached_not_interested' | 'not_reached' | 'other') => void
  onSkip: () => void
  onReopen?: () => void
  onOpenProspect: (prospectId: string) => void
  onSyncCalendar?: () => void
}) {
  const [showOutcome, setShowOutcome] = useState(false)
  const [outcome, setOutcome] = useState('')
  const [showNotes, setShowNotes] = useState(false)
  const [calendarSyncing, setCalendarSyncing] = useState(false)
  const [calendarError, setCalendarError] = useState<string | null>(null)
  const [showFallback, setShowFallback] = useState(false)
  const [fallbackError, setFallbackError] = useState<string | null>(null)
  const currentUser = useAuthStore((s) => s.user)
  const a = useAnonymize()

  const handleSyncCalendar = async () => {
    setCalendarSyncing(true)
    setCalendarError(null)
    try {
      await apiClient.post(`/acquisition/tasks/${task.id}/sync-calendar`, {})
      onSyncCalendar?.()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setCalendarError(e?.response?.data?.message || e?.message || 'Kalender-Sync fehlgeschlagen')
    } finally {
      setCalendarSyncing(false)
    }
  }

  const p = task.prospect
  const l = task.letter
  const fullName = [p?.contactSalutation, p?.contactFirstName, p?.contactLastName]
    .filter(Boolean).join(' ').trim()
  const phone = p?.contactPhone?.trim() || null
  const email = p?.contactEmail?.trim() || null
  const linkedinUrl = p?.contactLinkedinUrl?.trim() || null
  const telHref = phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : null

  const handleFallback = async (channel: 'email' | 'linkedin' | 'letter' | 'none') => {
    if (!p) return
    setFallbackError(null)

    if (channel === 'none') {
      onComplete(outcome || 'Nicht erreicht', 'not_reached')
      setShowOutcome(false)
      setShowFallback(false)
      return
    }

    let externalUrl: string | null = null
    let noteKind: ProspectNoteKind = 'other'
    let channelLabel = ''

    if (channel === 'email' && email) {
      externalUrl = buildFallbackEmailMailto({
        email,
        salutation: p.contactSalutation,
        lastName: p.contactLastName,
        companyName: p.companyName,
        userName: currentUser?.name ?? null,
      })
      noteKind = 'email'
      channelLabel = `E-Mail an ${email}`
    } else if (channel === 'linkedin' && linkedinUrl) {
      externalUrl = linkedinUrl
      noteKind = 'other'
      channelLabel = 'LinkedIn-Profil'
    } else if (channel === 'letter') {
      noteKind = 'other'
      channelLabel = 'Brief'
    } else {
      // Defensive: Knopf war disabled, sollte nicht passieren
      setFallbackError('Kontaktdaten fuer diesen Kanal fehlen.')
      return
    }

    const noteContent = outcome
      ? `Folgeaktion: ${channelLabel} (Anruf nicht erreicht). Notiz: ${outcome}`
      : `Folgeaktion: ${channelLabel} (Anruf nicht erreicht).`

    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer')
    }

    try {
      await apiClient.post(`/acquisition/prospects/${p.id}/notes`, {
        kind: noteKind,
        content: noteContent,
        taskId: task.id,
      })
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setFallbackError(`Folgeaktion-Notiz konnte nicht gespeichert werden: ${e?.response?.data?.message || e?.message || 'Unbekannter Fehler'}`)
      // Trotz Note-Fehler die Aufgabe abschliessen, damit der Nutzer nicht haengt.
    }

    onComplete(`Nicht erreicht — Folgeaktion: ${channelLabel}${outcome ? ` (${outcome})` : ''}`, 'not_reached')

    if (channel === 'letter') {
      onOpenProspect(p.id)
    }

    setShowOutcome(false)
    setShowFallback(false)
  }

  const sentDate = l?.versandDatum || l?.sentAt || null
  const daysSinceSent = sentDate
    ? Math.max(0, Math.floor((Date.now() - new Date(sentDate).getTime()) / (24 * 60 * 60 * 1000)))
    : null

  const scoreColor = p ? getScoreColor(p.icpScore) : 'bg-gray-100 text-gray-600'

  const isCall = task.type === 'call'
  const isEmail = task.type === 'email'
  const isOpenTask = task.status === 'pending' || task.status === 'in_progress'
  // „Ueberfaellig" gilt nur fuer noch offene Tasks. Erledigte/uebersprungene
  // Aufgaben sind per Definition nicht mehr ueberfaellig — sonst sieht der
  // User eine geschlossene Task in der „Alle"-Liste mit rotem Ueberfaellig-
  // Label und denkt, sie sei noch offen.
  const isOverdue = isOpenTask && new Date(task.dueDate).getTime() < Date.now() - 12 * 60 * 60 * 1000
  const notesPreview = p?.notes ? p.notes.trim() : ''

  return (
    <div className={cn('bg-white border border-gray-200 rounded-lg border-l-4 overflow-hidden', PRIORITY_BORDER_CLASSES[task.priority as Priority] ?? 'border-l-gray-300')}>
      {/* ─── Header: Firma + Score + Stadt ─── */}
      {p ? (
        <button
          type="button"
          onClick={() => onOpenProspect(p.id)}
          className="w-full text-left px-4 pt-3 pb-2 hover:bg-gray-50 transition-colors border-b border-gray-100"
          title="Zielkunden-Detail oeffnen"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate">{p.companyName}</span>
            <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', scoreColor)} title="ICP-Score">
              {p.icpScore}
            </span>
          </div>
          {(fullName || p.contactRole || p.city) && (
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-600 flex-wrap">
              {fullName && (
                <span className="inline-flex items-center gap-1">
                  <User className="w-3 h-3 text-gray-400" />
                  {fullName}
                  {p.contactRole && <span className="text-gray-400"> · {p.contactRole}</span>}
                </span>
              )}
              {p.city && (
                <span className="inline-flex items-center gap-1 text-gray-500">
                  <MapPin className="w-3 h-3 text-gray-400" />
                  {p.city}
                </span>
              )}
            </div>
          )}
        </button>
      ) : (
        <div className="px-4 pt-3 pb-2 border-b border-gray-100">
          <p className="text-xs text-gray-400 italic">Kein Zielkunden-Kontext verknuepft</p>
        </div>
      )}

      {/* ─── Primaer-CTA: Telefonnummer / E-Mail ─── */}
      {p && (
        <div className="px-4 pt-3 space-y-2">
          {isCall && telHref && (
            <a
              href={telHref}
              className="flex items-center gap-2 px-3 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">{phone}</span>
              <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded">Anrufen</span>
            </a>
          )}
          {isCall && !phone && (
            <button
              type="button"
              onClick={() => onOpenProspect(p.id)}
              className="w-full flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 hover:bg-red-100 text-red-700 text-sm font-medium rounded-lg transition-colors"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="flex-1 text-left">Keine Rufnummer hinterlegt</span>
              <span className="text-[10px] bg-red-200 px-1.5 py-0.5 rounded shrink-0">Recherchieren</span>
            </button>
          )}
          {isEmail && email && (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 px-3 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">{email}</span>
              <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded">E-Mail</span>
            </a>
          )}

          {/* Sekundaere Kontaktmoeglichkeit (bei call: E-Mail, bei anderen Typen: beides) */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {!isCall && phone && telHref && (
              <a
                href={telHref}
                className="inline-flex items-center gap-1 px-2 py-1 border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700 rounded-md transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="w-3 h-3" />
                {phone}
              </a>
            )}
            {!isEmail && email && (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1 px-2 py-1 border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700 rounded-md transition-colors truncate max-w-[200px]"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail className="w-3 h-3 shrink-0" />
                <span className="truncate">{email}</span>
              </a>
            )}
            {p.website && (
              <a
                href={p.website.startsWith('http') ? p.website : `https://${p.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700 rounded-md transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe className="w-3 h-3" />
                Website
              </a>
            )}
          </div>
        </div>
      )}

      {/* ─── Kontext-Banner ─── */}
      {p && (sentDate || p.triggerEvent) && (
        <div className="px-4 pt-3 space-y-1">
          {sentDate && (
            <p className="text-[11px] text-gray-500 inline-flex items-center gap-1.5">
              <Mail className="w-3 h-3 text-amber-500" />
              Brief versendet {daysSinceSent === 0 ? 'heute' : daysSinceSent === 1 ? 'gestern' : `vor ${daysSinceSent} Tagen`} ({formatDate(sentDate)})
              {l?.sentByUserName && <> · von {l.sentByUserName}</>}
            </p>
          )}
          {p.triggerEvent && (
            <p className="text-[11px] text-gray-500 inline-flex items-start gap-1.5">
              <Target className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{p.triggerEvent}</span>
            </p>
          )}
        </div>
      )}

      {/* ─── Aufgaben-Titel + Beschreibung ─── */}
      <div className="px-4 pt-3 pb-2">
        <p className="text-xs font-medium text-gray-900">{task.title}</p>
        {task.description && (
          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{task.description}</p>
        )}
        <div className="flex items-center gap-2 mt-1.5 text-[11px]">
          {isOpenTask ? (
            <span className={cn(
              'inline-flex items-center gap-1',
              isOverdue ? 'text-red-600 font-medium' : 'text-gray-400',
            )}>
              <Clock className="w-3 h-3" />
              {isOverdue ? 'Ueberfaellig: ' : 'Faellig: '}{formatDate(task.dueDate)}
            </span>
          ) : (
            <span className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
              task.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600',
            )}>
              {task.status === 'completed' ? <CheckCircle2 className="w-3 h-3" /> : <SkipForward className="w-3 h-3" />}
              {task.status === 'completed' ? 'Erledigt' : 'Uebersprungen'}
              {task.completedAt && ` · ${formatDate(task.completedAt)}`}
            </span>
          )}
          {isOpenTask && task.priority === 'high' && (
            <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Hoch</span>
          )}
          {task.assignedUserName && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded"
              title={`Zustaendig: ${task.assignedUserName}`}
            >
              <User className="w-3 h-3" />
              {a.name(task.assignedUserName)}
            </span>
          )}
          <button
            onClick={handleSyncCalendar}
            disabled={calendarSyncing}
            title={task.m365EventId ? 'Termin in O365 aktualisieren' : 'Als Termin in O365-Kalender eintragen'}
            className={cn(
              'ml-auto w-6 h-6 rounded flex items-center justify-center transition-colors',
              task.m365EventId
                ? 'text-blue-500 bg-blue-50 hover:bg-blue-100'
                : 'text-gray-300 hover:text-blue-500 hover:bg-blue-50',
              calendarSyncing && 'opacity-50 cursor-wait',
            )}
          >
            {calendarSyncing
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Calendar className="w-3 h-3" />
            }
          </button>
        </div>
        {calendarError && (
          <p className="text-[10px] text-red-500 mt-1">{calendarError}</p>
        )}
      </div>

      {/* ─── Vorherige Notizen (collapsible) ─── */}
      {notesPreview && (
        <div className="px-4 pb-2">
          <button
            type="button"
            onClick={() => setShowNotes((v) => !v)}
            className="text-[11px] text-gray-400 hover:text-gray-600 inline-flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            {showNotes ? 'Notizen ausblenden' : `Notizen anzeigen (${notesPreview.length} Zeichen)`}
          </button>
          {showNotes && (
            <p className="mt-1 text-[11px] text-gray-600 bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">
              {notesPreview}
            </p>
          )}
        </div>
      )}

      {/* ─── Aktionen ─── */}
      <div className="px-4 pb-3 pt-2 border-t border-gray-100">
        {!isOpenTask ? (
          // Erledigte/uebersprungene Tasks: keine Aktionen, nur Ergebnis +
          // Detail-Link. Bei `skipped` zusaetzlich „Wieder oeffnen", damit ein
          // versehentliches Ueberspringen ohne DB-Eingriff korrigierbar ist.
          <div className="flex gap-2 items-center">
            {task.outcome ? (
              <span className="text-[11px] text-gray-500 truncate" title={task.outcome}>
                Ergebnis: {task.outcome}
              </span>
            ) : (
              <span className="text-[11px] text-gray-400">Ohne Notiz abgeschlossen</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {task.status === 'skipped' && onReopen && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={onReopen}
                  className="text-amber-600 hover:text-amber-700"
                  title="Aufgabe wieder als offen markieren"
                  leadingIcon={<RotateCw className="w-3 h-3" />}
                >
                  Wieder oeffnen
                </Button>
              )}
              {p && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onOpenProspect(p.id)}
                  className="text-brand-600 hover:text-brand-700"
                  title="Zielkunden-Detail oeffnen"
                  leadingIcon={<Eye className="w-3 h-3" />}
                >
                  Details
                </Button>
              )}
            </div>
          </div>
        ) : !showOutcome ? (
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="xs"
              onClick={() => setShowOutcome(true)}
              className="bg-green-500 hover:bg-green-600"
              leadingIcon={<CheckCircle2 className="w-3 h-3" />}
            >
              Erledigt
            </Button>
            <Button
              variant="secondary"
              size="xs"
              onClick={onSkip}
            >
              Ueberspringen
            </Button>
            {p && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onOpenProspect(p.id)}
                className="ml-auto text-brand-600 hover:text-brand-700"
                title="Zielkunden-Detail oeffnen"
                leadingIcon={<Eye className="w-3 h-3" />}
              >
                Details
              </Button>
            )}
          </div>
        ) : (
          // Strukturierte Outcome-Auswahl. Jeder Button steht fuer einen
          // definierten Outcome-Code (Backend triggert damit automatisch
          // Statuswechsel des Prospects). Zusaetzliche freie Notiz moeglich.
          <div className="space-y-2">
            <textarea
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="Gespraechsnotiz (optional)..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs resize-none"
            />
            {!showFallback ? (
              <>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => { onComplete(outcome || 'Reaktion / Interesse', 'reached_interested'); setShowOutcome(false) }}
                    className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1"
                    title="Prospect wechselt auf 'Interesse' und bekommt Termin-Aufgabe"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Interesse
                  </button>
                  <button
                    onClick={() => setShowFallback(true)}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-md transition-colors"
                    title="Anruf nicht erreicht — danach Folgeaktion (Mail/LinkedIn/Brief) waehlen"
                  >
                    Nicht erreicht
                  </button>
                  <button
                    onClick={() => { onComplete(outcome || 'Kein Interesse', 'reached_not_interested'); setShowOutcome(false) }}
                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-md transition-colors"
                    title="Prospect wechselt auf 'Kein Interesse'"
                  >
                    Kein Interesse
                  </button>
                  <button
                    onClick={() => { onComplete(outcome || 'Abgeschlossen', 'other'); setShowOutcome(false) }}
                    className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded-md transition-colors"
                    title="Abschluss ohne weitere Aktion"
                  >
                    Andere
                  </button>
                </div>
                <button
                  onClick={() => setShowOutcome(false)}
                  className="w-full px-3 py-1 border border-gray-300 text-gray-600 text-[11px] font-medium rounded-md hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
              </>
            ) : (
              // Fallback-Kanal: Nach „Nicht erreicht" waehlt der Vertriebler
              // die Folgeaktion. Jede Aktion oeffnet ggf. einen externen Link
              // (mailto / LinkedIn-Profil), legt eine Aktivitaets-Notiz am
              // Prospect an und schliesst die Aufgabe mit Outcome `not_reached`
              // ab. Brief navigiert zusaetzlich ins Prospect-Detail, wo der
              // Letter-Composer angestossen werden kann.
              <div className="space-y-1.5">
                <p className="text-[11px] text-gray-600 font-medium">Folgeaktion?</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => handleFallback('email')}
                    disabled={!email}
                    className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1"
                    title={email ? `E-Mail an ${email} mit vorbereitetem Text oeffnen` : 'Keine E-Mail-Adresse hinterlegt'}
                  >
                    <Mail className="w-3 h-3" /> E-Mail
                  </button>
                  <button
                    onClick={() => handleFallback('linkedin')}
                    disabled={!linkedinUrl}
                    className="px-3 py-1.5 bg-[#0A66C2] hover:bg-[#004182] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1"
                    title={linkedinUrl ? 'LinkedIn-Profil oeffnen und als Kontaktversuch loggen' : 'Keine LinkedIn-URL hinterlegt'}
                  >
                    <Linkedin className="w-3 h-3" /> LinkedIn
                  </button>
                  <button
                    onClick={() => handleFallback('letter')}
                    className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1"
                    title="Folgeaktion 'Brief' loggen und ins Prospect-Detail springen (Letter-Composer)"
                  >
                    <FileText className="w-3 h-3" /> Brief
                  </button>
                  <button
                    onClick={() => handleFallback('none')}
                    className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded-md transition-colors"
                    title="Nur als 'nicht erreicht' markieren — Pipeline-Tick erzeugt nach 12 Tagen den 2. Versuch"
                  >
                    Keine
                  </button>
                </div>
                {fallbackError && (
                  <p className="text-[10px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{fallbackError}</p>
                )}
                <button
                  onClick={() => { setShowFallback(false); setFallbackError(null) }}
                  className="w-full px-3 py-1 border border-gray-300 text-gray-600 text-[11px] font-medium rounded-md hover:bg-gray-50 transition-colors"
                >
                  Zurueck
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function AddProspectModal({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const [form, setForm] = useState({
    companyName: '',
    industry: '',
    employeeCount: '',
    city: '',
    zipCode: '',
    street: '',
    contactSalutation: 'Herr',
    contactFirstName: '',
    contactLastName: '',
    contactRole: '',
    contactEmail: '',
    contactPhone: '',
    source: 'manuell',
    notes: '',
    memberships: '',
  })

  const createProspect = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post('/acquisition/prospects', data).then((r) => r.data),
    onSuccess: () => onSave(),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    createProspect.mutate({
      ...form,
      employeeCount: form.employeeCount ? Number(form.employeeCount) : null,
      memberships: form.memberships ? form.memberships.split(/[,;]+/).map((s) => s.trim()).filter(Boolean) : [],
    })
  }

  const field = (label: string, key: keyof typeof form, opts?: { type?: string; required?: boolean; placeholder?: string }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={opts?.type ?? 'text'}
        required={opts?.required}
        placeholder={opts?.placeholder}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
    </div>
  )

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      title="Neue Firma"
      size="xl"
    >
      <form onSubmit={handleSubmit} id="add-prospect-form" className="space-y-6">
        <div>
          <h3 className="text-small font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Unternehmen
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('Firmenname *', 'companyName', { required: true })}
            {field('Branche', 'industry', { placeholder: 'z.B. Steuerkanzlei' })}
            {field('Mitarbeiteranzahl', 'employeeCount', { type: 'number' })}
            {field('Strasse', 'street')}
            {field('PLZ', 'zipCode')}
            {field('Stadt', 'city')}
          </div>
        </div>

        <div>
          <h3 className="text-small font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            Ansprechpartner
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-caption font-medium text-gray-600 mb-1">Anrede</label>
              <select
                value={form.contactSalutation}
                onChange={(e) => setForm((f) => ({ ...f, contactSalutation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-small bg-white"
              >
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
                <option value="">Keine Angabe</option>
              </select>
            </div>
            {field('Vorname', 'contactFirstName')}
            {field('Nachname', 'contactLastName')}
            {field('Position', 'contactRole', { placeholder: 'z.B. Geschaeftsfuehrer' })}
            {field('E-Mail', 'contactEmail', { type: 'email' })}
            {field('Telefon', 'contactPhone')}
          </div>
        </div>

        <Input
          label="Netzwerke / Verbaende"
          value={form.memberships}
          onChange={(e) => setForm((f) => ({ ...f, memberships: e.target.value }))}
          placeholder="Kommagetrennt, z.B. Die Familienunternehmer, IHK, BVMW"
        />

        <Textarea
          label="Notizen"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          rows={3}
          placeholder="z.B. Trigger-Event, Pain Points, besondere Hinweise..."
        />

        <div className="flex gap-3 justify-end">
          <Button variant="secondary" size="md" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button variant="primary" size="md" type="submit" loading={createProspect.isPending}>
            {createProspect.isPending ? 'Wird gespeichert...' : 'Firma anlegen'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Prospect Detail Modal (Vollansicht mit Historie, Bearbeitung, Aktionen) ──

type DetailActionKey = 'recheck' | 'address' | 'enrichment' | 'profile' | 'rescore' | 'deactivate' | 'handoff'

function ProspectDetailModal({ prospect, onClose, onUpdate }: {
  prospect: Prospect
  onClose: () => void
  onUpdate: () => void
}) {
  const a = useAnonymize()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    companyName: prospect.companyName ?? '',
    industry: prospect.industry ?? '',
    employeeCount: prospect.employeeCount != null ? String(prospect.employeeCount) : '',
    website: prospect.website ?? '',
    street: prospect.street ?? '',
    zipCode: prospect.zipCode ?? '',
    city: prospect.city ?? '',
    region: prospect.region ?? '',
    country: prospect.country ?? 'DE',
    contactSalutation: prospect.contactSalutation ?? '',
    contactFirstName: prospect.contactFirstName ?? '',
    contactLastName: prospect.contactLastName ?? '',
    contactRole: prospect.contactRole ?? '',
    contactEmail: prospect.contactEmail ?? '',
    contactPhone: prospect.contactPhone ?? '',
    contactLinkedinUrl: prospect.contactLinkedinUrl ?? '',
    status: prospect.status ?? 'new',
    triggerEvent: prospect.triggerEvent ?? '',
    notes: prospect.notes ?? '',
    nextFollowUpAt: prospect.nextFollowUpAt ? prospect.nextFollowUpAt.substring(0, 10) : '',
  })
  const [isDirty, setIsDirty] = useState(false)
  const [actionFeedback, setActionFeedback] = useState<{ kind: 'info' | 'error' | 'success'; text: string } | null>(null)
  const [runningAction, setRunningAction] = useState<DetailActionKey | null>(null)
  const [activeTab, setActiveTab] = useState<'ueberblick' | 'aktivitaeten' | 'profil'>('ueberblick')
  const [showLetterComposer, setShowLetterComposer] = useState(false)
  const [showLinkedInModal, setShowLinkedInModal] = useState(false)
  const [showEmailComposer, setShowEmailComposer] = useState(false)

  // Brief-Button: verhindere Duplikate fuer Prospects mit laufendem Versand
  // (spiegelt die Backend-Filter-Logik in acquisition.ts /prospects/daily).
  const latestLetter = prospect.latestLetter
  const letterInProgress = Boolean(
    latestLetter && (
      latestLetter.kundenschreiberOrderId ||
      latestLetter.status === 'sending' ||
      latestLetter.status === 'sent' ||
      latestLetter.status === 'delivered'
    ),
  )
  const addressMissing = !prospect.street || !prospect.zipCode
  const letterButtonTitle = letterInProgress
    ? 'Für diesen Kunden läuft bereits ein Briefversand'
    : addressMissing
      ? 'Adresse (Straße & PLZ) fehlt – erst ergänzen'
      : 'Personalisierten Brief über Kundenschreiber verschicken'

  // Reset form whenever the prospect changes externally (e.g. after enrichment)
  useEffect(() => {
    setForm({
      companyName: prospect.companyName ?? '',
      industry: prospect.industry ?? '',
      employeeCount: prospect.employeeCount != null ? String(prospect.employeeCount) : '',
      website: prospect.website ?? '',
      street: prospect.street ?? '',
      zipCode: prospect.zipCode ?? '',
      city: prospect.city ?? '',
      region: prospect.region ?? '',
      country: prospect.country ?? 'DE',
      contactSalutation: prospect.contactSalutation ?? '',
      contactFirstName: prospect.contactFirstName ?? '',
      contactLastName: prospect.contactLastName ?? '',
      contactRole: prospect.contactRole ?? '',
      contactEmail: prospect.contactEmail ?? '',
      contactPhone: prospect.contactPhone ?? '',
      contactLinkedinUrl: prospect.contactLinkedinUrl ?? '',
      status: prospect.status ?? 'new',
      triggerEvent: prospect.triggerEvent ?? '',
      notes: prospect.notes ?? '',
      nextFollowUpAt: prospect.nextFollowUpAt ? prospect.nextFollowUpAt.substring(0, 10) : '',
    })
    setIsDirty(false)
  }, [prospect])

  const updateField = <K extends keyof typeof form>(key: K, value: typeof form[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setIsDirty(true)
  }

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['acquisition'] })
    queryClient.invalidateQueries({ queryKey: ['prospect-history', prospect.id] })
    queryClient.invalidateQueries({ queryKey: ['prospect-tasks', prospect.id] })
    onUpdate()
  }

  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.patch(`/acquisition/prospects/${prospect.id}`, data).then((r) => r.data),
    onSuccess: () => {
      setIsDirty(false)
      setActionFeedback({ kind: 'success', text: 'Änderungen gespeichert' })
      invalidateAll()
    },
    onError: (err: unknown) => {
      const axErr = err as { response?: { data?: { error?: string } } }
      setActionFeedback({ kind: 'error', text: axErr?.response?.data?.error || 'Fehler beim Speichern' })
    },
  })

  function handleSave(e?: React.FormEvent) {
    e?.preventDefault()
    save.mutate({
      ...form,
      employeeCount: form.employeeCount ? Number(form.employeeCount) : null,
      nextFollowUpAt: form.nextFollowUpAt ? new Date(form.nextFollowUpAt).toISOString() : null,
    })
  }

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      apiClient.patch(`/acquisition/prospects/${prospect.id}`, { status }).then((r) => r.data),
    onSuccess: () => {
      setActionFeedback({ kind: 'success', text: 'Status aktualisiert' })
      invalidateAll()
    },
    onError: () => setActionFeedback({ kind: 'error', text: 'Status konnte nicht aktualisiert werden' }),
  })

  function changeStatus(newStatus: string) {
    updateField('status', newStatus)
    statusMutation.mutate(newStatus)
  }

  async function runAction(key: DetailActionKey) {
    setRunningAction(key)
    setActionFeedback(null)
    try {
      if (key === 'recheck') {
        const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/recheck`, {})
        setActionFeedback({ kind: data.found ? 'success' : 'info', text: data.message || (data.found ? 'Neue Daten gefunden' : 'Keine neuen Daten gefunden') })
      } else if (key === 'address') {
        const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/enrich`, { type: 'address' })
        setActionFeedback({ kind: data.found ? 'success' : 'info', text: data.message || (data.found ? 'Adresse gefunden' : 'Keine Adresse gefunden') })
      } else if (key === 'enrichment') {
        const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/enrich`, { type: 'contact' })
        setActionFeedback({ kind: data.found ? 'success' : 'info', text: data.message || (data.found ? 'Ansprechpartner ergänzt' : 'Kein Kontakt gefunden') })
      } else if (key === 'profile') {
        const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/profile`, {})
        setActionFeedback({ kind: data.success ? 'success' : 'info', text: data.message || (data.success ? 'Unternehmensprofil aktualisiert' : 'Profiling fehlgeschlagen') })
      } else if (key === 'rescore') {
        const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/rescore`, {})
        setActionFeedback({ kind: 'success', text: `Score neu berechnet: ${data.icpScore} Punkte` })
      } else if (key === 'deactivate') {
        if (!window.confirm(`"${a.name(prospect.companyName)}" wirklich deaktivieren? Der Status wird auf "Kein Interesse" gesetzt.`)) {
          setRunningAction(null)
          return
        }
        await apiClient.patch(`/acquisition/prospects/${prospect.id}`, { status: 'not_interested' })
        updateField('status', 'not_interested')
        setActionFeedback({ kind: 'success', text: 'Zielkunde deaktiviert' })
      } else if (key === 'handoff') {
        // Optional: TANSS-Ticket-ID mitgeben, damit der Prospect direkt
        // mit dem Deal verknuepft werden kann. Leer lassen → Ticket muss
        // noch in TANSS angelegt werden.
        const ticketRaw = window.prompt(
          `"${prospect.companyName}" an Vertrieb übergeben.\n\n` +
            'Optional: TANSS-Ticket-ID des neuen Deals (leer lassen, wenn das Ticket ' +
            'noch nicht existiert – du musst es dann manuell in TANSS mit Tag ' +
            '"Vertrieb: Lead neu" versehen).',
          '',
        )
        if (ticketRaw === null) { setRunningAction(null); return }
        const tanssTicketId = ticketRaw.trim() || null
        const { data } = await apiClient.post(
          `/acquisition/prospects/${prospect.id}/handoff`,
          { tanssTicketId },
        )
        updateField('status', 'converted')
        setActionFeedback({
          kind: 'success',
          text: data?.note || 'An Vertrieb übergeben. Offene Aufgaben wurden übersprungen.',
        })
      }
      invalidateAll()
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string } } }
      setActionFeedback({ kind: 'error', text: axErr?.response?.data?.error || 'Aktion fehlgeschlagen' })
    } finally {
      setRunningAction(null)
    }
  }

  // Tasks and letters — for the activity tab badge count (shared query keys with
  // ProspectActivityTimeline so React Query deduplicates the requests).
  const tasksQuery = useQuery<AcquisitionTask[]>({
    queryKey: ['prospect-tasks', prospect.id],
    queryFn: () => apiClient.get(`/acquisition/tasks?prospectId=${prospect.id}`).then((r) => r.data),
    staleTime: 15_000,
  })

  const lettersQueryForBadge = useQuery<ProspectLetter[]>({
    queryKey: ['prospect-letters', prospect.id],
    queryFn: () => apiClient.get(`/acquisition/prospects/${prospect.id}/letters`).then((r) => r.data),
    staleTime: 15_000,
  })

  const scoreColor = getScoreColor(prospect.icpScore)
  const statusLabel = STATUS_LABELS[prospect.status]?.label ?? prospect.status

  const scoringDetails = (prospect as unknown as { scoringDetails?: Record<string, unknown> | null }).scoringDetails ?? null
  const companyProfile = prospect.companyProfile as Record<string, any> | null
  const rawMemberships = prospect.memberships
  const memberships: string[] = Array.isArray(rawMemberships)
    ? rawMemberships
    : typeof rawMemberships === 'string'
      ? (() => { try { const parsed = JSON.parse(rawMemberships); return Array.isArray(parsed) ? parsed : [] } catch { return [] } })()
      : []

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      size="xl"
      hideCloseButton
      className="max-w-6xl h-[90vh]"
    >
      <div className="-mx-5 -my-5 flex flex-col h-[calc(90vh)]">
        {/* ─── Header mit Aktionen ─── */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <CompanyLogo website={prospect.website} companyName={prospect.companyName} size="md" />
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate">{a.name(prospect.companyName)}</h2>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className={cn('text-caption font-bold px-2 py-0.5 rounded', scoreColor)} title="ICP-Score">
                    Score {prospect.icpScore}
                  </span>
                  <StatusBadge status={prospect.status} label={statusLabel} />
                  {prospect.sourceChannel && (
                    <SourceBadge source={prospect.source} sourceChannel={prospect.sourceChannel} website={prospect.website} />
                  )}
                  {prospect.website && !a.enabled && (
                    <a
                      href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs text-brand-600 hover:text-brand-800 hover:underline inline-flex items-center gap-1"
                    >
                      <Globe className="w-3 h-3" />
                      {prospect.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              <DetailActionButton
                icon={Search}
                label="Prüfen"
                color="purple"
                onClick={() => runAction('recheck')}
                pending={runningAction === 'recheck'}
                disabled={!prospect.website || runningAction !== null}
                title="Impressum neu laden (findet Ansprechpartner, Adresse, Firmennamen)"
              />
              <DetailActionButton
                icon={MapPin}
                label="Adresse"
                color="amber"
                onClick={() => runAction('address')}
                pending={runningAction === 'address'}
                disabled={runningAction !== null}
                title="Adresse per Google Places suchen"
              />
              <DetailActionButton
                icon={Sparkles}
                label="Enrichment"
                color="blue"
                onClick={() => runAction('enrichment')}
                pending={runningAction === 'enrichment'}
                disabled={runningAction !== null}
                title="Ansprechpartner per Apollo suchen"
              />
              <DetailActionButton
                icon={Globe}
                label="Profil"
                color="indigo"
                onClick={() => runAction('profile')}
                pending={runningAction === 'profile'}
                disabled={!prospect.website || runningAction !== null}
                title="Website AI-Profiling"
              />
              <DetailActionButton
                icon={TrendingUp}
                label="Score"
                color="green"
                onClick={() => runAction('rescore')}
                pending={runningAction === 'rescore'}
                disabled={runningAction !== null}
                title="ICP-Score neu berechnen"
              />
              {(prospect.status === 'interested' || prospect.status === 'meeting_scheduled') && (
                <DetailActionButton
                  icon={ArrowRightCircle}
                  label="An Vertrieb"
                  color="indigo"
                  onClick={() => runAction('handoff')}
                  pending={runningAction === 'handoff'}
                  disabled={runningAction !== null}
                  title="An Vertriebsboard übergeben (Status → Gewonnen)"
                />
              )}
              <DetailActionButton
                icon={XCircle}
                label="Deaktivieren"
                color="red"
                onClick={() => runAction('deactivate')}
                pending={runningAction === 'deactivate'}
                disabled={runningAction !== null}
                title="Zielkunde deaktivieren (Status: Kein Interesse)"
              />
              <button
                type="button"
                onClick={onClose}
                className="ml-1 text-gray-400 hover:text-gray-600 p-1.5"
                title="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {actionFeedback && (
            <div
              className={cn(
                'mt-3 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2',
                actionFeedback.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                actionFeedback.kind === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                'bg-blue-50 text-blue-700 border border-blue-200',
              )}
            >
              {actionFeedback.kind === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> :
               actionFeedback.kind === 'error' ? <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> :
               <Info className="w-3.5 h-3.5 shrink-0" />}
              <span className="flex-1">{actionFeedback.text}</span>
              <button type="button" onClick={() => setActionFeedback(null)} className="text-current opacity-60 hover:opacity-100">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* ─── Status/Timing Leiste (immer sichtbar) ─── */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50/50 grid grid-cols-3 gap-4 flex-shrink-0">
          <div>
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Status</label>
            <select
              value={form.status}
              onChange={(e) => changeStatus(e.target.value)}
              disabled={statusMutation.isPending}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs bg-white"
            >
              {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Nächster Follow-up</label>
            <input
              type="date"
              value={form.nextFollowUpAt}
              onChange={(e) => updateField('nextFollowUpAt', e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-500 mb-0.5">Letzter Kontakt</label>
            <div className="w-full px-2 py-1.5 border border-gray-200 bg-white rounded-md text-xs text-gray-600 truncate">
              {prospect.lastContactedAt ? new Date(prospect.lastContactedAt).toLocaleString('de-DE') : '–'}
            </div>
          </div>
        </div>

        {/* ─── Tab Bar ─── */}
        <div className="px-6 border-b border-gray-200 flex items-center gap-1 flex-shrink-0 overflow-x-auto">
          {([
            { key: 'ueberblick', label: 'Überblick', icon: Building2, badge: 0 },
            { key: 'aktivitaeten', label: 'Aktivitäten', icon: Clock, badge: (Array.isArray(tasksQuery.data) ? tasksQuery.data.length : 0) + (Array.isArray(lettersQueryForBadge.data) ? lettersQueryForBadge.data.length : 0) },
            { key: 'profil', label: 'Profil & Score', icon: Sparkles, badge: 0 },
          ] as const).map(({ key, label, icon: TabIcon, badge }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                activeTab === key
                  ? 'border-brand-500 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              )}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {label}
              {typeof badge === 'number' && badge > 0 && (
                <span className="ml-1 text-[10px] bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded-full font-semibold">{badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ─── Tab Content ─── */}
        <form onSubmit={handleSave} id="prospect-detail-form" className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
            {/* UeBERBLICK: Unternehmen + Ansprechpartner + Kontext & Notizen (HubSpot "About"-Panel-Pattern) */}
            {activeTab === 'ueberblick' && (
              <div className="space-y-5">
                {/* Unternehmen */}
                <section className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5 mb-3">
                    <Building2 className="w-3.5 h-3.5 text-gray-400" />
                    Unternehmen
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <DetailField label="Firmenname *" value={form.companyName} onChange={(v) => updateField('companyName', v)} />
                    <DetailField label="Branche" value={form.industry} onChange={(v) => updateField('industry', v)} placeholder="z.B. Steuerkanzlei" />
                    <DetailField label="Mitarbeiter" value={form.employeeCount} onChange={(v) => updateField('employeeCount', v)} type="number" />
                    <DetailField label="Website" value={form.website} onChange={(v) => updateField('website', v)} placeholder="firma.de" />
                    <DetailField label="Straße" value={form.street} onChange={(v) => updateField('street', v)} />
                    <DetailField label="PLZ" value={form.zipCode} onChange={(v) => updateField('zipCode', v)} />
                    <DetailField label="Stadt" value={form.city} onChange={(v) => updateField('city', v)} />
                    <DetailField label="Region" value={form.region} onChange={(v) => updateField('region', v)} />
                    <DetailField label="Land" value={form.country} onChange={(v) => updateField('country', v)} placeholder="DE" />
                  </div>
                </section>

                {/* Ansprechpartner */}
                <section className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5 mb-3">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    Ansprechpartner
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Anrede</label>
                      <select
                        value={form.contactSalutation}
                        onChange={(e) => updateField('contactSalutation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                      >
                        <option value="">Keine Angabe</option>
                        <option value="Herr">Herr</option>
                        <option value="Frau">Frau</option>
                      </select>
                    </div>
                    <DetailField label="Vorname" value={form.contactFirstName} onChange={(v) => updateField('contactFirstName', v)} />
                    <DetailField label="Nachname" value={form.contactLastName} onChange={(v) => updateField('contactLastName', v)} />
                    <DetailField label="Position" value={form.contactRole} onChange={(v) => updateField('contactRole', v)} placeholder="z.B. Geschäftsführer" />
                    <DetailField label="E-Mail" value={form.contactEmail} onChange={(v) => updateField('contactEmail', v)} type="email" />
                    <DetailField label="Telefon" value={form.contactPhone} onChange={(v) => updateField('contactPhone', v)} />
                    <div className="md:col-span-2 lg:col-span-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-xs font-medium text-gray-700">LinkedIn</span>
                        <a
                          href={`https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent([form.contactFirstName, form.contactLastName].filter(Boolean).join(' ').trim() || form.companyName)}&origin=GLOBAL_SEARCH_HEADER`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Auf LinkedIn suchen"
                        >
                          <IconButton icon={<Search className="w-3.5 h-3.5" />} aria-label="Auf LinkedIn suchen" size="xs" variant="ghost" />
                        </a>
                        {form.contactLinkedinUrl && (
                          <a href={form.contactLinkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:text-[#004182]" title="LinkedIn-Profil öffnen">
                            <Linkedin className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <Input
                        value={form.contactLinkedinUrl}
                        placeholder="https://linkedin.com/in/..."
                        onChange={(e) => updateField('contactLinkedinUrl', e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                {/* Kontext & Notizen */}
                <section className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5 mb-3">
                    <Info className="w-3.5 h-3.5 text-gray-400" />
                    Kontext & Notizen
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Trigger-Event</label>
                      <input
                        type="text"
                        value={form.triggerEvent}
                        onChange={(e) => updateField('triggerEvent', e.target.value)}
                        placeholder="z.B. Stellenanzeige Fachinformatiker"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    {memberships.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Mitgliedschaften</label>
                        <div className="flex flex-wrap gap-1.5">
                          {memberships.map((m, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                              {m}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notizen</label>
                      <textarea
                        value={form.notes}
                        onChange={(e) => updateField('notes', e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                      />
                    </div>
                    <div className="text-xs text-gray-400 border-t border-gray-100 pt-3 flex items-center gap-4 flex-wrap">
                      <span>Erstellt: {new Date(prospect.createdAt).toLocaleString('de-DE')}</span>
                      <span>•</span>
                      <span className="font-mono">{prospect.id}</span>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* AKTIVITAeTEN: unified Timeline (Briefe, Stellenanzeigen, Aufgaben, Aenderungen) */}
            {activeTab === 'aktivitaeten' && (
              <ProspectActivityTimeline
                prospectId={prospect.id}
                letterAction={{
                  label: letterInProgress ? 'Brief läuft' : 'Brief verfassen',
                  title: letterButtonTitle,
                  disabled: letterInProgress || addressMissing,
                  onClick: () => setShowLetterComposer(true),
                }}
                emailAction={{
                  title: prospect.contactEmail
                    ? 'E-Mail über dein M365-Postfach senden'
                    : 'Keine E-Mail-Adresse hinterlegt',
                  disabled: !prospect.contactEmail,
                  onClick: () => setShowEmailComposer(true),
                }}
                linkedInAction={{
                  onClick: () => setShowLinkedInModal(true),
                }}
              />
            )}

            {/* PROFIL & SCORE */}
            {activeTab === 'profil' && (
              <div className="space-y-4">
                {companyProfile ? (
                  <div className="bg-indigo-50/30 border border-indigo-100 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-indigo-500" />
                      Unternehmensprofil (KI)
                    </h4>
                    <div className="text-xs text-gray-700 space-y-2">
                      {companyProfile.summary && (
                        <p><span className="font-medium text-gray-600">Zusammenfassung:</span> {companyProfile.summary}</p>
                      )}
                      {companyProfile.description && (
                        <p><span className="font-medium text-gray-600">Beschreibung:</span> {companyProfile.description}</p>
                      )}
                      {companyProfile.industry && (
                        <p><span className="font-medium text-gray-600">Branche:</span> {companyProfile.industry}</p>
                      )}
                      {Array.isArray(companyProfile.services) && companyProfile.services.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-600">Leistungen:</span>{' '}
                          {companyProfile.services.join(', ')}
                        </div>
                      )}
                      {Array.isArray(companyProfile.leadership) && companyProfile.leadership.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-600">Geschäftsführung:</span>
                          <ul className="list-disc list-inside mt-1">
                            {companyProfile.leadership.map((l: any, i: number) => (
                              <li key={i}>{l.name}{l.role ? ` – ${l.role}` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {companyProfile.employeeEstimate && (
                        <p><span className="font-medium text-gray-600">Geschätzte Mitarbeiter:</span> {companyProfile.employeeEstimate}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Noch kein Unternehmensprofil. Klicke „Profil" oben rechts für eine KI-Analyse der Website.</p>
                )}

                {scoringDetails && Object.keys(scoringDetails).length > 0 ? (
                  <div className="bg-green-50/30 border border-green-100 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      Score-Details ({prospect.icpScore} Punkte)
                    </h4>
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                      {JSON.stringify(scoringDetails, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Keine Score-Details vorhanden.</p>
                )}
              </div>
            )}
          </div>

          {/* ─── FOOTER / SAVE BAR ─── */}
          <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between gap-3 rounded-b-xl flex-shrink-0">
            <div className="text-caption text-gray-500">
              {isDirty ? (
                <span className="text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Ungespeicherte Änderungen
                </span>
              ) : (
                <span>Keine ausstehenden Änderungen</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="md" type="button" onClick={onClose}>
                Schließen
              </Button>
              <Button
                variant="primary"
                size="md"
                type="submit"
                disabled={!isDirty}
                loading={save.isPending}
                leadingIcon={!save.isPending ? <Save className="w-4 h-4" /> : undefined}
              >
                {save.isPending ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </div>
        </form>
      </div>
      {showLetterComposer && (
        <ProspectLetterModal
          prospect={prospect}
          onClose={() => setShowLetterComposer(false)}
          onSent={() => {
            setShowLetterComposer(false)
            setActionFeedback({ kind: 'success', text: 'Brief gesendet – Kundenschreiber-Auftrag angelegt.' })
            invalidateAll()
          }}
        />
      )}
      {showEmailComposer && (
        <ProspectEmailModal
          prospect={prospect}
          onClose={() => setShowEmailComposer(false)}
          onSent={() => {
            setShowEmailComposer(false)
            setActionFeedback({ kind: 'success', text: 'E-Mail gesendet – im Postfach „Gesendete Elemente" und im Aktivitäten-Verlauf dokumentiert.' })
            invalidateAll()
            queryClient.invalidateQueries({ queryKey: ['prospect-notes', prospect.id] })
          }}
        />
      )}
      {showLinkedInModal && (
        <ProspectLinkedInModal
          prospect={prospect}
          onClose={() => setShowLinkedInModal(false)}
        />
      )}
    </Modal>
  )
}

function ProspectLetterModal({ prospect, onClose, onSent }: {
  prospect: Prospect
  onClose: () => void
  onSent: () => void
}) {
  const a = useAnonymize()
  const contactName = [prospect.contactSalutation, prospect.contactFirstName, prospect.contactLastName]
    .filter(Boolean)
    .join(' ')
    .trim()
  const addressLine = [prospect.street, [prospect.zipCode, prospect.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      size="lg"
      title={(
        <span className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-brand-500" />
          Brief verfassen · {a.name(prospect.companyName)}
        </span>
      )}
    >
      <div className="space-y-3">
        <div className="text-xs text-gray-500 space-y-0.5">
          {contactName && <div>Empfänger: {a.name(contactName)}</div>}
          {addressLine && <div>Adresse: {addressLine}</div>}
        </div>
        <LetterComposer
          prospect={prospect}
          referenceCustomerId={prospect.referenceCustomerId}
          onSent={onSent}
          variant="modal"
        />
      </div>
    </Modal>
  )
}

// ─── Prospect Email Modal (M365-Versand mit prospect_notes-Doku) ──────────

function ProspectEmailModal({ prospect, onClose, onSent }: {
  prospect: Prospect
  onClose: () => void
  onSent: () => void
}) {
  const a = useAnonymize()
  const user = useAuthStore((s) => s.user)

  const contactName = [prospect.contactFirstName, prospect.contactLastName].filter(Boolean).join(' ').trim()
  const greeting = (() => {
    if (prospect.contactSalutation === 'Herr' && prospect.contactLastName) {
      return `Sehr geehrter Herr ${prospect.contactLastName},`
    }
    if (prospect.contactSalutation === 'Frau' && prospect.contactLastName) {
      return `Sehr geehrte Frau ${prospect.contactLastName},`
    }
    return 'Guten Tag,'
  })()

  const [to, setTo] = useState(prospect.contactEmail ?? '')
  const [cc, setCc] = useState('')
  const [showCc, setShowCc] = useState(false)
  const [subject, setSubject] = useState('')
  const [bodyText, setBodyText] = useState(`${greeting}\n\n\n\nMit freundlichen Grüßen\n${user?.name ?? ''}`.trim() + '\n')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [draftMeta, setDraftMeta] = useState<{
    addressForm: 'sie' | 'du'
    usedLetter: boolean
    usedEmailHistory: boolean
    usedCallNote: boolean
    warnings: string[]
  } | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)

  const draft = useMutation({
    mutationFn: async (): Promise<{
      subject: string
      body: string
      addressForm: 'sie' | 'du'
      usedLetter: boolean
      usedEmailHistory: boolean
      usedCallNote: boolean
      warnings: string[]
    }> => {
      const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/email-draft`, {
        senderName: user?.name ?? '',
      })
      return data
    },
    onSuccess: (data) => {
      setSubject(data.subject)
      setBodyText(data.body)
      setDraftMeta({
        addressForm: data.addressForm,
        usedLetter: data.usedLetter,
        usedEmailHistory: data.usedEmailHistory,
        usedCallNote: data.usedCallNote,
        warnings: data.warnings ?? [],
      })
      setDraftError(null)
    },
    onError: (err: unknown) => {
      const axErr = err as { response?: { data?: { error?: string } }; message?: string }
      setDraftError(axErr?.response?.data?.error ?? axErr?.message ?? 'KI-Vorschlag fehlgeschlagen.')
    },
  })

  const send = useMutation({
    mutationFn: async () => {
      const toList = to.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
      const ccList = cc.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
      const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/email`, {
        to: toList,
        cc: ccList,
        subject: subject.trim(),
        body: bodyText,
      })
      return data
    },
    onSuccess: () => {
      onSent()
    },
    onError: (err: unknown) => {
      const axErr = err as { response?: { data?: { error?: string; code?: string } } }
      setErrorCode(axErr?.response?.data?.code ?? null)
      setErrorMsg(axErr?.response?.data?.error ?? 'E-Mail-Versand fehlgeschlagen.')
    },
  })

  const canSend = to.trim().length > 0 && subject.trim().length > 0 && bodyText.trim().length > 0 && !send.isPending

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      size="lg"
      title={(
        <span className="flex items-center gap-2">
          <Send className="w-5 h-5 text-emerald-600" />
          E-Mail senden · {a.name(prospect.companyName)}
        </span>
      )}
    >
      <div className="space-y-3">
        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Mail className="w-3.5 h-3.5" />
            Versand über dein M365-Postfach
          </span>
          {user?.email && <span className="text-gray-400">· Absender: {user.email}</span>}
          {contactName && <span className="text-gray-400">· An: {a.name(contactName)}</span>}
        </div>

        <div className="flex items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2">
          <div className="text-xs text-gray-700 min-w-0">
            <div className="flex items-center gap-1.5 font-medium text-indigo-700">
              <Sparkles className="w-3.5 h-3.5" />
              KI-Textvorschlag
            </div>
            {draftMeta ? (
              <div className="mt-0.5 text-[11px] text-gray-600 flex flex-wrap gap-x-2 gap-y-0.5">
                <span>
                  Anrede:{' '}
                  <span className="font-medium text-gray-800">
                    {draftMeta.addressForm === 'du' ? 'Du (informell)' : 'Sie (formell)'}
                  </span>
                </span>
                <span className="text-gray-400">·</span>
                <span>
                  Bezug:{' '}
                  <span className={cn('inline-flex items-center gap-0.5', draftMeta.usedEmailHistory ? 'text-green-700' : 'text-gray-400')}>
                    {draftMeta.usedEmailHistory ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} frühere Mails
                  </span>
                </span>
                <span className={cn('inline-flex items-center gap-0.5', draftMeta.usedLetter ? 'text-green-700' : 'text-gray-400')}>
                  {draftMeta.usedLetter ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} Brief
                </span>
                <span className={cn('inline-flex items-center gap-0.5', draftMeta.usedCallNote ? 'text-green-700' : 'text-gray-400')}>
                  {draftMeta.usedCallNote ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} Anruf-Notiz
                </span>
              </div>
            ) : (
              <p className="mt-0.5 text-[11px] text-gray-500">
                Generiert Betreff und Text. Du/Sie wird automatisch aus der bisherigen Kommunikation übernommen.
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => draft.mutate()}
            loading={draft.isPending}
            disabled={draft.isPending || send.isPending}
            leadingIcon={!draft.isPending ? <Sparkles className="w-3.5 h-3.5" /> : undefined}
          >
            {draft.isPending ? 'Generiert…' : draftMeta ? 'Neu generieren' : 'Vorschlag erzeugen'}
          </Button>
        </div>

        {draftMeta && draftMeta.warnings.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {draftMeta.warnings.map((w, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700"
              >
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                {w}
              </span>
            ))}
          </div>
        )}

        {draftError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="break-words">{draftError}</span>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">An *</label>
          <Input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="empfaenger@firma.de (mehrere mit Komma trennen)"
          />
          {!showCc && (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="text-xs text-brand-600 hover:underline mt-1"
            >
              CC hinzufügen
            </button>
          )}
        </div>

        {showCc && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">CC</label>
            <Input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="cc@firma.de"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Betreff *</label>
          <Input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Betreff der Nachricht"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nachricht *</label>
          <Textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={12}
            hint="Reiner Text – Zeilenumbrüche werden im Versand übernommen. Wird automatisch im Aktivitäten-Verlauf dokumentiert."
          />
        </div>

        {send.isError && errorMsg && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium break-words">{errorMsg}</p>
              {errorCode === 'M365_NOT_CONNECTED' && (
                <p className="text-xs text-red-600 mt-1">
                  Tipp: Über „Microsoft anmelden" auf der Login-Seite neu einloggen, dann ist dein Postfach verbunden.
                </p>
              )}
              {errorCode && errorCode !== 'M365_NOT_CONNECTED' && (
                <p className="text-xs text-red-600 mt-1 font-mono">Code: {errorCode}</p>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={send.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => send.mutate()}
            disabled={!canSend}
            loading={send.isPending}
            leadingIcon={!send.isPending ? <Send className="w-4 h-4" /> : undefined}
          >
            {send.isPending ? 'Wird gesendet…' : 'Senden'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

type LinkedInMode = 'connect' | 'dm'

const LINKEDIN_DEFAULT_LIMIT: Record<LinkedInMode, number> = {
  connect: 200,
  dm: 600,
}

function ProspectLinkedInModal({ prospect, onClose }: {
  prospect: Prospect
  onClose: () => void
}) {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<LinkedInMode>('connect')
  const [message, setMessage] = useState<string | null>(null)
  const [charLimit, setCharLimit] = useState<number>(LINKEDIN_DEFAULT_LIMIT.connect)
  const [usedLetter, setUsedLetter] = useState(false)
  const [usedCallNote, setUsedCallNote] = useState(false)
  const [warnings, setWarnings] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [savingActivity, setSavingActivity] = useState(false)
  const [savedActivity, setSavedActivity] = useState(false)
  const [activityError, setActivityError] = useState<string | null>(null)

  // Latest mode/charLimit refs so generate() always reads the current value
  // without needing them in its dependency list (which would re-trigger the
  // initial useEffect on every char-limit edit).
  const modeRef = useRef(mode)
  const charLimitRef = useRef(charLimit)
  useEffect(() => { modeRef.current = mode }, [mode])
  useEffect(() => { charLimitRef.current = charLimit }, [charLimit])

  const generate = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    setCopied(false)
    setSavedActivity(false)
    setActivityError(null)
    try {
      const m = modeRef.current
      const limit = charLimitRef.current
      const { data } = await apiClient.post(
        `/acquisition/prospects/${prospect.id}/linkedin-message`,
        { mode: m, charLimit: limit },
      )
      setMessage(typeof data?.message === 'string' ? data.message : '')
      if (typeof data?.charLimit === 'number') setCharLimit(data.charLimit)
      setUsedLetter(Boolean(data?.usedLetter))
      setUsedCallNote(Boolean(data?.usedCallNote))
      setWarnings(Array.isArray(data?.warnings) ? data.warnings : [])
    } catch (err: unknown) {
      const axErr = err as {
        response?: { data?: { error?: string; message?: string } }
        message?: string
      }
      setError(
        axErr?.response?.data?.error ||
        axErr?.response?.data?.message ||
        axErr?.message ||
        'Unbekannter Fehler beim Generieren der LinkedIn-Nachricht.',
      )
    } finally {
      setIsLoading(false)
    }
  }, [prospect.id])

  // Initial generation on open and whenever the mode changes.
  // charLimit alone does NOT auto-regenerate (avoids burning AI calls while
  // the user types into the number field); user clicks "Neu generieren" instead.
  useEffect(() => {
    void generate()
  }, [generate, mode])

  // When mode flips, jump charLimit to that mode's default — the user can
  // override it manually afterwards.
  useEffect(() => {
    setCharLimit(LINKEDIN_DEFAULT_LIMIT[mode])
  }, [mode])

  async function handleCopy() {
    if (!message) return
    try {
      await navigator.clipboard.writeText(message)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Kopieren in die Zwischenablage fehlgeschlagen.')
    }
  }

  async function handleSaveActivity() {
    if (!message) return
    setSavingActivity(true)
    setActivityError(null)
    try {
      // Eigene Note-Kinds fuer LinkedIn (siehe schema.ts: linkedin_connect / linkedin_message),
      // damit der Eintrag in Timeline + Filter sauber als LinkedIn-Aktivitaet auftaucht
      // statt im "Sonstiges"-Topf.
      const noteKind: ProspectNoteKind = mode === 'connect' ? 'linkedin_connect' : 'linkedin_message'
      await apiClient.post(`/acquisition/prospects/${prospect.id}/notes`, {
        content: message,
        kind: noteKind,
        occurredAt: new Date().toISOString(),
      })
      queryClient.invalidateQueries({ queryKey: ['prospect-notes', prospect.id] })
      queryClient.invalidateQueries({ queryKey: ['prospect-history', prospect.id] })
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
      setSavedActivity(true)
      setTimeout(() => setSavedActivity(false), 2500)
    } catch (err: unknown) {
      const axErr = err as {
        response?: { data?: { error?: string; message?: string } }
        message?: string
      }
      setActivityError(
        axErr?.response?.data?.error ||
        axErr?.response?.data?.message ||
        axErr?.message ||
        'Konnte nicht als Aktivität gespeichert werden.',
      )
    } finally {
      setSavingActivity(false)
    }
  }

  const linkedInHref = prospect.contactLinkedinUrl ?? null
  const charCount = message?.length ?? 0
  const overLimit = charCount > charLimit
  const limitHint = mode === 'connect'
    ? 'Vernetzungs-Anfrage – LinkedIn schneidet bei 200 ab'
    : 'Direktnachricht – LinkedIn-Limit 8000'

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      size="md"
      title={(
        <span className="flex items-center gap-2">
          <Linkedin className="w-5 h-5 text-[#0A66C2]" />
          LinkedIn-Nachricht · {a.name(prospect.companyName)}
        </span>
      )}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-xs font-medium">
            <button
              type="button"
              onClick={() => setMode('connect')}
              disabled={isLoading}
              className={cn(
                'px-3 py-1.5 rounded-md transition-colors',
                mode === 'connect' ? 'bg-white text-[#0A66C2] shadow-sm' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              Vernetzen
            </button>
            <button
              type="button"
              onClick={() => setMode('dm')}
              disabled={isLoading}
              className={cn(
                'px-3 py-1.5 rounded-md transition-colors',
                mode === 'dm' ? 'bg-white text-[#0A66C2] shadow-sm' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              Direktnachricht
            </button>
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            Limit:
            <input
              type="number"
              min={60}
              max={8000}
              step={10}
              value={charLimit}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                if (Number.isFinite(v)) setCharLimit(Math.max(60, Math.min(8000, v)))
              }}
              disabled={isLoading}
              className="w-20 px-2 py-1 border border-gray-300 rounded-md text-xs bg-white"
              title="Zeichenlimit – wird beim nächsten Generieren als Vorgabe an die KI mitgegeben"
            />
            <span className="text-gray-400">Zeichen</span>
          </label>
        </div>
        <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            Bezug:
            <span className={cn('ml-1 inline-flex items-center gap-1', usedLetter ? 'text-green-700' : 'text-gray-400')}>
              {usedLetter ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              Brief
            </span>
            <span className={cn('ml-2 inline-flex items-center gap-1', usedCallNote ? 'text-green-700' : 'text-gray-400')}>
              {usedCallNote ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              Anruf-Notiz
            </span>
          </span>
          {linkedInHref && (
            <a
              href={linkedInHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#0A66C2] hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              Profil öffnen
            </a>
          )}
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <Loader2 className="w-7 h-7 animate-spin mb-2 text-brand-400" />
            <p className="text-sm font-medium">Nachricht wird generiert…</p>
            <p className="text-xs text-gray-300 mt-1">KI bezieht sich auf Brief & letzten Anruf</p>
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-red-700 mb-1">Generierung fehlgeschlagen</p>
            <p className="text-xs text-red-600 break-words">{error}</p>
          </div>
        )}

        {!isLoading && !error && message !== null && (
          <>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={Math.max(6, message.split('\n').length + 1)}
              className={cn(overLimit && 'border-red-400 focus:ring-red-300')}
              hint={(
                <span className={cn(overLimit && 'text-red-600 font-medium')}>
                  {charCount} / {charLimit} Zeichen · {limitHint}
                </span>
              )}
            />
            {warnings.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {warnings.map((w, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700"
                  >
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    {w}
                  </span>
                ))}
              </div>
            )}
            {activityError && (
              <div className="text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                {activityError}
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void generate()}
            disabled={isLoading}
            leadingIcon={<RotateCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />}
          >
            Neu generieren
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            {linkedInHref && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(linkedInHref, '_blank', 'noopener,noreferrer')}
                leadingIcon={<Linkedin className="w-4 h-4" />}
              >
                Auf LinkedIn öffnen
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSaveActivity}
              disabled={!message || isLoading || savingActivity}
              leadingIcon={savedActivity ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            >
              {savingActivity ? 'Speichert…' : savedActivity ? 'Gespeichert' : 'Als Aktivität speichern'}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCopy}
              disabled={!message || isLoading}
              leadingIcon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            >
              {copied ? 'Kopiert' : 'Text kopieren'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function DetailField({
  label, value, onChange, placeholder, type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
}) {
  return (
    <Input
      label={label}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function DetailActionButton({
  icon: Icon, label, color, onClick, pending, disabled, title,
}: {
  icon: typeof Search
  label: string
  color: 'purple' | 'amber' | 'blue' | 'indigo' | 'green' | 'red' | 'brand'
  onClick: () => void
  pending: boolean
  disabled?: boolean
  title?: string
}) {
  const colorMap: Record<string, string> = {
    purple: 'border-purple-300 text-purple-700 hover:bg-purple-50',
    amber: 'border-amber-300 text-amber-700 hover:bg-amber-50',
    blue: 'border-blue-300 text-blue-700 hover:bg-blue-50',
    indigo: 'border-indigo-300 text-indigo-700 hover:bg-indigo-50',
    green: 'border-green-300 text-green-700 hover:bg-green-50',
    red: 'border-red-300 text-red-700 hover:bg-red-50',
    brand: 'border-brand-300 text-brand-700 hover:bg-brand-50',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-lg transition-colors bg-white disabled:opacity-50 disabled:cursor-not-allowed',
        colorMap[color],
      )}
    >
      {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  )
}

// ─── Reject Prospect Modal ─────────────────────────────────────────────────

const REJECTION_CATEGORIES = [
  { value: 'wrong_industry', label: 'Falsche Branche' },
  { value: 'too_small', label: 'Zu klein' },
  { value: 'too_large', label: 'Zu gross' },
  { value: 'wrong_region', label: 'Falsche Region' },
  { value: 'already_customer', label: 'Bereits Kunde' },
  { value: 'bad_timing', label: 'Schlechtes Timing' },
  { value: 'competitor_customer', label: 'Wettbewerber-Kunde' },
  { value: 'other', label: 'Sonstiges' },
]

function RejectProspectModal({ prospect, onClose, onReject, isPending }: {
  prospect: Prospect
  onClose: () => void
  onReject: (data: { reason: string; reasonCategory: string; isPermanent?: boolean; recheckAfterDays?: number }) => void
  isPending: boolean
}) {
  const a = useAnonymize()
  const [reasonCategory, setReasonCategory] = useState('other')
  const [reason, setReason] = useState('')
  const [isPermanent, setIsPermanent] = useState(false)
  const [recheckDays, setRecheckDays] = useState(90)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onReject({
      reason: reason || REJECTION_CATEGORIES.find((c) => c.value === reasonCategory)?.label || 'Abgelehnt',
      reasonCategory,
      isPermanent,
      recheckAfterDays: isPermanent ? undefined : recheckDays,
    })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose() }}
      size="md"
      title={
        <span className="flex items-center gap-2">
          <ThumbsDown className="w-5 h-5 text-red-500" />
          Firma ablehnen
        </span>
      }
      description={`${a.name(prospect.companyName)} (${prospect.icpScore} Pt)`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-caption font-medium text-gray-600 mb-1">Grund *</label>
          <select
            value={reasonCategory}
            onChange={(e) => setReasonCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-small bg-white"
          >
            {REJECTION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <Textarea
          label="Kommentar"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Optionale Begruendung..."
        />

        <div className="bg-gray-50 rounded-lg p-3 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPermanent}
              onChange={(e) => setIsPermanent(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-small text-gray-700">Permanent ausschliessen</span>
          </label>

          {!isPermanent && (
            <div>
              <label className="block text-caption text-gray-500 mb-1">
                Erneut pruefen nach (Tagen):
              </label>
              <select
                value={recheckDays}
                onChange={(e) => setRecheckDays(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-small bg-white"
              >
                <option value={30}>30 Tage</option>
                <option value={60}>60 Tage</option>
                <option value={90}>90 Tage</option>
                <option value={180}>6 Monate</option>
                <option value={365}>1 Jahr</option>
              </select>
              <p className="text-caption text-gray-400 mt-1">
                Wird auch frueher vorgeschlagen wenn sich die Bewertungsgrundlage aendert (Score-Delta &gt; 15 Punkte).
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" size="md" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button variant="danger" size="md" type="submit" loading={isPending}>
            {isPending ? 'Wird abgelehnt...' : 'Ablehnen'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Campaigns Tab ────────────────────────────────────────────────────────

const COST_PER_LETTER = 3.95

interface Campaign {
  id: string
  name: string
  description: string | null
  campaignType?: 'standard' | 'referenz' | 'stellenanzeige'
  referenceCustomerId: string | null
  radiusKm: number | null
  status: string
  minIcpScore: number
  totalLettersSent: number
  totalResponses: number
  totalMeetings: number
  createdAt: string
}

const CAMPAIGN_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  standard: { label: 'Standard', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  referenz: { label: 'Referenz', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  stellenanzeige: { label: 'Stellenanzeige', color: 'bg-orange-50 text-orange-700 border-orange-200' },
}

interface CampaignProspect extends Prospect {
  distanceKm?: number
}

interface RefCustomer {
  id: string
  companyName: string
  industry: string | null
  zipCode: string | null
  city: string | null
  services: string[]
  isActive: boolean
}

function CampaignsTab() {
  const queryClient = useQueryClient()

  // Views: null = list, Campaign = detail
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ referenceCustomerId: '', radiusKm: 5, campaignType: 'referenz' as 'standard' | 'referenz' | 'stellenanzeige' })
  const [creating, setCreating] = useState(false)
  const [message, setMessage] = useState('')

  // Campaign detail
  const [campaignProspects, setCampaignProspects] = useState<CampaignProspect[]>([])
  const [_excluded, setExcluded] = useState<Set<string>>(new Set())
  const [loadingProspects, setLoadingProspects] = useState(false)
  const [refCustomerName, setRefCustomerName] = useState<string | null>(null)
  const [_expandedProspect, setExpandedProspect] = useState<string | null>(null)

  const [_letters, setLetters] = useState<Record<string, { text: string; id: string }>>({})

  // Discovery
  const [discovering, setDiscovering] = useState(false)
  const [discoveryLog, setDiscoveryLog] = useState<Array<{ step: string; detail: string; status: string }>>([])
  const discoveryPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const campaigns = useQuery<Campaign[]>({
    queryKey: ['acquisition', 'campaigns'],
    queryFn: () => apiClient.get('/acquisition/campaigns').then((r) => r.data),
    retry: false,
  })

  const refCustomers = useQuery<RefCustomer[]>({
    queryKey: ['acquisition', 'reference-customers'],
    queryFn: () => apiClient.get('/acquisition/reference-customers').then((r) => r.data),
    retry: false,
  })

  const activeRefCustomers = (refCustomers.data || []).filter((r) => r.isActive && r.zipCode)


  const createCampaign = async () => {
    if (createForm.campaignType === 'referenz' && !createForm.referenceCustomerId) { setMessage('Bitte Referenzkunden waehlen.'); return }
    setCreating(true); setMessage('')
    try {
      let data: Campaign
      if (createForm.campaignType === 'referenz') {
        const res = await apiClient.post('/acquisition/campaigns/reference', { ...createForm })
        data = res.data
      } else {
        const name = createForm.campaignType === 'stellenanzeige' ? 'Stellenanzeigen-Kampagne' : 'Standard-Kampagne'
        const res = await apiClient.post('/acquisition/campaigns', { name, campaignType: createForm.campaignType, status: 'active' })
        data = res.data
      }
      queryClient.invalidateQueries({ queryKey: ['acquisition', 'campaigns'] })
      setShowCreate(false); setCreateForm({ referenceCustomerId: '', radiusKm: 5, campaignType: 'referenz' })
      openCampaign(data) // auto-open new campaign
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler'
      setMessage(`Fehler: ${msg}`)
    }
    setCreating(false)
  }

  const deleteCampaign = async (id: string, name: string) => {
    if (!confirm(`Kampagne "${name}" wirklich loeschen?`)) return
    try {
      await apiClient.delete(`/acquisition/campaigns/${id}`)
      queryClient.invalidateQueries({ queryKey: ['acquisition', 'campaigns'] })
      if (selectedCampaign?.id === id) { setSelectedCampaign(null); setCampaignProspects([]) }
    } catch { setMessage('Fehler beim Loeschen.') }
  }

  const openCampaign = async (campaign: Campaign) => {
    setSelectedCampaign(campaign); setLoadingProspects(true)
    setCampaignProspects([]); setExcluded(new Set()); setLetters({}); setExpandedProspect(null); setDiscoveryLog([])
    try {
      const { data } = await apiClient.get(`/acquisition/campaigns/${campaign.id}/prospects`)
      setCampaignProspects(data.prospects || [])
      setRefCustomerName(data.referenceCustomer?.companyName || null)
    } catch { /* ignore */ }
    setLoadingProspects(false)
  }

  const triggerDiscovery = async () => {
    if (!selectedCampaign) return
    setDiscovering(true); setDiscoveryLog([])
    try {
      await apiClient.post(`/acquisition/campaigns/${selectedCampaign.id}/discover`)
      if (discoveryPollRef.current) clearInterval(discoveryPollRef.current)
      discoveryPollRef.current = setInterval(async () => {
        try {
          const { data } = await apiClient.get('/acquisition/discovery/status')
          if (data.log?.length) setDiscoveryLog(data.log)
          if (data.done) {
            setDiscovering(false)
            setMessage(data.message || 'Suche abgeschlossen')
            if (discoveryPollRef.current) { clearInterval(discoveryPollRef.current); discoveryPollRef.current = null }
            queryClient.invalidateQueries({ queryKey: ['acquisition'] })
            if (selectedCampaign) openCampaign(selectedCampaign)
            setTimeout(() => setMessage(''), 10000)
          }
        } catch { /* ignore */ }
      }, 2000)
      setTimeout(() => { if (discoveryPollRef.current) { clearInterval(discoveryPollRef.current); discoveryPollRef.current = null; setDiscovering(false) } }, 600000)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler'
      setMessage(msg); setDiscovering(false)
    }
  }

  // ── Campaign Detail View — uses same LetterReviewCard as Tagesansicht ──
  if (selectedCampaign) {
    return (
      <div className="space-y-4">
        {/* Back + delete */}
        <div className="flex items-center justify-between">
          <button onClick={() => { setSelectedCampaign(null); setDiscoveryLog([]) }} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"><ChevronLeft className="w-4 h-4" /> Zurueck</button>
          <button onClick={() => deleteCampaign(selectedCampaign.id, selectedCampaign.name)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /> Loeschen</button>
        </div>

        {/* Campaign header */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{selectedCampaign.name}</h2>
              <p className="text-sm text-gray-500">{selectedCampaign.description}</p>
              {campaignProspects.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">{campaignProspects.length} Zielkunden | Kosten ca. {(campaignProspects.length * COST_PER_LETTER).toFixed(2).replace('.', ',')} EUR ({COST_PER_LETTER.toFixed(2).replace('.', ',')} EUR/Brief)</p>
              )}
            </div>
            <button onClick={triggerDiscovery} disabled={discovering} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50">
              {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {discovering ? 'Suche laeuft...' : 'Zielkunden suchen'}
            </button>
          </div>
        </div>

        {message && <div className={cn('px-4 py-2 rounded-lg text-sm', message.startsWith('Fehler') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200')}>{message}</div>}

        {/* Discovery progress */}
        {discoveryLog.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 max-h-40 overflow-y-auto">
            <p className="text-xs font-medium text-blue-800 mb-1">Fortschritt:</p>
            {discoveryLog.map((entry, i) => (
              <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                <span className={cn('font-medium flex-shrink-0', entry.status === 'error' ? 'text-red-600' : entry.status === 'success' ? 'text-green-600' : entry.status === 'warning' ? 'text-amber-600' : 'text-blue-600')}>{entry.step}:</span>
                <span className="text-gray-700">{entry.detail}</span>
              </div>
            ))}
          </div>
        )}

        {/* Same LetterReviewCard as Tagesansicht */}
        {loadingProspects ? (
          <div className="bg-white border border-gray-200 rounded-xl flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" /><span className="ml-2 text-sm text-gray-500">Lade Zielkunden...</span>
          </div>
        ) : campaignProspects.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center">
            <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Noch keine Zielkunden{refCustomerName ? ` im Umkreis von ${refCustomerName}` : ''}.</p>
            <p className="text-xs text-gray-400 mt-1">Klicke "Zielkunden suchen" um Firmen zu finden.</p>
          </div>
        ) : (
          <LetterReviewCard
            prospects={campaignProspects}
            referenceCustomerId={selectedCampaign.referenceCustomerId || undefined}
            onStatusChange={(id, status) => apiClient.patch(`/acquisition/prospects/${id}`, { status }).catch(() => {})}
            onReject={(prospect) => apiClient.post(`/acquisition/prospects/${prospect.id}/reject`, { reason: 'Aus Kampagne entfernt', reasonCategory: 'other', isPermanent: false, recheckAfterDays: 90 }).catch(() => {})}
          />
        )}
      </div>
    )
  }

  // ── Campaign List View ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Kampagnen: Gezielt Firmen anschreiben — per Referenzkunde, Stellenanzeigen oder Standard.</p>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-lg transition-colors"><Plus className="w-4 h-4" /> Neue Kampagne</button>
      </div>
      {message && <div className={cn('px-4 py-2 rounded-lg text-sm', message.startsWith('Fehler') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200')}>{message}</div>}
      {showCreate && (
        <div className="bg-white border border-brand-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-gray-900">Neue Kampagne</h3>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Kampagnen-Typ</label>
            <div className="flex gap-2">
              {([
                { value: 'referenz', label: 'Referenzkunde', desc: 'Firmen im Umkreis eines Referenzkunden' },
                { value: 'stellenanzeige', label: 'Stellenanzeige', desc: 'Firmen die IT-Personal suchen' },
                { value: 'standard', label: 'Standard', desc: 'Allgemeine Akquise' },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCreateForm((f) => ({ ...f, campaignType: opt.value }))}
                  className={cn(
                    'flex-1 p-3 rounded-lg border text-left transition-all',
                    createForm.campaignType === opt.value
                      ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                      : 'border-gray-200 hover:border-gray-300',
                  )}
                >
                  <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
          {createForm.campaignType === 'referenz' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Referenzkunde *</label>
                <select value={createForm.referenceCustomerId} onChange={(e) => setCreateForm((f) => ({ ...f, referenceCustomerId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">Bitte waehlen...</option>
                  {activeRefCustomers.map((r) => (<option key={r.id} value={r.id}>{r.companyName} {'\u2014'} {r.zipCode} {r.city || ''} {r.industry ? `(${r.industry})` : ''}</option>))}
                </select>
                {activeRefCustomers.length === 0 && <p className="text-xs text-amber-600 mt-1">Keine Referenzkunden mit PLZ. Unter Einstellungen &gt; Referenzkunden anlegen.</p>}
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Umkreis (km)</label>
                <input type="number" min={1} max={100} value={createForm.radiusKm} onChange={(e) => setCreateForm((f) => ({ ...f, radiusKm: parseInt(e.target.value) || 5 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
          )}
          {createForm.campaignType === 'stellenanzeige' && (
            <p className="text-xs text-gray-500 bg-orange-50 border border-orange-200 rounded-lg p-3">Filtert automatisch auf Zielkunden aus Stellenanzeigen. Erfasse Stellenanzeigen-URLs ueber die Schnellerfassung (+).</p>
          )}
          {createForm.campaignType === 'standard' && (
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">Allgemeine Akquise-Kampagne. Alle qualifizierten Zielkunden sind berechtigt.</p>
          )}
          <div className="flex gap-2">
            <button onClick={createCampaign} disabled={creating || (createForm.campaignType === 'referenz' && !createForm.referenceCustomerId)} className="px-4 py-2 bg-brand-500 text-white text-sm rounded-lg hover:bg-brand-600 disabled:opacity-50">{creating ? 'Wird erstellt...' : 'Kampagne erstellen'}</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50">Abbrechen</button>
          </div>
        </div>
      )}
      {(campaigns.data || []).length === 0 && !showCreate && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <Target className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Noch keine Kampagnen.</p>
          <p className="text-xs text-gray-400 mt-1">Erstelle eine Kampagne mit "Neue Kampagne" oben rechts.</p>
        </div>
      )}
      {(campaigns.data || []).map((c) => (
        <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-brand-300 transition-colors group">
          <div className="flex items-center justify-between">
            <div className="flex-1 cursor-pointer" onClick={() => openCampaign(c)}>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900">{c.name}</p>
                {c.campaignType && CAMPAIGN_TYPE_LABELS[c.campaignType] && (
                  <span className={cn('px-1.5 py-0.5 rounded border text-[10px] font-medium', CAMPAIGN_TYPE_LABELS[c.campaignType]!.color)}>
                    {CAMPAIGN_TYPE_LABELS[c.campaignType]!.label}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {c.radiusKm && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.radiusKm}km</span>}
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.totalLettersSent}</span>
              <span className={cn('px-2 py-0.5 rounded-full font-medium', c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>{c.status === 'active' ? 'Aktiv' : 'Entwurf'}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteCampaign(c.id, c.name) }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity" title="Loeschen"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Inline Settings Tab ───────────────────────────────────────────────────

function InlineSettingsTab() {
  const queryClient = useQueryClient()
  const [activeSection, setActiveSection] = useState<'scoring' | 'geo' | 'painpoints' | 'references'>('geo')
  const [message, setMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [recalculating, setRecalculating] = useState(false)

  const [weights, setWeights] = useState({
    weightIndustryFit: 25, weightCompanySize: 15, weightRegionMatch: 15,
    weightDecisionMaker: 15, weightTriggerEvents: 20, weightContactHistory: 10,
  })
  const [_targetIndustries, setTargetIndustries] = useState<string[]>([])
  const [excludedIndustries, setExcludedIndustries] = useState<string[]>([])
  const [newIndustry, setNewIndustry] = useState('')
  const [triggerScores, setTriggerScores] = useState<Record<string, number>>({})
  const [newTrigger, setNewTrigger] = useState({ key: '', score: 20 })

  const [zones, setZones] = useState<Array<{ id: string; name: string; type: string; plzPrefixes: unknown; centerPlz: string | null; radiusKm: number | null }>>([])
  const [showAddZone, setShowAddZone] = useState(false)
  const [newZone, setNewZone] = useState({ name: '', type: 'plz_prefix' as 'plz_prefix' | 'radius', plzPrefixes: '', centerPlz: '', radiusKm: 50 })
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null)
  const [editZone, setEditZone] = useState({ name: '', plzPrefixes: '', centerPlz: '', radiusKm: 50 })

  const [painPoints, setPainPoints] = useState<Array<{ id: string; industry: string; title: string; description: string; isActive: boolean }>>([])
  const [generating, setGenerating] = useState(false)

  // Reference customers
  const [refCustomers, setRefCustomers] = useState<Array<{ id: string; companyName: string; industry: string | null; zipCode: string | null; city: string | null; services: string[]; projectHighlight: string | null; isActive: boolean }>>([])
  const [showAddRef, setShowAddRef] = useState(false)
  const [newRef, setNewRef] = useState({ companyName: '', industry: '', zipCode: '', city: '', services: '', projectHighlight: '' })
  const [editingRefId, setEditingRefId] = useState<string | null>(null)
  const [editRef, setEditRef] = useState({ companyName: '', industry: '', zipCode: '', city: '', services: '', projectHighlight: '' })

  useEffect(() => {
    apiClient.get('/acquisition/scoring-config').then(({ data }) => {
      if (data) {
        setWeights({ weightIndustryFit: data.weightIndustryFit ?? 25, weightCompanySize: data.weightCompanySize ?? 15, weightRegionMatch: data.weightRegionMatch ?? 15, weightDecisionMaker: data.weightDecisionMaker ?? 15, weightTriggerEvents: data.weightTriggerEvents ?? 20, weightContactHistory: data.weightContactHistory ?? 10 })
        setTriggerScores(data.triggerEventScores ?? {})
        let industries = data.targetIndustries ?? []
        if (typeof industries === 'string') { try { industries = JSON.parse(industries) } catch { industries = [] } }
        if (!Array.isArray(industries)) industries = []
        setTargetIndustries(industries.filter((t: { score: number }) => t.score > 0).map((t: { industry: string }) => t.industry))
        setExcludedIndustries(industries.filter((t: { score: number }) => t.score === 0).map((t: { industry: string }) => t.industry))
      }
    }).catch(() => {})
    apiClient.get('/acquisition/geo-zones').then(({ data }) => setZones(data ?? [])).catch(() => {})
    apiClient.get('/acquisition/pain-points').then(({ data }) => setPainPoints(data ?? [])).catch(() => {})
    apiClient.get('/acquisition/reference-customers').then(({ data }) => setRefCustomers((data ?? []).map((r: Record<string, unknown>) => ({ ...r, services: Array.isArray(r.services) ? r.services : [] })))).catch(() => {})
  }, [])

  const totalWeight = Object.values(weights).reduce((s, v) => s + v, 0)
  function parsePlz(val: unknown): string[] { if (Array.isArray(val)) return val.filter(Boolean); if (typeof val === 'string') { try { const p = JSON.parse(val); if (Array.isArray(p)) return p.filter(Boolean) } catch { /* ignore parse errors */ } } return [] }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {([{ key: 'geo' as const, label: 'Geo-Zonen', icon: MapPin }, { key: 'references' as const, label: 'Referenzkunden', icon: Building2 }, { key: 'scoring' as const, label: 'ICP-Scoring', icon: Target }, { key: 'painpoints' as const, label: 'Pain Points', icon: AlertTriangle }]).map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => { setActiveSection(key); setMessage('') }} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors', activeSection === key ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200' : 'text-gray-600 hover:bg-gray-100')}><Icon className="w-3.5 h-3.5" />{label}</button>
        ))}
      </div>
      {message && <div className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg p-3">{message}</div>}

      {activeSection === 'geo' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <p className="text-xs text-gray-500">Zielgebiete fuer die automatische Firmensuche.</p>
          {zones.map((zone) => { const pf = parsePlz(zone.plzPrefixes); return (
            <div key={zone.id} className="bg-gray-50 rounded-lg p-3">
              {editingZoneId === zone.id ? (
                <div className="space-y-2">
                  <input type="text" value={editZone.name} onChange={(e) => setEditZone((z) => ({ ...z, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  {zone.type === 'plz_prefix' ? <input type="text" value={editZone.plzPrefixes} onChange={(e) => setEditZone((z) => ({ ...z, plzPrefixes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /> : <div className="grid grid-cols-2 gap-2"><input type="text" value={editZone.centerPlz} onChange={(e) => setEditZone((z) => ({ ...z, centerPlz: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" /><div className="flex items-center gap-2"><input type="range" min={10} max={200} value={editZone.radiusKm} onChange={(e) => setEditZone((z) => ({ ...z, radiusKm: Number(e.target.value) }))} className="flex-1" /><span className="text-sm font-mono">{editZone.radiusKm}km</span></div></div>}
                  <div className="flex gap-2">
                    <button onClick={async () => { const body: Record<string, unknown> = { name: editZone.name }; if (zone.type === 'plz_prefix') body.plzPrefixes = editZone.plzPrefixes.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean); else { body.centerPlz = editZone.centerPlz; body.radiusKm = editZone.radiusKm }; const { data } = await apiClient.patch(`/acquisition/geo-zones/${zone.id}`, body); setZones((p) => p.map((z) => z.id === zone.id ? { ...z, ...data } : z)); setEditingZoneId(null) }} className="px-3 py-1.5 bg-brand-500 text-white text-xs rounded-lg">Speichern</button>
                    <button onClick={() => setEditingZoneId(null)} className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg">Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="cursor-pointer flex-1" onClick={() => { setEditingZoneId(zone.id); setEditZone({ name: zone.name, plzPrefixes: pf.join(', '), centerPlz: zone.centerPlz || '', radiusKm: zone.radiusKm || 50 }) }}>
                    <p className="text-sm font-medium text-gray-900">{zone.name}</p>
                    <p className="text-xs text-gray-500">{zone.type === 'plz_prefix' ? `PLZ: ${pf.join(', ') || '(keine)'}` : `${zone.radiusKm}km um ${zone.centerPlz}`}</p>
                  </div>
                  <button onClick={async () => { await apiClient.delete(`/acquisition/geo-zones/${zone.id}`); setZones((p) => p.filter((z) => z.id !== zone.id)) }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          )})}
          {showAddZone ? (
            <div className="border border-brand-200 bg-brand-50 rounded-lg p-3 space-y-2">
              <input type="text" placeholder="Name" value={newZone.name} onChange={(e) => setNewZone((z) => ({ ...z, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="radio" checked={newZone.type === 'plz_prefix'} onChange={() => setNewZone((z) => ({ ...z, type: 'plz_prefix' }))} /> PLZ-Bereich</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" checked={newZone.type === 'radius'} onChange={() => setNewZone((z) => ({ ...z, type: 'radius' }))} /> Umkreis</label>
              </div>
              {newZone.type === 'plz_prefix' ? <input type="text" placeholder="PLZ (z.B. 40, 41, 42; 47; 50)" value={newZone.plzPrefixes} onChange={(e) => setNewZone((z) => ({ ...z, plzPrefixes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /> : <div className="grid grid-cols-2 gap-2"><input type="text" placeholder="PLZ" value={newZone.centerPlz} onChange={(e) => setNewZone((z) => ({ ...z, centerPlz: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" /><div className="flex items-center gap-2"><input type="range" min={10} max={200} value={newZone.radiusKm} onChange={(e) => setNewZone((z) => ({ ...z, radiusKm: Number(e.target.value) }))} className="flex-1" /><span className="text-sm font-mono">{newZone.radiusKm}km</span></div></div>}
              <div className="flex gap-2">
                <button onClick={async () => { try { const body = newZone.type === 'plz_prefix' ? { name: newZone.name, type: 'plz_prefix', plzPrefixes: newZone.plzPrefixes.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean) } : { name: newZone.name, type: 'radius', centerPlz: newZone.centerPlz, radiusKm: newZone.radiusKm }; const { data } = await apiClient.post('/acquisition/geo-zones', body); setZones((p) => [data, ...p]); setShowAddZone(false); setNewZone({ name: '', type: 'plz_prefix', plzPrefixes: '', centerPlz: '', radiusKm: 50 }) } catch { setMessage('Fehler beim Erstellen.') } }} className="px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg">Erstellen</button>
                <button onClick={() => setShowAddZone(false)} className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg">Abbrechen</button>
              </div>
            </div>
          ) : <button onClick={() => setShowAddZone(true)} className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 w-full justify-center"><Plus className="w-4 h-4" /> Neue Geo-Zone</button>}
        </div>
      )}

      {activeSection === 'scoring' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Scoring-Gewichte</h3>
            <p className="text-xs text-gray-500 mb-3">Summe: <span className={totalWeight === 100 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{totalWeight}/100</span></p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {([['weightIndustryFit', 'Branche'], ['weightCompanySize', 'Groesse'], ['weightRegionMatch', 'Region'], ['weightDecisionMaker', 'Entscheider'], ['weightTriggerEvents', 'Trigger'], ['weightContactHistory', 'Historie']] as const).map(([k, l]) => (
                <div key={k}><label className="text-xs font-medium text-gray-600">{l}</label><div className="flex items-center gap-2 mt-1"><input type="range" min={0} max={50} value={weights[k]} onChange={(e) => setWeights((w) => ({ ...w, [k]: Number(e.target.value) }))} className="flex-1" /><span className="text-sm font-mono w-8 text-right">{weights[k]}%</span></div></div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Branchenkatalog</h3>
            <p className="text-xs text-gray-500 mb-3">Alle Branchen sind aktiv. Klicke um eine Branche <strong>auszuschliessen</strong>. Ausgeschlossene Branchen werden nicht gesucht.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                'Steuerberatung','Rechtsanwalt','Wirtschaftsprüfung','Unternehmensberatung',
                'Arztpraxis','Zahnarzt','Apotheke','Physiotherapie','Pflegedienst','Tierarzt',
                'Handwerk','Elektrotechnik','Sanitär/Heizung','Malerbetrieb','Schreinerei','Metallbau','Dachdecker',
                'Ingenieurbüro','Architekturbüro','Vermessungsbüro',
                'IT-Dienstleistung','Softwareentwicklung','Webdesign',
                'Immobilienverwaltung','Immobilienmakler',
                'Großhandel','Einzelhandel','Fachhandel',
                'Versicherung','Finanzberatung','Bankfiliale',
                'Autohaus','KFZ-Werkstatt',
                'Logistik','Spedition','Kurierdienst',
                'Gastronomie','Hotel','Bäckerei','Metzgerei',
                'Fitnessstudio','Sportverein',
                'Druckerei','Werbeagentur','Medienproduktion',
                'Bildungsträger','Fahrschule','Musikschule',
                'Reinigungsdienst','Sicherheitsdienst','Gebäudemanagement',
                'Optiker','Hörakustiker',
                'Reisebüro','Eventmanagement',
                'Verlag','Übersetzungsbüro',
                'Textilreinigung','Friseursalon','Kosmetikstudio',
              ].map((ind) => {
                const isExcluded = excludedIndustries.includes(ind)
                return (
                  <button key={ind} onClick={() => setExcludedIndustries((prev) => isExcluded ? prev.filter((e) => e !== ind) : [...prev, ind])}
                    className={cn('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                      isExcluded ? 'bg-red-50 text-red-400 border-red-200 line-through' : 'bg-green-50 text-green-700 border-green-200'
                    )}>
                    {ind}
                  </button>
                )
              })}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="Eigene Branche hinzufuegen..." value={newIndustry} onChange={(e) => setNewIndustry(e.target.value)} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
              <button onClick={() => { if (newIndustry) { setExcludedIndustries((e) => e.filter((x) => x !== newIndustry)); setNewIndustry('') } }} className="px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg hover:bg-brand-600"><Plus className="w-4 h-4" /></button>
            </div>
            {excludedIndustries.length > 0 && (
              <p className="text-xs text-red-500 mt-2">{excludedIndustries.length} Branche(n) ausgeschlossen: {excludedIndustries.join(', ')}</p>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Trigger-Event-Keywords</h3>
            <div className="space-y-1 mb-3">{Object.entries(triggerScores).map(([k, s]) => (<div key={k} className="flex items-center gap-2 text-sm"><code className="flex-1 bg-gray-50 px-2 py-0.5 rounded text-xs">{k}</code><span className="text-gray-500">+{s}</span><button onClick={() => setTriggerScores((ss) => { const n = { ...ss }; delete n[k]; return n })} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button></div>))}</div>
            <div className="flex gap-2"><input type="text" placeholder="z.B. nis2" value={newTrigger.key} onChange={(e) => setNewTrigger((n) => ({ ...n, key: e.target.value }))} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" /><input type="number" min={0} max={100} value={newTrigger.score} onChange={(e) => setNewTrigger((n) => ({ ...n, score: Number(e.target.value) }))} className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" /><button onClick={() => { if (newTrigger.key) { setTriggerScores((s) => ({ ...s, [newTrigger.key]: newTrigger.score })); setNewTrigger({ key: '', score: 20 }) } }} className="px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg"><Plus className="w-4 h-4" /></button></div>
          </div>
          <div className="flex gap-3">
            <button onClick={async () => { setSaving(true); setMessage(''); try { await apiClient.post('/acquisition/scoring-config', { ...weights, targetIndustries: excludedIndustries.map((ind) => ({ industry: ind, score: 0 })), triggerEventScores: triggerScores, targetRoles: [] }); setMessage('Konfiguration gespeichert.') } catch { setMessage('Fehler beim Speichern.') } finally { setSaving(false) } }} disabled={saving || totalWeight !== 100} className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Speichern
            </button>
            <button onClick={async () => { setRecalculating(true); setMessage(''); try { const { data } = await apiClient.post('/acquisition/scoring-config/recalculate'); setMessage(`${data.updated} Zielkunden neu bewertet.`); queryClient.invalidateQueries({ queryKey: ['acquisition'] }) } catch (err: unknown) { const axErr = err as { response?: { data?: { error?: string } } }; setMessage(axErr?.response?.data?.error || 'Fehler bei Neuberechnung.') } finally { setRecalculating(false) } }} disabled={recalculating} className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {recalculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Scores neu berechnen
            </button>
          </div>
        </div>
      )}

      {activeSection === 'painpoints' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">KI-generiert, werden in Briefen eingesetzt.</p>
            <button onClick={async () => { setGenerating(true); setMessage(''); try { const { data } = await apiClient.post('/acquisition/pain-points/generate'); setMessage(`${data.generated} Pain Points generiert.`); const pp = await apiClient.get('/acquisition/pain-points'); setPainPoints(pp.data ?? []) } catch { setMessage('Fehler.') } finally { setGenerating(false) } }} disabled={generating} className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white text-xs rounded-lg disabled:opacity-50">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} KI-generieren
            </button>
          </div>
          {painPoints.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Keine Pain Points. Klicke "KI-generieren".</p>}
          {painPoints.map((pp) => (
            <div key={pp.id} className={cn('flex items-start gap-3 rounded-lg p-3', pp.isActive ? 'bg-gray-50' : 'bg-gray-50/50 opacity-50')}>
              <button onClick={async () => { await apiClient.patch(`/acquisition/pain-points/${pp.id}`, { isActive: !pp.isActive }); setPainPoints((prev) => prev.map((p) => p.id === pp.id ? { ...p, isActive: !pp.isActive } : p)) }} className={cn('mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0', pp.isActive ? 'border-green-500 bg-green-500' : 'border-gray-300')}>{pp.isActive && <span className="text-white text-xs">✓</span>}</button>
              <div className="flex-1"><div className="flex items-center gap-2"><span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{pp.industry === '*' ? 'Alle' : pp.industry}</span><p className="text-sm font-medium text-gray-900">{pp.title}</p></div><p className="text-xs text-gray-500 mt-1 line-clamp-2">{pp.description}</p></div>
            </div>
          ))}
        </div>
      )}

      {activeSection === 'references' && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <p className="text-xs text-gray-500">Referenzkunden werden automatisch in Briefen erwaehnt, wenn sie regional und thematisch zum Empfaenger passen.</p>
          {refCustomers.map((ref) => (
            <div key={ref.id} className={cn('rounded-lg p-3', ref.isActive ? 'bg-gray-50' : 'bg-gray-50/50 opacity-50')}>
              {editingRefId === ref.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="Firmenname *" value={editRef.companyName} onChange={(e) => setEditRef((r) => ({ ...r, companyName: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    <input type="text" placeholder="Branche" value={editRef.industry} onChange={(e) => setEditRef((r) => ({ ...r, industry: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="PLZ" value={editRef.zipCode} onChange={(e) => setEditRef((r) => ({ ...r, zipCode: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    <input type="text" placeholder="Stadt" value={editRef.city} onChange={(e) => setEditRef((r) => ({ ...r, city: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <input type="text" placeholder="Leistungen (kommagetrennt, z.B. Netzwerk, WLAN, Videoueberwachung)" value={editRef.services} onChange={(e) => setEditRef((r) => ({ ...r, services: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <textarea placeholder="Projekt-Highlight (was wurde umgesetzt?)" value={editRef.projectHighlight} onChange={(e) => setEditRef((r) => ({ ...r, projectHighlight: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <div className="flex gap-2">
                    <button onClick={async () => { const body = { companyName: editRef.companyName, industry: editRef.industry || null, zipCode: editRef.zipCode || null, city: editRef.city || null, services: editRef.services.split(/[,;]+/).map((s) => s.trim()).filter(Boolean), projectHighlight: editRef.projectHighlight || null }; const { data } = await apiClient.patch(`/acquisition/reference-customers/${ref.id}`, body); setRefCustomers((p) => p.map((r) => r.id === ref.id ? { ...r, ...data, services: Array.isArray(data.services) ? data.services : [] } : r)); setEditingRefId(null) }} className="px-3 py-1.5 bg-brand-500 text-white text-xs rounded-lg">Speichern</button>
                    <button onClick={() => setEditingRefId(null)} className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg">Abbrechen</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 cursor-pointer" onClick={() => { setEditingRefId(ref.id); setEditRef({ companyName: ref.companyName, industry: ref.industry || '', zipCode: ref.zipCode || '', city: ref.city || '', services: (ref.services || []).join(', '), projectHighlight: ref.projectHighlight || '' }) }}>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{ref.companyName}</p>
                      {ref.city && <span className="text-xs text-gray-500">{ref.zipCode ? `${ref.zipCode} ` : ''}{ref.city}</span>}
                    </div>
                    {ref.industry && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mt-1 inline-block">{ref.industry}</span>}
                    {ref.services.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{ref.services.map((s, i) => <span key={i} className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{s}</span>)}</div>}
                    {ref.projectHighlight && <p className="text-xs text-gray-500 mt-1">{ref.projectHighlight}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={async () => { await apiClient.patch(`/acquisition/reference-customers/${ref.id}`, { isActive: !ref.isActive }); setRefCustomers((p) => p.map((r) => r.id === ref.id ? { ...r, isActive: !ref.isActive } : r)) }} className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', ref.isActive ? 'border-green-500 bg-green-500' : 'border-gray-300')}>{ref.isActive && <span className="text-white text-xs">✓</span>}</button>
                    <button onClick={async () => { await apiClient.delete(`/acquisition/reference-customers/${ref.id}`); setRefCustomers((p) => p.filter((r) => r.id !== ref.id)) }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {refCustomers.length === 0 && !showAddRef && <p className="text-sm text-gray-400 text-center py-4">Keine Referenzkunden. Fuege welche hinzu, damit die KI sie in Briefen erwaehnen kann.</p>}
          {showAddRef ? (
            <div className="border border-brand-200 bg-brand-50 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Firmenname *" value={newRef.companyName} onChange={(e) => setNewRef((r) => ({ ...r, companyName: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="text" placeholder="Branche (z.B. Dachdecker)" value={newRef.industry} onChange={(e) => setNewRef((r) => ({ ...r, industry: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="PLZ" value={newRef.zipCode} onChange={(e) => setNewRef((r) => ({ ...r, zipCode: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="text" placeholder="Stadt" value={newRef.city} onChange={(e) => setNewRef((r) => ({ ...r, city: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <input type="text" placeholder="Leistungen (kommagetrennt, z.B. Netzwerk, WLAN, Videoueberwachung)" value={newRef.services} onChange={(e) => setNewRef((r) => ({ ...r, services: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <textarea placeholder="Projekt-Highlight (z.B. Komplettes Netzwerk mit flaechendeckender WLAN-Ausleuchtung inkl. Aussenbereich + Videoueberwachung)" value={newRef.projectHighlight} onChange={(e) => setNewRef((r) => ({ ...r, projectHighlight: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <div className="flex gap-2">
                <button onClick={async () => { if (!newRef.companyName) { setMessage('Firmenname ist erforderlich.'); return }; try { const body = { companyName: newRef.companyName, industry: newRef.industry || undefined, zipCode: newRef.zipCode || undefined, city: newRef.city || undefined, services: newRef.services.split(/[,;]+/).map((s) => s.trim()).filter(Boolean), projectHighlight: newRef.projectHighlight || undefined }; const { data } = await apiClient.post('/acquisition/reference-customers', body); setRefCustomers((p) => [{ ...data, services: Array.isArray(data.services) ? data.services : [] }, ...p]); setShowAddRef(false); setNewRef({ companyName: '', industry: '', zipCode: '', city: '', services: '', projectHighlight: '' }) } catch { setMessage('Fehler beim Erstellen.') } }} className="px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg">Erstellen</button>
                <button onClick={() => setShowAddRef(false)} className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg">Abbrechen</button>
              </div>
            </div>
          ) : <button onClick={() => setShowAddRef(true)} className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 w-full justify-center"><Plus className="w-4 h-4" /> Referenzkunde hinzufuegen</button>}
        </div>
      )}
    </div>
  )
}

// ─── Prospect Row Actions ──────────────────────────────────────────────────

function ProspectRowActions({ prospect, onUpdate, onEdit }: { prospect: Prospect; onUpdate: () => void; onEdit: (p: Prospect) => void }) {
  const [loading, setLoading] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const [showJobInput, setShowJobInput] = useState(false)
  const [jobUrl, setJobUrl] = useState('')
  const queryClient = useQueryClient()

  const addJobPosting = useMutation({
    mutationFn: (url: string) => apiClient.post(`/acquisition/prospects/${prospect.id}/job-postings`, { url }).then((r) => r.data),
    onSuccess: () => {
      setJobUrl('')
      setShowJobInput(false)
      setResult('Stellenanzeige wird analysiert...')
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
      onUpdate()
    },
    onError: () => setResult('Fehler beim Hinzufügen'),
  })

  const needsAddress = !prospect.street || !prospect.zipCode
  const needsContact = !prospect.contactFirstName && !prospect.contactLastName
  const hasContact = !!(prospect.contactFirstName || prospect.contactLastName)

  const enrich = async (type: 'address' | 'contact') => {
    setLoading(type)
    setResult(null)
    try {
      const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/enrich`, { type })
      setResult(data.message || (data.found ? 'OK' : '–'))
      if (data.found) onUpdate()
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string } } }
      setResult(axErr?.response?.data?.error || 'Fehler')
    } finally {
      setLoading(null)
    }
  }

  const recheck = async () => {
    setLoading('recheck')
    setResult(null)
    try {
      const { data } = await apiClient.post(`/acquisition/prospects/${prospect.id}/recheck`)
      setResult(data.message || (data.found ? 'OK' : 'Nichts gefunden'))
      onUpdate()
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { error?: string } } }
      setResult(axErr?.response?.data?.error || 'Fehler')
    } finally {
      setLoading(null)
    }
  }

  const exclude = async () => {
    await apiClient.patch(`/acquisition/prospects/${prospect.id}`, { status: 'not_interested' })
    onUpdate()
  }

  const resetStatus = async () => {
    setLoading('reset')
    try {
      await apiClient.patch(`/acquisition/prospects/${prospect.id}`, { status: 'qualified' })
      onUpdate()
    } finally {
      setLoading(null)
    }
  }

  const canReset = ['letter_sent', 'not_interested', 'nurture'].includes(prospect.status)

  return (
    <div className="space-y-1">
    <div className="flex items-center gap-1 flex-wrap">
      <button onClick={() => onEdit(prospect)} title="Bearbeiten"
        className="px-1.5 py-0.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50">
        ✏️
      </button>
      {canReset && (
        <button onClick={resetStatus} disabled={loading !== null} title="Status zurücksetzen auf 'Qualifiziert'"
          className="px-1.5 py-0.5 text-xs border border-green-300 text-green-600 rounded hover:bg-green-50 disabled:opacity-50">
          {loading === 'reset' ? '...' : '↩ Zurück'}
        </button>
      )}
      {prospect.website && (
        <button onClick={recheck} disabled={loading !== null}
          title={hasContact ? 'Kontaktdaten erneut prüfen (Impressum neu laden)' : 'Impressum prüfen — findet Ansprechpartner, Adresse und Firmennamen'}
          className="px-1.5 py-0.5 text-xs border border-purple-300 text-purple-600 rounded hover:bg-purple-50 disabled:opacity-50">
          {loading === 'recheck' ? '...' : '🔍 Prüfen'}
        </button>
      )}
      <button onClick={exclude} title="Ausschliessen"
        className="px-1.5 py-0.5 text-xs border border-red-200 text-red-400 rounded hover:bg-red-50">
        ✕
      </button>
      {needsAddress && (
        <button onClick={() => enrich('address')} disabled={loading !== null}
          title="Adresse suchen (Google Places — kostet Credits)"
          className="px-1.5 py-0.5 text-xs border border-amber-300 text-amber-600 rounded hover:bg-amber-50 disabled:opacity-50">
          {loading === 'address' ? '...' : '📍 Adresse'}
        </button>
      )}
      {needsContact && (
        <button onClick={() => enrich('contact')} disabled={loading !== null}
          title="Ansprechpartner suchen (Apollo — kostenlos)"
          className="px-1.5 py-0.5 text-xs border border-blue-300 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50">
          {loading === 'contact' ? '...' : '👤 Kontakt'}
        </button>
      )}
      {prospect.sourceChannel !== 'job_posting' && (
        <button
          onClick={() => { setShowJobInput((v) => !v); setResult(null) }}
          title="Stellenanzeige verknüpfen"
          className="px-1.5 py-0.5 text-xs border border-indigo-300 text-indigo-600 rounded hover:bg-indigo-50"
        >
          📎 Anzeige
        </button>
      )}
      {result && <span className="text-xs text-gray-500 max-w-[150px] truncate" title={result}>{result}</span>}
    </div>
    {showJobInput && (
      <form
        onSubmit={(e) => { e.preventDefault(); if (jobUrl.trim()) addJobPosting.mutate(jobUrl.trim()) }}
        className="flex items-center gap-1.5 mt-1"
      >
        <input
          type="text"
          value={jobUrl}
          onChange={(e) => setJobUrl(e.target.value)}
          placeholder="Stellenanzeigen-URL..."
          autoFocus
          className="flex-1 min-w-0 px-2 py-1 text-xs border border-indigo-200 rounded focus:outline-none focus:border-indigo-400"
        />
        <button
          type="submit"
          disabled={!jobUrl.trim() || addJobPosting.isPending}
          className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
        >
          {addJobPosting.isPending ? '...' : 'OK'}
        </button>
        <button type="button" onClick={() => setShowJobInput(false)} className="text-gray-400 hover:text-gray-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </form>
    )}
    </div>
  )
}

// ─── Kundenschreiber Orders Status ─────────────────────────────────────────

const ORDER_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-700' },
  processing: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-700' },
  writing: { label: 'Wird geschrieben', color: 'bg-indigo-100 text-indigo-700' },
  quality_check: { label: 'Qualitätsprüfung', color: 'bg-purple-100 text-purple-700' },
  shipped: { label: 'Versandt', color: 'bg-green-100 text-green-700' },
  delivered: { label: 'Zugestellt', color: 'bg-green-200 text-green-800' },
  cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-700' },
  failed: { label: 'Fehlgeschlagen', color: 'bg-red-200 text-red-800' },
}

interface KsOrder {
  id: string
  status: string
  total_items?: number
  credits_used?: number
  created_at: string
}

function KundenschreiberOrdersList() {
  const [expanded, setExpanded] = useState(false)
  const { data: orders, isLoading, error } = useQuery<{ data: KsOrder[] }>({
    queryKey: ['kundenschreiber-orders'],
    queryFn: () => apiClient.get('/shipping/kundenschreiber/orders', { params: { limit: 10 } }).then(r => r.data),
    staleTime: 30_000,
    retry: false,
  })

  const orderList = orders?.data ?? []

  if (isLoading) return null
  if (error || orderList.length === 0) return null

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-4 h-4 text-brand-500" />
          Kundenschreiber-Aufträge
          <span className="text-xs font-normal text-gray-500">({orderList.length})</span>
        </h3>
        <ChevronRight className={cn('w-4 h-4 text-gray-400 transition-transform', expanded && 'rotate-90')} />
      </button>
      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {orderList.map((order) => {
            const statusInfo = ORDER_STATUS_LABELS[order.status] ?? { label: order.status, color: 'bg-gray-100 text-gray-600' }
            const date = new Date(order.created_at)
            return (
              <div key={order.id} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', statusInfo.color)}>
                      {statusInfo.label}
                    </span>
                    {order.total_items && (
                      <span className="text-xs text-gray-500">{order.total_items} Brief{order.total_items > 1 ? 'e' : ''}</span>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {date.toLocaleDateString('de-DE')} {date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <span className="text-[10px] text-gray-300 font-mono">{order.id.substring(0, 8)}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

